import React, { useState, useCallback, useLayoutEffect, useRef, useEffect, useMemo } from 'react';
import Konva from 'konva';
import { Stage, Layer, Rect, Image as KonvaImage, Line, Circle, Text, Label, Tag, Group } from 'react-konva';
import useImage from 'use-image';
import { BoundingBox, ClickTarget, ClickTargetId, Section, PnidWizardState, getEquipmentInstrumentName, getInstrumentCategoryName, instrumentRegions, MarkingStatus, MARKING_STATUS_STYLES, MarkingStatusStyle, NodePointCandidate, NodePointCandidateLabel, InstrumentCategory, CanvasComment, LineSegmentStatus } from '../types';
import { appendImageCacheVersion, documentApi, type ResolvedImageUrl } from '../api';
import { INITIAL_PNID_IMAGE_LEVEL, usePnidImage } from '../queries/usePnidImage';
import type { ActiveAssetKind } from '../pnid/activeAssetKind';
import { DRAW_TOOL_KEY, DRAW_TOOL_LABEL, type ArmedTool } from '../pnid/armedTool';
import DrawToolLabel from '../pnid/DrawToolLabel';
import CommentBubble from './CommentBubble';
import { entityAtPoint, type EntityHit, mergeOverlappingLineSegments, MergedLineSegment } from '../utils/geometry';
import {
  EDGE_PAN_COMMIT_INTERVAL_MS,
  EDGE_PAN_DWELL_MS,
  ZOOM_STEP_FACTOR,
  arrowPanDelta,
  clampPanPosition,
  computeEdgePanVelocity,
  fitBoxInViewport,
  isWhollyOnScreen,
  navigateToTarget,
  panToKeepVisible,
  stepPanPosition,
  zoomAtPoint,
  zoomKeyFactor,
  type PanImageSize,
  type PanPointer,
  type PanViewport,
} from './pidCanvasNavigation';
import {
  addVertex,
  EMPTY_LINE_DRAFT,
  type LineDraft,
  type LineEnds,
  redoLast,
  removeLast,
  toDrawBox,
  toLineEnds,
  toPolyline,
} from '../pnid/lineDraft';
import { clickTargetAt } from '../pnid/clickTargetGeometry';
import { isEventInsideEditorDock } from '../pnid/dock/isEventInsideEditorDock';
import './pid-canvas-kind-pill.css';
import type { CanvasInspectionOverlay } from '../types/pipelineInspection';
import { getInstrumentPrimaryLabel } from '../utils/instrumentIdentity';
import {
  getEquipmentContextInstruments,
  getInstrumentEffectiveContext,
  getNodeContextInstruments,
  getUnattachedInstruments,
  isTemplateMatchInstrument,
} from '../utils/unifiedInstruments';
import {
  attachedInstrumentOverlayVisible,
  equipmentOverlayVisible,
  nodeOverlayVisible,
  unattachedInstrumentOverlayVisible,
} from './pidCanvasVisibility';

/** Wizard overlay when item is inactive: green only after explicit review */
function wizardInactivePalette(
  kind: 'equipment' | 'line' | 'instrument',
  reviewed: boolean
): { stroke: string; fill: string; hoverStroke: string; hoverFill: string } {
  if (reviewed) {
    return {
      stroke: '#86efac',
      fill: 'rgba(134, 239, 172, 0.12)',
      hoverStroke: '#4ade80',
      hoverFill: 'rgba(134, 239, 172, 0.22)',
    };
  }
  if (kind === 'equipment') {
    return {
      stroke: '#60a5fa',
      fill: 'rgba(59, 130, 246, 0.12)',
      hoverStroke: '#2563eb',
      hoverFill: 'rgba(59, 130, 246, 0.22)',
    };
  }
  if (kind === 'line') {
    return {
      stroke: '#fb923c',
      fill: 'rgba(251, 146, 60, 0.12)',
      hoverStroke: '#ea580c',
      hoverFill: 'rgba(251, 146, 60, 0.22)',
    };
  }
  return {
    stroke: '#a78bfa',
    fill: 'rgba(167, 139, 250, 0.12)',
    hoverStroke: '#7c3aed',
    hoverFill: 'rgba(167, 139, 250, 0.22)',
  };
}

const targetOrAncestorHasKonvaName = (target: any, names: string[]): boolean => {
  let node = target;
  while (node) {
    if (names.some((name) => node.hasName?.(name))) {
      return true;
    }
    node = node.getParent?.();
  }
  return false;
};

const CanvasPerfHud: React.FC<{
  scale: number;
  perfStats: { nodes: number; nodesWithPolyline: number; totalNodePoints: number } | null;
  nodePointMode: NodePointRenderMode;
  setNodePointMode: (mode: NodePointRenderMode) => void;
  cursorPosition: { x: number; y: number } | null;
  onHide: () => void;
}> = ({ scale, perfStats, nodePointMode, setNodePointMode, cursorPosition, onHide }) => {
  const [fps, setFps] = useState<number | null>(null);
  useEffect(() => {
    let rafId = 0;
    let lastT = performance.now();
    let frameCount = 0;

    const tick = (t: number) => {
      frameCount += 1;
      const dt = t - lastT;
      if (dt >= 500) {
        setFps(Math.round((frameCount * 1000) / dt));
        frameCount = 0;
        lastT = t;
      }
      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, []);

  return (
    <div
      style={{
        position: 'absolute',
        top: 10,
        right: 10,
        zIndex: 12,
        padding: '8px 10px',
        backgroundColor: 'rgba(17, 24, 39, 0.9)',
        color: 'white',
        borderRadius: 8,
        fontSize: 12,
        fontFamily:
          'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
        minWidth: 220,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
        <div style={{ fontWeight: 700, letterSpacing: '0.06em', color: 'rgba(229,231,235,0.9)' }}>CANVAS PERF</div>
        <button
          onClick={onHide}
          style={{
            padding: '1px 6px',
            borderRadius: 6,
            border: '1px solid rgba(255,255,255,0.18)',
            backgroundColor: 'rgba(255,255,255,0.08)',
            color: 'white',
            cursor: 'pointer',
          }}
          title="Hide (re-enable with ?canvasPerf=1)"
        >
          Hide
        </button>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <div>FPS</div>
        <div>{fps ?? '…'}</div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <div>Zoom</div>
        <div>{scale.toFixed(2)}</div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <div>Cursor</div>
        <div>{cursorPosition ? `${cursorPosition.x.toFixed(1)}, ${cursorPosition.y.toFixed(1)}` : '—'}</div>
      </div>
      {perfStats && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <div>Nodes</div>
            <div>
              {perfStats.nodes} ({perfStats.nodesWithPolyline} poly)
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <div>Node pts</div>
            <div>{perfStats.totalNodePoints}</div>
          </div>
        </>
      )}

      <div style={{ marginTop: 8, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ color: 'rgba(229, 231, 235, 0.85)' }}>Node points:</span>
        <button
          onClick={() => setNodePointMode('none')}
          style={{
            padding: '2px 6px',
            borderRadius: 6,
            border: '1px solid rgba(255,255,255,0.18)',
            backgroundColor: nodePointMode === 'none' ? '#2563eb' : 'rgba(255,255,255,0.08)',
            color: 'white',
            cursor: 'pointer',
          }}
        >
          None
        </button>
        <button
          onClick={() => setNodePointMode('active')}
          style={{
            padding: '2px 6px',
            borderRadius: 6,
            border: '1px solid rgba(255,255,255,0.18)',
            backgroundColor: nodePointMode === 'active' ? '#2563eb' : 'rgba(255,255,255,0.08)',
            color: 'white',
            cursor: 'pointer',
          }}
        >
          Active
        </button>
        <button
          onClick={() => setNodePointMode('all')}
          style={{
            padding: '2px 6px',
            borderRadius: 6,
            border: '1px solid rgba(255,255,255,0.18)',
            backgroundColor: nodePointMode === 'all' ? '#2563eb' : 'rgba(255,255,255,0.08)',
            color: 'white',
            cursor: 'pointer',
          }}
        >
          All
        </button>
      </div>
    </div>
  );
};

// Hover tooltip info type
interface HoveredItem {
  type: 'equipment' | 'equipment_instrument' | 'node_instrument' | 'unattached_instrument' | 'node' | 'control_loop';
  id: string;
  tag: string;
  itemType: string;
  bbox: BoundingBox;
  /** Only for type === 'node' */
  lineNo?: string;
}

/** One navigation to a box on the drawing. A `requestId` the canvas has not applied yet moves the view. */
export interface CanvasFocusRequest {
  bbox: BoundingBox;
  requestId: number | string;
}

/**
 * One asset to look at on a viewer canvas: its regions drawn in the highlight
 * colour, the rest of the sheet dimmed. Sheet coordinates, as the marks store them.
 */
export interface CanvasFocusOverlay {
  boxes: BoundingBox[];
  /** Flat `[x1, y1, x2, y2, …]` paths, as a line's `polyline` stores them. */
  polylines: number[][];
}

/** The dim over the sheet outside a focus overlay. Light enough to read the context through. */
const FOCUS_OVERLAY_DIM_FILL = 'rgba(14, 23, 27, 0.45)';
/** How wide, in sheet pixels, the undimmed band along a focused path is. */
const FOCUS_OVERLAY_PATH_CLEARANCE = 48;

/** One press of a zoom control outside the canvas: in, out, 100%, or fit the drawing. */
export interface CanvasZoomRequest {
  kind: 'in' | 'out' | 'reset' | 'fit';
  requestId: number;
}

interface PidCanvasProps {
  imageUrl: string;
  sections: Section[];
  /** A finished box drag or polygon. */
  onBoxDrawn: (bbox: BoundingBox, anchor: { x: number; y: number }) => void;
  /** A finished line also reports what its two ends bind to. */
  onLineDrawn: (bbox: BoundingBox, anchor: { x: number; y: number }, lineEnds?: LineEnds) => void;
  pendingPieces: { bbox: BoundingBox; context: string; id: string }[];
  onCancelPending?: (pieceId:string)=> void;
  onSelectSection: (sectionId: string) => void;
  selectedSectionId?: string;
  onSelectPending?: (pendingId: string) => void;
  selectedPendingId?: string;
  /** Optional fixed width. If omitted, canvas fills parent horizontally */
  width?: number;
  /** Optional fixed height. If omitted, canvas fills parent vertically */
  height?: number;
  /** Document ID for SAM API calls */
  docId?: string;
  /** Changes when the source image changes, so browser/proxy caches refetch an `imageUrl` image. */
  imageVersion?: string | null;
  /** The `docId` image level to load first, before the fit picks the level the zoom needs. */
  initialImageLevel?: number;
  /** Canonical image-coordinate frame for overlays when a lower-resolution image variant is rendered. */
  logicalImageSize?: { width: number; height: number };
  /** Read-only execution-provenance overlays for pipeline inspection. */
  inspectionOverlay?: CanvasInspectionOverlay | null;
  /** Dims the sheet around one asset and outlines it, for a viewer that reviews that asset. */
  focusOverlay?: CanvasFocusOverlay | null;
  /** Hide developer/performance chrome in embedded read-only inspection views. */
  hidePerfHud?: boolean;
  /**
   * Scroll the drawing while the cursor rests near a border. Turn this off in a
   * viewer that frames the view itself, or while an editor beside the canvas is
   * open: a cursor crossing to it would scroll the framing or the target away.
   * Off, the border still scrolls a box, polygon, or line that is being drawn,
   * so the gesture reaches an off-screen area.
   */
  edgePanEnabled?: boolean;
  /** Fit the full drawing when an embedded viewer clears its focus request. */
  fitOnFocusClear?: boolean;
  /** Fit the full drawing each time this value changes. */
  fitRequestId?: number;
  /**
   * Fits `bbox` wholly in the canvas, centred, once per `requestId`, at whatever
   * scale that takes. A resize or a re-render with the same request does not move
   * the view again.
   */
  fitBoxRequest?: CanvasFocusRequest | null;
  /** Applied once per `requestId`, as the zoom keys would apply it. */
  zoomRequest?: CanvasZoomRequest | null;
  /** Called with each committed stage scale, so a control outside the canvas can show it. */
  onScaleChange?: (scale: number) => void;
  /**
   * The canvas is the drawing workspace's annotating surface: a drag draws a box, a
   * click selects a mark or starts a line, and the marking overlays and the toolbar
   * render. Without it the canvas is a viewer in `select` mode, as on the pipeline
   * inspection page.
   */
  annotating?: boolean;
  pnidState?: PnidWizardState | null;
  /** The workspace's armed draw tool. Line makes a click a vertex and a drag inert. */
  armedTool?: ArmedTool | null;
  /** The draw tools the toolbar offers, in toolbar order; each one's key presses its button at rest. */
  offeredDrawTools?: ReadonlyArray<ArmedTool>;
  /** Presses a toolbar draw button from its key: arms the tool, or lets go of it when it is armed. */
  onPressDrawTool?: (tool: ArmedTool) => void;
  /** Which kind's active asset renders active, takes direct attaches and stays when isolated. */
  activeAssetKind?: ActiveAssetKind;
  activeEquipmentId?: string | null;
  activeNodeId?: string | null;
  selectedInstrumentId?: string | null;
  onRightClickInstrument?: (id: string, screenPos: { x: number; y: number }) => void;
  /** Isolate: draw only the active asset and its instruments. Holding X isolates too. */
  isolateActiveAsset?: boolean;
  /**
   * Moves the view to `bbox` by the rule of `navigateToTarget`, once per `requestId`.
   * A resize or a re-render with the same request does not move it again.
   */
  focusRequest?: CanvasFocusRequest | null;
  /**
   * A box the canvas keeps on screen across a resize: when a narrower or moved
   * canvas would cut off a box that was wholly visible, the view pans the
   * minimum distance to show it again, at the same scale.
   */
  keepVisibleBbox?: BoundingBox | null;
  /** Box currently being marked (shown while mark form is open) */
  pendingMarkBbox?: BoundingBox | null;
  /** Update pending mark bbox (for resize/drag handles) */
  onUpdatePendingMarkBbox?: (bbox: BoundingBox) => void;
  /** Select a marked wizard item */
  onSelectWizardItem?: (target: {
    kind: WizardCanvasItemKind;
    id: string;
    screenPosition?: { x: number; y: number };
    isMultiSelect?: boolean;
  }) => void;
  /** Selected persisted wizard annotations for bulk movement/deletion */
  selectedWizardItemKeys?: Set<string>;
  onSelectAllWizardItems?: () => void;
  onClearWizardItemSelection?: () => void;
  onDeleteSelectedWizardItems?: () => void;
  /** Puts the selected equipment's tags in one redundancy set. Absent unless the selection is equipment alone, the drawing has a plant and the user can edit. */
  onMarkSelectedAsRedundancySet?: () => void;
  onShiftSelectedWizardAnnotations?: (
    delta: { dx: number; dy: number },
    options?: { lineSegmentScale?: { sx: number; sy: number } },
  ) => Promise<void> | void;
  /** Capture undo before a persisted box drag/resize starts. */
  onBeginWizardItemBboxUpdate?: (
    target: { kind: 'equipment' | 'equipmentInstrument' | 'nodeInstrument' | 'controlLoop' | 'node'; id: string },
  ) => void;
  /**
   * Adjust a persisted rectangle mark. commit=false is a local preview; commit=true
   * persists the final geometry. `regionIndex` says which of a multi-region marking's
   * rectangles moved; it is absent for marks that carry a single rectangle.
   */
  onUpdateWizardItemBbox?: (
    target: { kind: 'equipment' | 'equipmentInstrument' | 'nodeInstrument' | 'controlLoop' | 'node'; id: string },
    bbox: BoundingBox,
    options?: { commit?: boolean; regionIndex?: number },
  ) => void;
  /** One-click delete of a persisted instrument annotation (quick remove, undoable via Cmd/Ctrl+Z). */
  onQuickDeleteWizardItem?: (
    target: { kind: 'equipmentInstrument' | 'nodeInstrument'; id: string },
  ) => void;
  /** Shift all saved wizard annotation geometry by a common image-coordinate delta. */
  onShiftWizardAnnotations?: (
    delta: { dx: number; dy: number },
    options?: { lineSegmentScale?: { sx: number; sy: number } },
  ) => Promise<void> | void;
  /**
   * The regions being identified, each outlined with an "Identifying..." label until
   * the answer arrives. One identify call covers the whole marking, so every region
   * the SME drew for it carries the wait.
   */
  identifyingBboxes?: BoundingBox[];
  /** Whether node marking mode is active (disables clicking on other nodes) */
  isNodeMarkingMode?: boolean;
  /** The shape the line in hand draws: a path, or a duct drawn as a box. */
  nodeDrawingShape?: 'pipeline' | 'duct';
  /** Candidate instruments detected along a node polyline */
  nodePointCandidates?: NodePointCandidate[];
  /** Resolve a candidate with a label or ignore */
  onResolveNodePointCandidate?: (candidateId: string, label: NodePointCandidateLabel | 'ignore') => void;
  /** Notify parent when a draft drawing is in progress */
  onDraftStateChange?: (hasDraft: boolean) => void;
  /** Trigger to clear any in-progress drawing, which returns the canvas to rest */
  clearDraftSignal?: number;
  /** Callback when user double-clicks a node to enter mark mode */
  onDoubleClickNode?: (nodeId: string, screenPosition?: { x: number; y: number }) => void;
  /** Callback when user starts a path (first point placed) */
  onPathStart?: (screenPosition: { x: number; y: number }, canvasPosition: { x: number; y: number }) => void;
  /** Escape with no vertex in flight, or on the last vertex of a line no tool holds: the SME leaves line drawing (D4). */
  onExitLineDrawing?: () => void;
  /** Optional line-segment review overlay for a specific node */
  segmentReview?: {
    nodeId: string | null;
    hoveredDistinctId?: string | null;
    selectedDistinctId?: string | null;
  };
  /** Optional coordinate transform for AI-extracted line segment coordinates → canvas image coordinates */
  segmentCoordinateScale?: { sx: number; sy: number };
  /** Notify parent when the underlying image natural size is known */
  onImageSize?: (width: number, height: number) => void;
  // Comment props
  /** All comments for the document */
  comments?: CanvasComment[];
  /** Currently selected comment ID */
  selectedCommentId?: string | null;
  /** Callback when a comment bubble is clicked (includes screen position) */
  onSelectComment?: (commentId: string, screenX: number, screenY: number) => void;
  /** Show resolved comments */
  showResolvedComments?: boolean;
  /** Click-to-attach: immediately persist an unattached (context-free) template/AI instrument onto the active equipment or line, as `activeAssetKind` picks */
  onDirectAttachAnnotation?: (annotationId: string) => void;
  /** Entity ID to highlight on canvas (from InstrumentEditor hover) */
  highlightedEntityId?: string | null;
  /** Reviewer mode: view + comment only, no editing/drawing */
  readOnly?: boolean;
  /**
   * External signal to arm the collect mode (e.g. from the mark dialog). It stays true
   * across mouse-ups, so the SME draws region after region without re-arming; the mode
   * ends when the workspace clears it.
   */
  collectingRegions?: boolean;
  /** Regions collected so far in the armed collect mode, in draw order. */
  draftRegions?: BoundingBox[];
  /** Finishes the draft. Behind the floating "Done · N regions" pill and the Enter key. */
  onFinishRegions?: () => void;
  /** Backs out the most recently drawn region. Escape, while the draft holds regions. */
  onRemoveLastRegion?: () => void;
  /** Leaves the collect mode. Escape, while the draft is empty. */
  onExitCollect?: () => void;
  /**
   * Nothing is armed. A finished box drag waits for its kind instead of reaching
   * `onBoxDrawn`: the canvas holds it as a dashed pending box under the kind pill, and
   * `onPendingBoxKind` reports the answer. A click on empty paper starts a line.
   */
  askKindOnDraw?: boolean;
  /** The kind the SME picked on the pill for the pending box, with the box and its anchor. */
  onPendingBoxKind?: (kind: PendingBoxKind, bbox: BoundingBox, anchor: { x: number; y: number }) => void;
  /** Called when the kind pill opens or closes. While it is open it owns Escape. */
  onKindPillOpenChange?: (open: boolean) => void;
  /**
   * The click targets to outline, largest box first. The workspace hands over only
   * the ones that should show, so an empty list draws nothing.
   */
  clickTargets?: ClickTarget[];
  /** A press too short to be a drag landed on this click target while collecting. */
  onClickTarget?: (clickTarget: ClickTarget) => void;
  /**
   * Set while an editor-dock field has focus and `clickTargets` holds the targets to copy
   * from. A press on one of them keeps the field's focus and does not draw, pan or
   * select; a release within the click limit reports the target. A press anywhere else
   * behaves as it does without this prop.
   */
  onCopyClickTarget?: (clickTarget: ClickTarget) => void;
  /** Callback for batched canvas interaction tracking (called every 10s with accumulated counts) */
  onCanvasInteraction?: (counts: Record<string, number>) => void;
}

/** The kinds the kind pill offers for a box drawn with no tool armed. */
export type PendingBoxKind = 'equipment' | 'instrument';

interface DrawingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

type DrawMode = 'select' | 'rect' | 'poly' | 'path' | 'shift-annotations';
type WizardCanvasItemKind = 'equipment' | 'equipmentInstrument' | 'nodeInstrument' | 'node' | 'controlLoop';
type PersistedBboxCanvasTarget = {
  // 'node' targets adjust a duct node's persisted duct_bbox.
  kind: 'equipment' | 'equipmentInstrument' | 'nodeInstrument' | 'controlLoop' | 'node';
  id: string;
};
type NodePointRenderMode = 'all' | 'active' | 'none';

// The collect mode's overlays are read, not measured, so they are sized in screen
// pixels and divided by the stage scale — legible at any zoom.
const DRAFT_REGION_CHIP_SIZE_PX = 18;

/**
 * The order chip on a region. Draw order is what makes a marking readable once it is
 * several rectangles — which one is the symbol, which the balloon — so the number is
 * on the sheet rather than only in the dialog. Drafts carry it top-left and saved
 * markings alike carry it at the top-right corner, so the number sits in one place
 * whatever the rectangle is.
 */
function RegionIndexChip({
  region,
  index,
  color,
  scale,
}: {
  region: BoundingBox;
  index: number;
  color: string;
  scale: number;
}) {
  const size = DRAFT_REGION_CHIP_SIZE_PX / scale;
  const inset = 2 / scale;
  const x = region.x2 - inset - size;
  const y = region.y1 + inset;
  return (
    <>
      <Rect
        x={x}
        y={y}
        width={size}
        height={size}
        cornerRadius={4 / scale}
        fill={color}
        listening={false}
        perfectDrawEnabled={false}
      />
      <Text
        x={x}
        y={y}
        width={size}
        height={size}
        text={String(index + 1)}
        fontSize={12 / scale}
        fontStyle="bold"
        fill="white"
        align="center"
        verticalAlign="middle"
        listening={false}
      />
    </>
  );
}
const DONE_PILL_HEIGHT_PX = 30;

/** A key cap inside the collecting pill, sized to sit on the pill's own baseline. */
const COLLECT_KEY_STYLE: React.CSSProperties = {
  display: 'inline-block',
  padding: '1px 6px',
  marginRight: 4,
  borderRadius: 4,
  border: '1px solid rgba(255, 255, 255, 0.55)',
  borderBottomWidth: 2,
  background: 'rgba(255, 255, 255, 0.18)',
  font: '700 11px ui-monospace, SFMono-Regular, Menlo, monospace',
  lineHeight: 1.5,
};
/** Bold 13px system text averages about this wide, which sizes the pill without measuring it. */
const DONE_PILL_GAP_PX = 10;
const DONE_PILL_VIEWPORT_MARGIN_PX = 12;
/** A press on the Done pill that moves less than this is a click, not a drag. */
const DONE_PILL_DRAG_THRESHOLD_PX = 4;
/** The kind pill's box: two small recipe buttons with key caps, the Esc hint and the pill's padding. */
const KIND_PILL_WIDTH_PX = 400;
const KIND_PILL_HEIGHT_PX = 44;
const INTERACTION_IMAGE_MAX_DIMENSION = 2048;
const INTERACTION_IMAGE_IDLE_MS = 500;
const PNID_IMAGE_LEVELS = [2048, 4096, 8192] as const;
const IMAGE_LEVEL_SETTLE_MS = 250;
const IMAGE_LEVEL_QUALITY_BUFFER = 1.2;
const VIEWPORT_CULL_PADDING_SCREEN_PX = 360;
/** How far, in screen px, the second click of a double-click may land from the first. */
const DOUBLE_CLICK_SLOP_PX = 5;
/** A release closer than this, in screen px, to its press is a click; farther is a drag. */
const CLICK_SLOP_PX = 5;
/** Konva's double-click window in ms, which its typings leave out. */
const konvaDoubleClickWindowMs = (): number => (Konva as unknown as { dblClickWindow: number }).dblClickWindow;
const VIEWPORT_CULL_MIN_IMAGE_PADDING = 300;
const OVERVIEW_DETAIL_SCALE = 0.35;
const NO_CLICK_TARGETS: ClickTarget[] = [];
/**
 * The canvas controls a press keeps for itself while a dock field has focus, even over
 * a target to copy: a handle or an outline that moves or resizes, the quick delete, and
 * the shift frame. A copy press there would also fire the control's own click.
 */
const CONTROLS_A_COPY_PRESS_LEAVES = [
  'pending-handle',
  'pending-point',
  'persisted-bbox-adjust',
  'persisted-bbox-handle',
  'quick-delete-badge',
  'annotation-shift-bounds',
  'selected-annotation-shift-bounds',
];

/**
 * Konva paints on a canvas, which cannot resolve a CSS variable, so the
 * click-target overlay reads its design tokens off the document instead.
 */
function readDesignToken(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

/** The hover label: what a text target reads, on one line, or the bare kind. */
function clickTargetLabel(clickTarget: ClickTarget): string {
  if (clickTarget.kind === 'symbol') return 'symbol';
  return `text · ${(clickTarget.text ?? '').replace(/\s+/g, ' ')}`;
}

const CLICK_TARGET_LABEL_GAP_PX = 4;

/**
 * The hover label's font in sheet units, so it reads 12 px on screen at any zoom,
 * on one line that ends in an ellipsis where a width cuts it. The drawn label and
 * its measurement both take it from here.
 */
function clickTargetLabelFont(scale: number) {
  return {
    fontSize: 12 / scale,
    fontFamily: 'system-ui, -apple-system, sans-serif',
    padding: 4 / scale,
    wrap: 'none',
    ellipsis: true,
  };
}

interface ScreenBox { left: number; top: number; right: number; bottom: number }
interface ScreenSize { width: number; height: number }

interface ClickTargetLabelSize {
  onScreen: ScreenSize;
  /** The text's width in sheet units when the stage cuts it, else none: it draws whole. */
  truncatedSheetWidth: number | undefined;
}

/**
 * The hover label's size, no wider than the stage. Glyph widths do not scale
 * linearly with font size, so it lays the text out at the drawn sheet-unit font
 * and then applies the zoom, as the stage does, rather than laying it out at the
 * 12 px screen size.
 */
function measureClickTargetLabel(text: string, scale: number, stageWidth: number): ClickTargetLabelSize {
  const sheetSize = new Konva.Text({ text, ...clickTargetLabelFont(scale) }).size();
  const fitsTheStage = sheetSize.width * scale <= stageWidth;
  return {
    onScreen: {
      width: fitsTheStage ? sheetSize.width * scale : stageWidth,
      height: sheetSize.height * scale,
    },
    truncatedSheetWidth: fitsTheStage ? undefined : stageWidth / scale,
  };
}

/**
 * The hover label's top-left on screen: centred above its target, below it when the
 * stage has no room above, and slid sideways to stay inside the stage. A target at
 * a sheet edge still shows its whole label without a pan.
 */
function placeClickTargetLabel(
  target: ScreenBox,
  label: ScreenSize,
  stage: ScreenSize,
): { x: number; y: number } {
  const centredX = (target.left + target.right) / 2 - label.width / 2;
  const aboveY = target.top - CLICK_TARGET_LABEL_GAP_PX - label.height;
  return {
    x: Math.max(0, Math.min(centredX, stage.width - label.width)),
    y: aboveY >= 0
      ? aboveY
      : Math.max(0, Math.min(target.bottom + CLICK_TARGET_LABEL_GAP_PX, stage.height - label.height)),
  };
}

type CanvasBounds = { x1: number; y1: number; x2: number; y2: number };

const boundsIntersect = (a: CanvasBounds, b: CanvasBounds): boolean => (
  a.x1 <= b.x2 &&
  a.x2 >= b.x1 &&
  a.y1 <= b.y2 &&
  a.y2 >= b.y1
);

const mergeBounds = (a: CanvasBounds | null, b: CanvasBounds | null): CanvasBounds | null => {
  if (!a) return b;
  if (!b) return a;
  return {
    x1: Math.min(a.x1, b.x1),
    y1: Math.min(a.y1, b.y1),
    x2: Math.max(a.x2, b.x2),
    y2: Math.max(a.y2, b.y2),
  };
};

const choosePnidImageLevel = (
  scale: number,
  originalWidth: number | null,
  originalHeight: number | null
): number => {
  if (!originalWidth || !originalHeight) return INITIAL_PNID_IMAGE_LEVEL;
  const originalMaxDim = Math.max(originalWidth, originalHeight);
  const deviceScale = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  const requiredMaxDim = originalMaxDim * Math.max(scale, 1e-4) * deviceScale * IMAGE_LEVEL_QUALITY_BUFFER;
  return PNID_IMAGE_LEVELS.find((level) => level >= requiredMaxDim) ?? PNID_IMAGE_LEVELS[PNID_IMAGE_LEVELS.length - 1];
};

const rectBounds = (x: number, y: number, width: number, height: number): CanvasBounds => ({
  x1: x,
  y1: y,
  x2: x + width,
  y2: y + height,
});

const boundsFromBbox = (bbox?: BoundingBox | null): CanvasBounds | null => {
  if (!bbox) return null;
  const rect = {
    x1: Math.min(bbox.x1, bbox.x2),
    y1: Math.min(bbox.y1, bbox.y2),
    x2: Math.max(bbox.x1, bbox.x2),
    y2: Math.max(bbox.y1, bbox.y2),
  };
  return mergeBounds(rect, boundsFromPolyline(bbox.polygon));
};

const boundsFromPolyline = (points?: number[] | null): CanvasBounds | null => {
  if (!points || points.length < 4) return null;
  let x1 = Infinity;
  let y1 = Infinity;
  let x2 = -Infinity;
  let y2 = -Infinity;

  for (let i = 0; i < points.length - 1; i += 2) {
    const x = points[i];
    const y = points[i + 1];
    if (typeof x !== 'number' || typeof y !== 'number') continue;
    x1 = Math.min(x1, x);
    y1 = Math.min(y1, y);
    x2 = Math.max(x2, x);
    y2 = Math.max(y2, y);
  }

  if (!Number.isFinite(x1) || !Number.isFinite(y1) || !Number.isFinite(x2) || !Number.isFinite(y2)) {
    return null;
  }

  return { x1, y1, x2, y2 };
};

const boundsFromLineSegments = (
  segments?: { x1: number; y1: number; x2: number; y2: number }[] | null,
  sx = 1,
  sy = 1
): CanvasBounds | null => {
  if (!segments?.length) return null;
  let x1 = Infinity;
  let y1 = Infinity;
  let x2 = -Infinity;
  let y2 = -Infinity;

  for (const segment of segments) {
    const ax = segment.x1 * sx;
    const ay = segment.y1 * sy;
    const bx = segment.x2 * sx;
    const by = segment.y2 * sy;
    x1 = Math.min(x1, ax, bx);
    y1 = Math.min(y1, ay, by);
    x2 = Math.max(x2, ax, bx);
    y2 = Math.max(y2, ay, by);
  }

  if (!Number.isFinite(x1) || !Number.isFinite(y1) || !Number.isFinite(x2) || !Number.isFinite(y2)) {
    return null;
  }

  return { x1, y1, x2, y2 };
};

const getPnidAnnotationBounds = (
  state: PnidWizardState,
  lineSegmentScale: { sx: number; sy: number }
): CanvasBounds | null => {
  let bounds: CanvasBounds | null = null;

  for (const equipment of state.equipments) {
    bounds = mergeBounds(bounds, boundsFromBbox(equipment.bbox));
    bounds = mergeBounds(bounds, boundsFromBbox(equipment.label_bbox));
  }
  for (const instrument of state.instruments) {
    bounds = mergeBounds(bounds, boundsFromBbox(instrument.bbox));
  }
  for (const node of state.nodes) {
    bounds = mergeBounds(bounds, node.is_duct ? boundsFromBbox(node.duct_bbox) : boundsFromPolyline(node.polyline));
    bounds = mergeBounds(bounds, boundsFromBbox(node.label_bbox));
    bounds = mergeBounds(
      bounds,
      boundsFromLineSegments(node.line_segments, lineSegmentScale.sx, lineSegmentScale.sy)
    );
  }
  for (const loop of state.control_loops) {
    bounds = mergeBounds(bounds, boundsFromBbox(loop.bbox));
  }

  return bounds;
};

const wizardCanvasItemKey = (target: { kind: WizardCanvasItemKind; id: string }) =>
  `${target.kind}:${target.id}`;

const getPnidAnnotationTargetBounds = (
  state: PnidWizardState,
  target: { kind: WizardCanvasItemKind; id: string },
  lineSegmentScale: { sx: number; sy: number }
): CanvasBounds | null => {
  if (target.kind === 'equipment') {
    const equipment = state.equipments.find((item) => item.id === target.id);
    if (!equipment) return null;
    return mergeBounds(boundsFromBbox(equipment.bbox), boundsFromBbox(equipment.label_bbox));
  }
  if (target.kind === 'node') {
    const node = state.nodes.find((item) => item.id === target.id);
    if (!node) return null;
    return mergeBounds(
      mergeBounds(node.is_duct ? boundsFromBbox(node.duct_bbox) : boundsFromPolyline(node.polyline), boundsFromBbox(node.label_bbox)),
      boundsFromLineSegments(node.line_segments, lineSegmentScale.sx, lineSegmentScale.sy)
    );
  }
  if (target.kind === 'controlLoop') {
    const loop = state.control_loops.find((item) => item.id === target.id);
    return boundsFromBbox(loop?.bbox);
  }
  const instrument = state.instruments.find((item) => item.id === target.id);
  return boundsFromBbox(instrument?.bbox);
};

const parseWizardCanvasItemKey = (key: string): { kind: WizardCanvasItemKind; id: string } | null => {
  const index = key.indexOf(':');
  if (index <= 0 || index === key.length - 1) return null;
  const kind = key.slice(0, index) as WizardCanvasItemKind;
  const id = key.slice(index + 1);
  if (
    kind !== 'equipment' &&
    kind !== 'equipmentInstrument' &&
    kind !== 'nodeInstrument' &&
    kind !== 'node' &&
    kind !== 'controlLoop'
  ) {
    return null;
  }
  return { kind, id };
};

const PidCanvas: React.FC<PidCanvasProps> = ({ 
  imageUrl, 
  sections, 
  onBoxDrawn,
  onLineDrawn,
  pendingPieces,
  onSelectSection,
  selectedSectionId,
  onSelectPending,
  selectedPendingId,
  width: widthProp,
  height: heightProp,
  docId,
  imageVersion,
  initialImageLevel = INITIAL_PNID_IMAGE_LEVEL,
  logicalImageSize,
  inspectionOverlay,
  focusOverlay = null,
  hidePerfHud = false,
  edgePanEnabled = true,
  fitOnFocusClear = false,
  fitRequestId,
  fitBoxRequest,
  zoomRequest,
  onScaleChange,
  // Annotating-surface props
  annotating,
  pnidState,
  armedTool = null,
  offeredDrawTools,
  onPressDrawTool,
  activeAssetKind = 'equipment',
  activeEquipmentId,
  activeNodeId,
  selectedInstrumentId = null,
  onRightClickInstrument,
  isolateActiveAsset = false,
  focusRequest,
  keepVisibleBbox,
  pendingMarkBbox,
  onUpdatePendingMarkBbox,
  selectedWizardItemKeys,
  onSelectAllWizardItems,
  onClearWizardItemSelection,
  onDeleteSelectedWizardItems,
  onMarkSelectedAsRedundancySet,
  onShiftSelectedWizardAnnotations,
  onBeginWizardItemBboxUpdate,
  onUpdateWizardItemBbox,
  onQuickDeleteWizardItem,
  onShiftWizardAnnotations,
  onSelectWizardItem: onSelectWizardItemProp,
  identifyingBboxes = [],
  isNodeMarkingMode = false,
  nodeDrawingShape = 'pipeline',
  nodePointCandidates = [],
  onResolveNodePointCandidate,
  onDraftStateChange,
  clearDraftSignal,
  onDoubleClickNode,
  onPathStart,
  onExitLineDrawing,
  segmentReview,
  segmentCoordinateScale,
  onImageSize,
  // Comment props
  comments = [],
  selectedCommentId = null,
  onSelectComment,
  showResolvedComments = false,
  onDirectAttachAnnotation,
  highlightedEntityId,
  readOnly = false,
  collectingRegions = false,
  draftRegions = [],
  onFinishRegions,
  onRemoveLastRegion,
  onExitCollect,
  askKindOnDraw = false,
  onPendingBoxKind,
  onKindPillOpenChange,
  clickTargets = NO_CLICK_TARGETS,
  onClickTarget,
  onCopyClickTarget,
  onCanvasInteraction,
}) => {
  const [desiredImageMaxDim, setDesiredImageMaxDim] = useState<number>(initialImageLevel);
  const [resolvedImageUrl, setResolvedImageUrl] = useState('');
  const [resolvedImageMeta, setResolvedImageMeta] = useState<ResolvedImageUrl | null>(null);
  const [activeImage, setActiveImage] = useState<HTMLImageElement | undefined>(undefined);
  const [activeImageMeta, setActiveImageMeta] = useState<ResolvedImageUrl | null>(null);
  const activeObjectUrlRef = useRef<string | null>(null);
  const pendingRevokeUrlRef = useRef<string | null>(null);
  const pnidImageQuery = usePnidImage(docId, desiredImageMaxDim);
  const pnidImage = pnidImageQuery.data;
  const pnidImageError = pnidImageQuery.error;
  // A `docId` image comes from the query; only an `imageUrl` image is resolved here.
  const canvasImageUrl = docId ? undefined : appendImageCacheVersion(imageUrl, imageVersion);
  // A `docId` image depends on the drawing alone, so a new `imageUrl` for the same
  // drawing keeps it.
  const imageSource = docId ?? imageUrl;

  // Declared before the effects that resolve the image: a cached image resolves
  // in the same commit as a mount or a drawing switch, and this reset must not
  // clear it afterwards.
  useEffect(() => {
    setDesiredImageMaxDim(initialImageLevel);
    setActiveImage(undefined);
    setActiveImageMeta(null);
    setResolvedImageUrl('');
    setResolvedImageMeta(null);
    if (activeObjectUrlRef.current) {
      window.URL.revokeObjectURL(activeObjectUrlRef.current);
      activeObjectUrlRef.current = null;
    }
    if (pendingRevokeUrlRef.current) {
      window.URL.revokeObjectURL(pendingRevokeUrlRef.current);
      pendingRevokeUrlRef.current = null;
    }
  }, [imageSource, initialImageLevel]);

  useEffect(() => {
    if (!pnidImage) return undefined;
    const objectUrl = window.URL.createObjectURL(pnidImage.blob);
    setResolvedImageMeta({
      url: objectUrl,
      revoke: true,
      originalWidth: pnidImage.originalWidth,
      originalHeight: pnidImage.originalHeight,
    });
    setResolvedImageUrl(objectUrl);
    return () => {
      pendingRevokeUrlRef.current = objectUrl;
    };
  }, [pnidImage]);

  useEffect(() => {
    if (pnidImageError) console.warn('Could not load P&ID image:', pnidImageError);
  }, [pnidImageError]);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    if (canvasImageUrl === undefined) return undefined;
    if (!canvasImageUrl) {
      setResolvedImageUrl('');
      setResolvedImageMeta(null);
      return undefined;
    }

    documentApi.resolveImageUrl(canvasImageUrl)
      .then((resolved) => {
        if (cancelled) {
          if (resolved.revoke) window.URL.revokeObjectURL(resolved.url);
          return;
        }
        objectUrl = resolved.revoke ? resolved.url : null;
        setResolvedImageMeta(resolved);
        setResolvedImageUrl(resolved.url);
      })
      .catch((error) => {
        if (!cancelled) {
          console.warn('Could not load P&ID image:', error);
        }
      });

    return () => {
      cancelled = true;
      if (objectUrl) {
        pendingRevokeUrlRef.current = objectUrl;
      }
    };
  }, [canvasImageUrl]);

  const [loadedImage] = useImage(resolvedImageUrl, 'anonymous');
  useEffect(() => {
    if (!loadedImage) return;
    if (resolvedImageUrl && loadedImage.src !== resolvedImageUrl) return;
    const previousObjectUrl = activeObjectUrlRef.current;
    setActiveImage(loadedImage);
    setActiveImageMeta(resolvedImageMeta);
    activeObjectUrlRef.current = resolvedImageMeta?.revoke ? resolvedImageMeta.url : null;
    if (previousObjectUrl && previousObjectUrl !== activeObjectUrlRef.current) {
      window.URL.revokeObjectURL(previousObjectUrl);
    }
    if (pendingRevokeUrlRef.current && pendingRevokeUrlRef.current !== activeObjectUrlRef.current) {
      window.URL.revokeObjectURL(pendingRevokeUrlRef.current);
      pendingRevokeUrlRef.current = null;
    }
  }, [loadedImage, resolvedImageMeta, resolvedImageUrl]);

  useEffect(() => () => {
    if (activeObjectUrlRef.current) window.URL.revokeObjectURL(activeObjectUrlRef.current);
    if (pendingRevokeUrlRef.current) window.URL.revokeObjectURL(pendingRevokeUrlRef.current);
  }, []);

  const image = activeImage;
  const imageCanvasWidth = logicalImageSize?.width || activeImageMeta?.originalWidth || image?.naturalWidth || image?.width || null;
  const imageCanvasHeight = logicalImageSize?.height || activeImageMeta?.originalHeight || image?.naturalHeight || image?.height || null;

  // Canvas interaction batching — flushes accumulated counts every 10s
  const interactionCounts = useRef<Record<string, number>>({});
  useEffect(() => {
    const timer = setInterval(() => {
      const counts = interactionCounts.current;
      if (Object.keys(counts).length > 0) {
        performance.mark('canvas-flush-start');
        onCanvasInteraction?.({ ...counts });
        performance.mark('canvas-flush-end');
        performance.measure('canvas-flush', 'canvas-flush-start', 'canvas-flush-end');
        interactionCounts.current = {};
      }
    }, 10_000);
    return () => clearInterval(timer);
  }, [onCanvasInteraction]);
  const trackCanvasEvent = useCallback((type: string) => {
    interactionCounts.current[type] = (interactionCounts.current[type] ?? 0) + 1;
  }, []);

  // Reviewer mode: only allow select (pan) and comment
  const effectiveReadOnly = readOnly;
  const equipmentInstruments = useMemo(
    () => getEquipmentContextInstruments(pnidState),
    [pnidState],
  );
  const nodeInstruments = useMemo(
    () => getNodeContextInstruments(pnidState),
    [pnidState],
  );
  const unattachedInstruments = useMemo(
    () => getUnattachedInstruments(pnidState),
    [pnidState],
  );
  const nodesById = useMemo(() => {
    const map = new Map<string, PnidWizardState['nodes'][number]>();
    for (const node of pnidState?.nodes ?? []) {
      map.set(node.id, node);
    }
    return map;
  }, [pnidState]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawingBox, setDrawingBox] = useState<DrawingBox | null>(null);
  // The one resting state: on the annotating surface a drag draws a box and a click selects
  // a mark or starts a line; on a viewer the canvas pans. Comment and Move All are modes the SME picks.
  const defaultDrawMode: DrawMode = annotating ? 'rect' : 'select';
  const [chosenDrawMode, setDrawMode] = useState<DrawMode>(defaultDrawMode);
  /** The box a drag finished with `askKindOnDraw` on, waiting on the kind pill. */
  const [pendingBox, setPendingBox] = useState<BoundingBox | null>(null);
  // A click on an asset lets go of the pending box, then selects the asset as before.
  const onSelectWizardItem = useMemo<PidCanvasProps['onSelectWizardItem']>(
    () => onSelectWizardItemProp && ((target) => {
      setPendingBox(null);
      onSelectWizardItemProp(target);
    }),
    [onSelectWizardItemProp],
  );
  // A read-only canvas lets go of the pending box too, so its pill cannot mark an asset.
  useEffect(() => {
    if (!askKindOnDraw || readOnly) setPendingBox(null);
  }, [askKindOnDraw, readOnly]);
  const kindPillOpen = pendingBox !== null;
  useEffect(() => {
    onKindPillOpenChange?.(kindPillOpen);
  }, [kindPillOpen, onKindPillOpenChange]);
  // Konva paints a bitmap and cannot read a CSS custom property, so the pending box's
  // stroke takes the brand token's resolved value.
  const pendingBoxStroke = useMemo(
    () => getComputedStyle(document.documentElement).getPropertyValue('--raven-primary-main').trim(),
    [],
  );
  const focusOverlayStroke = useMemo(
    () => getComputedStyle(document.documentElement).getPropertyValue('--raven-warning').trim(),
    [],
  );

  React.useEffect(() => {
    // A read-only canvas pans. Leaving read-only, as the Drawing details dock closes,
    // returns to rest.
    if (effectiveReadOnly && chosenDrawMode !== 'select') {
      setDrawMode('select');
    } else if (!effectiveReadOnly && chosenDrawMode === 'select') {
      setDrawMode(defaultDrawMode);
    }
  }, [effectiveReadOnly, chosenDrawMode, defaultDrawMode]);

  const [polyPoints, setPolyPoints] = useState<number[]>([]); // [x1,y1,x2,y2,...]
  const [isPolyDrawing, setIsPolyDrawing] = useState(false);
  // The line in flight (click to add points, for connections). `pathPoints` is its flat
  // [x1,y1,x2,y2,...] view, which the render reads.
  const [lineDraft, setLineDraft] = useState<LineDraft>(EMPTY_LINE_DRAFT);
  const pathPoints = useMemo(() => toPolyline(lineDraft), [lineDraft]);
  // A line in flight, or a tool that draws lines (Line armed, or a redraw), takes the box
  // mode over, unless the line in hand is a duct, which is drawn as a box. A line a click
  // started stays in flight while its undone vertices wait for a redo.
  const lineIntent = armedTool === 'line' || isNodeMarkingMode;
  const lineInFlight = pathPoints.length > 0 || (lineDraft.redo.length > 0 && !armedTool);
  const drawMode: DrawMode = chosenDrawMode === 'rect'
    && (lineInFlight || (lineIntent && nodeDrawingShape !== 'duct'))
    ? 'path'
    : chosenDrawMode;
  // At rest, nothing armed and nothing drawn, a click on a saved mark selects it.
  const marksTakeClicks = (drawMode === 'rect' || drawMode === 'select') && !armedTool && !collectingRegions;
  // The left press in flight, in container px, and whether it landed on empty paper.
  const pressRef = useRef<{ x: number; y: number; onEmptyPaper: boolean } | null>(null);
  // Whether the last release ended a drag. A mark reads it on its click, which Konva fires
  // after the release, before React renders what the release changed.
  const pressDraggedRef = useRef(false);
  // Whether the last release started a line from the mark it landed on. The mark's click,
  // which follows, must not also select it.
  const pressStartedLineFromMarkRef = useRef(false);
  // The left press that landed on a target to copy, in container px. The native press
  // listener claims it before Konva sees the press, so a mark that stops the bubble
  // cannot keep it from the release.
  const copyPressRef = useRef<{ x: number; y: number; clickTarget: ClickTarget } | null>(null);
  // Whether the last release copied a target. A mark's click, which follows, must not
  // also select or attach the mark the target sits on.
  const pressCopiedRef = useRef(false);
  // The release that placed the last vertex, in container px, for the double-click finish.
  const lastPathClickRef = useRef<{ x: number; y: number; time: number } | null>(null);
  // finishPathDrawing is declared after the press handler, which calls it through this ref.
  const finishPathDrawingRef = useRef<() => void>(() => undefined);
  // The marked geometries a line vertex binds to (D1), and the one under the pointer now.
  // A line being redrawn is hidden while it is marked, and is no target for its own vertices.
  const bindTargets = useMemo(() => {
    if (!pnidState) return null;
    const lineUnderRedrawId = isNodeMarkingMode ? activeNodeId : null;
    return {
      equipments: pnidState.equipments,
      instruments: pnidState.instruments,
      nodes: lineUnderRedrawId ? pnidState.nodes.filter((node) => node.id !== lineUnderRedrawId) : pnidState.nodes,
    };
  }, [pnidState, isNodeMarkingMode, activeNodeId]);
  const [bindHover, setBindHover] = useState<EntityHit | null>(null);
  useEffect(() => {
    if (drawMode !== 'path' || pendingMarkBbox) setBindHover(null);
  }, [drawMode, pendingMarkBbox]);
  const [pathPreviewPoint, setPathPreviewPoint] = useState<{ x: number; y: number } | null>(null);
  const [cursorPosition, setCursorPosition] = useState<{ x: number; y: number } | null>(null);
  const cursorPositionRef = useRef<{ x: number; y: number } | null>(null);
  const cursorRafRef = useRef<number | null>(null);
  // Space key for temporary pan mode (Figma-style)
  const [isSpaceHeld, setIsSpaceHeld] = useState(false);
  // Shift held hides the Done pill, so the SME can read the text under it.
  const [isShiftHeld, setIsShiftHeld] = useState(false);
  // Where the SME dragged the Done pill, in container pixels. Null keeps the pill under
  // the last drawn region; a drag pins it for the rest of that draft.
  const [donePillDraggedPosition, setDonePillDraggedPosition] = useState<{ left: number; top: number } | null>(null);
  const [isDonePillDragging, setIsDonePillDragging] = useState(false);
  const donePillDragRef = useRef<{
    pointerId: number;
    startClientX: number;
    startClientY: number;
    startLeft: number;
    startTop: number;
    pillWidth: number;
    moved: boolean;
  } | null>(null);
  // The click that ends a drag must not also finish the draft.
  const suppressDonePillClickRef = useRef(false);
  useEffect(() => {
    if (!collectingRegions) {
      setDonePillDraggedPosition(null);
      setIsDonePillDragging(false);
      donePillDragRef.current = null;
    }
  }, [collectingRegions]);
  // X held isolates the active asset, whatever the toolbar's Isolate button says.
  const [isIsolateKeyHeld, setIsIsolateKeyHeld] = useState(false);
  const isolating = isolateActiveAsset || isIsolateKeyHeld;
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  
  // Hover tooltip state
  const [hoveredItem, setHoveredItem] = useState<HoveredItem | null>(null);
  const [hoveredClickTargetId, setHoveredClickTargetId] = useState<ClickTargetId | null>(null);
  // Clearing hover is slightly delayed so the resize handles / quick-delete badge
  // rendered on hover stay mounted while the pointer travels onto them.
  const hoverClearTimeoutRef = useRef<number | null>(null);
  const cancelHoverClear = useCallback(() => {
    if (hoverClearTimeoutRef.current !== null) {
      window.clearTimeout(hoverClearTimeoutRef.current);
      hoverClearTimeoutRef.current = null;
    }
  }, []);
  const scheduleHoverClear = useCallback(() => {
    cancelHoverClear();
    hoverClearTimeoutRef.current = window.setTimeout(() => {
      hoverClearTimeoutRef.current = null;
      setHoveredItem(null);
    }, 120);
  }, [cancelHoverClear]);
  useEffect(() => () => cancelHoverClear(), [cancelHoverClear]);

  const [annotationShiftPreview, setAnnotationShiftPreview] = useState({ dx: 0, dy: 0 });
  const [isAnnotationShiftSaving, setIsAnnotationShiftSaving] = useState(false);
  const [selectedAnnotationShiftPreview, setSelectedAnnotationShiftPreview] = useState({ dx: 0, dy: 0 });
  const [isSelectedAnnotationShiftSaving, setIsSelectedAnnotationShiftSaving] = useState(false);

  
  useEffect(() => {
    if (drawMode !== 'shift-annotations') {
      setAnnotationShiftPreview({ dx: 0, dy: 0 });
    }
  }, [drawMode]);
  
  // Dynamic container size when width/height props are not provided
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState<{ width: number; height: number }>({
    width: widthProp ?? 800,
    height: heightProp ?? 600,
  });

  const keepVisibleBboxRef = useRef(keepVisibleBbox);
  /** The container's screen rect as the last resize, or fit request, saw it. */
  const observedContainerRectRef = useRef<DOMRect | null>(null);
  useEffect(() => {
    keepVisibleBboxRef.current = keepVisibleBbox;
  }, [keepVisibleBbox]);

  // Observe parent size changes
  useEffect(() => {
    if (widthProp && heightProp) return; // static sizing provided

    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    observedContainerRectRef.current = el.getBoundingClientRect();
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setContainerSize((current) => (
          current.width === width && current.height === height
            ? current
            : { width, height }
        ));

        // The stage hangs from the container's top-left corner. When a rail or a
        // banner moves that corner on screen, the drawing pans back by the same
        // amount, so it stays where the SME was looking.
        const rect = el.getBoundingClientRect();
        const before = { rect: observedContainerRectRef.current ?? rect, position: positionRef.current };
        observedContainerRectRef.current = rect;
        const scale = scaleRef.current;
        let next = {
          x: before.position.x - (rect.left - before.rect.left),
          y: before.position.y - (rect.top - before.rect.top),
        };
        // A kept box that was wholly on screen before the resize stays on screen.
        const keepVisible = keepVisibleBboxRef.current;
        if (keepVisible && isWhollyOnScreen(before.position, scale, keepVisible, before.rect)) {
          next = panToKeepVisible(next, scale, keepVisible, { width, height });
        }
        if (next.x === before.position.x && next.y === before.position.y) continue;
        next = clampPanPosition(next, scale, { width, height }, panContextRef.current.imageSize);
        positionRef.current = next;
        setPosition(next);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [widthProp, heightProp]);

  // Decide working width & height
  const width = widthProp ?? containerSize.width;
  const height = heightProp ?? containerSize.height;
  const [interactionImage, setInteractionImage] = useState<HTMLCanvasElement | null>(null);
  const [isZoomInteractionActive, setIsZoomInteractionActive] = useState(false);
  const [isPanInteractionActive, setIsPanInteractionActive] = useState(false);

  const lastImageSizeRef = useRef<{ width: number; height: number } | null>(null);
  useEffect(() => {
    if (!image) return;
    if (!onImageSize) return;
    const w = imageCanvasWidth || (image as any).naturalWidth || image.width;
    const h = imageCanvasHeight || (image as any).naturalHeight || image.height;
    if (!w || !h) return;
    const last = lastImageSizeRef.current;
    if (last && last.width === w && last.height === h) return;
    lastImageSizeRef.current = { width: w, height: h };
    onImageSize(w, h);
  }, [image, imageCanvasHeight, imageCanvasWidth, onImageSize]);

  const interactionImageIdleTimerRef = useRef<number | null>(null);
  const panInteractionIdleTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!image) {
      setInteractionImage(null);
      return;
    }

    const sourceWidth = (image as HTMLImageElement).naturalWidth || image.width;
    const sourceHeight = (image as HTMLImageElement).naturalHeight || image.height;
    if (!sourceWidth || !sourceHeight) return;

    const maxSourceDimension = Math.max(sourceWidth, sourceHeight);
    if (maxSourceDimension <= INTERACTION_IMAGE_MAX_DIMENSION) {
      setInteractionImage(null);
      return;
    }

    let cancelled = false;
    const buildInteractionImage = () => {
      const scaleFactor = INTERACTION_IMAGE_MAX_DIMENSION / maxSourceDimension;
      const renderWidth = Math.max(1, Math.round(sourceWidth * scaleFactor));
      const renderHeight = Math.max(1, Math.round(sourceHeight * scaleFactor));
      const canvas = document.createElement('canvas');
      canvas.width = renderWidth;
      canvas.height = renderHeight;

      const ctx = canvas.getContext('2d', { alpha: false });
      if (!ctx) return;

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'low';
      ctx.drawImage(image, 0, 0, sourceWidth, sourceHeight, 0, 0, renderWidth, renderHeight);

      if (cancelled) return;

      setInteractionImage(canvas);
    };

    const idleCallback = window.requestIdleCallback;
    if (typeof idleCallback === 'function') {
      const id = idleCallback(buildInteractionImage, { timeout: 500 });
      return () => {
        cancelled = true;
        window.cancelIdleCallback?.(id);
      };
    }

    const timeoutId = window.setTimeout(buildInteractionImage, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [image]);

  // Zoom and pan state
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  
  // Refs to track latest scale/position for wheel handler (avoids stale closure)
  const scaleRef = useRef(scale);
  const positionRef = useRef(position);
  useEffect(() => { scaleRef.current = scale; }, [scale]);
  useEffect(() => { onScaleChange?.(scale); }, [scale, onScaleChange]);
  // True while the edge-pan loop, or a held arrow key, writes `positionRef`
  // directly. The passive sync below would otherwise rewind the ref to the value
  // React committed one or two frames ago, and the drawing would jerk backwards
  // on every throttled commit.
  const edgePanMovingRef = useRef(false);
  const keyPanActiveRef = useRef(false);
  const isImperativePanActive = useCallback(
    () => edgePanMovingRef.current || keyPanActiveRef.current,
    []
  );
  useEffect(() => {
    if (edgePanMovingRef.current || keyPanActiveRef.current) return;
    positionRef.current = position;
  }, [position]);

  useEffect(() => {
    if (!docId || !imageCanvasWidth || !imageCanvasHeight) return;
    const timer = window.setTimeout(() => {
      const nextLevel = choosePnidImageLevel(scaleRef.current, imageCanvasWidth, imageCanvasHeight);
      setDesiredImageMaxDim((current) => (current === nextLevel ? current : nextLevel));
    }, IMAGE_LEVEL_SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [docId, imageCanvasHeight, imageCanvasWidth, scale]);
  
  const stageRef = useRef<any>(null);
  const panMovedRef = useRef(false);
  const panPointerStartRef = useRef<{ x: number; y: number } | null>(null);

  /** Unscaled drawing size, or null before the drawing loads. */
  const panImageSize = useMemo<PanImageSize | null>(() => {
    const drawingWidth = imageCanvasWidth || image?.width || 0;
    const drawingHeight = imageCanvasHeight || image?.height || 0;
    if (!(drawingWidth > 0) || !(drawingHeight > 0)) return null;
    return { width: drawingWidth, height: drawingHeight };
  }, [image, imageCanvasHeight, imageCanvasWidth]);

  /**
   * Latest inputs every pan path needs, held in one ref.
   *
   * The edge-pan loop runs on animation frames rather than renders, and the
   * wheel handler is declared before the values it clamps against exist. Both
   * read this instead of closing over state that would be a render behind.
   */
  const panContextRef = useRef<{
    viewport: PanViewport;
    imageSize: PanImageSize | null;
    /** True while the SME already drives the drawing by hand. */
    suppressed: boolean;
    /** True while a gesture tracks the image coordinate under the cursor. */
    gestureInFlight: boolean;
  }>({
    viewport: { width: 0, height: 0 },
    imageSize: null,
    suppressed: false,
    gestureInFlight: false,
  });
  useEffect(() => {
    const gestureInFlight =
      isDrawing
      || isPolyDrawing
      || (drawMode === 'path' && pathPoints.length >= 2);
    panContextRef.current = {
      viewport: { width, height },
      imageSize: panImageSize,
      // A manual drag pan and a held Space bar already move the drawing. Edge
      // pan must not push against them.
      suppressed: isPanning || isSpaceHeld || isDonePillDragging || (!edgePanEnabled && !gestureInFlight),
      gestureInFlight,
    };
  }, [
    drawMode,
    edgePanEnabled,
    height,
    isDrawing,
    isDonePillDragging,
    isPanning,
    isPolyDrawing,
    isSpaceHeld,
    panImageSize,
    pathPoints.length,
    width,
  ]);
  const usingInteractionImage = Boolean(interactionImage) && (isPanning || isZoomInteractionActive || isPanInteractionActive);
  const isInteractionLightMode = isPanning || isZoomInteractionActive || isPanInteractionActive;
  const isOverviewLightMode = Boolean(annotating && scale < OVERVIEW_DETAIL_SCALE);
  const reducePassiveOverlays = isInteractionLightMode || isOverviewLightMode;
  const suppressHoverInspection = isInteractionLightMode;
  useEffect(() => {
    if (suppressHoverInspection) {
      setHoveredItem(null);
    }
  }, [suppressHoverInspection]);
  const viewportBounds = useMemo<CanvasBounds>(() => {
    const safeScale = Math.max(scale, 1e-6);
    const padding = Math.max(
      VIEWPORT_CULL_MIN_IMAGE_PADDING,
      VIEWPORT_CULL_PADDING_SCREEN_PX / safeScale
    );

    return {
      x1: (-position.x) / safeScale - padding,
      y1: (-position.y) / safeScale - padding,
      x2: (width - position.x) / safeScale + padding,
      y2: (height - position.y) / safeScale + padding,
    };
  }, [height, position.x, position.y, scale, width]);
  const isInViewport = useCallback(
    (bounds: CanvasBounds | BoundingBox | null | undefined, force = false): boolean => {
      if (force) return true;
      if (!bounds) return false;
      return boundsIntersect(bounds, viewportBounds);
    },
    [viewportBounds]
  );
  const clickTargetsInViewport = useMemo(
    () => clickTargets.filter(({ bbox }) => isInViewport(bbox)),
    [clickTargets, isInViewport]
  );
  const hoveredClickTarget =
    clickTargetsInViewport.find(({ id }) => id === hoveredClickTargetId) ?? null;
  const hoveredClickTargetLabel = useMemo(() => {
    if (!hoveredClickTarget) return null;
    const text = clickTargetLabel(hoveredClickTarget);
    const { bbox } = hoveredClickTarget;
    const labelSize = measureClickTargetLabel(text, scale, width);
    const labelOnScreen = placeClickTargetLabel(
      {
        left: bbox.x1 * scale + position.x,
        top: bbox.y1 * scale + position.y,
        right: bbox.x2 * scale + position.x,
        bottom: bbox.y2 * scale + position.y,
      },
      labelSize.onScreen,
      { width, height },
    );
    return {
      text,
      truncatedWidth: labelSize.truncatedSheetWidth,
      x: (labelOnScreen.x - position.x) / scale,
      y: (labelOnScreen.y - position.y) / scale,
    };
  }, [hoveredClickTarget, scale, position, width, height]);
  // The tokens are static in the stylesheet, so one read per mount is enough.
  const clickTargetColors = useMemo(() => ({
    resting: readDesignToken('--raven-divider'),
    hovered: readDesignToken('--raven-primary-main'),
    labelText: readDesignToken('--raven-primary-contrast-text'),
  }), []);

  /* Perf HUD: a debugging instrument, so it is opt-IN, not opt-out. It used to
     default on, which made a black debug panel the loudest element on the page
     for every user. `?canvasPerf=1` or `localStorage.canvasPerf = "1"` turns it
     on; in a dev build it also stays on once enabled. */
  const [perfDebugEnabled, setPerfDebugEnabled] = useState(() => {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    const qp = params.get('canvasPerf');
    if (qp === '0') return false;
    if (qp === '1') return true;
    /* A NEW key: the old default-on build persisted canvasPerf="1" for every
       user, so honouring it would keep the panel up forever. */
    const ls = window.localStorage.getItem('pnid_canvas_perf');
    if (ls === '1') return true;
    return false;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (perfDebugEnabled) window.localStorage.setItem('pnid_canvas_perf', '1');
    else window.localStorage.removeItem('pnid_canvas_perf');
  }, [perfDebugEnabled]);

  const [nodePointMode, setNodePointMode] = useState<NodePointRenderMode>(() => {
    if (typeof window === 'undefined') return 'active';
    const raw = window.localStorage.getItem('pidcanvas_nodePointMode');
    return raw === 'all' || raw === 'active' || raw === 'none' ? raw : 'active';
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('pidcanvas_nodePointMode', nodePointMode);
  }, [nodePointMode]);

  const perfStats = useMemo(() => {
    if (!pnidState) return null;
    let totalNodePoints = 0;
    let nodesWithPolyline = 0;
    for (const node of pnidState.nodes) {
      if (!node.polyline || node.polyline.length < 4) continue;
      nodesWithPolyline += 1;
      totalNodePoints += node.polyline.length / 2;
    }
    return {
      nodes: pnidState.nodes.length,
      nodesWithPolyline,
      totalNodePoints,
    };
  }, [pnidState]);

  const activatePanInteractionRaster = useCallback(() => {
    setIsPanInteractionActive(true);
    if (panInteractionIdleTimerRef.current !== null) {
      window.clearTimeout(panInteractionIdleTimerRef.current);
      panInteractionIdleTimerRef.current = null;
    }
  }, []);

  const releasePanInteractionRasterSoon = useCallback(() => {
    if (panInteractionIdleTimerRef.current !== null) {
      window.clearTimeout(panInteractionIdleTimerRef.current);
    }
    panInteractionIdleTimerRef.current = window.setTimeout(() => {
      setIsPanInteractionActive(false);
      panInteractionIdleTimerRef.current = null;
    }, INTERACTION_IMAGE_IDLE_MS);
  }, []);

  const getPointerScreenPosition = useCallback((): { x: number; y: number } | undefined => {
    const stage = stageRef.current;
    const pointer = stage?.getPointerPosition?.();
    const container = stage?.container?.();
    if (!pointer || !container) return undefined;
    const rect = container.getBoundingClientRect();
    return { x: rect.left + pointer.x, y: rect.top + pointer.y };
  }, []);

  const segmentReviewNode = useMemo(() => {
    if (!annotating || !pnidState) return null;
    const nodeId = segmentReview?.nodeId;
    if (!nodeId) return null;
    return pnidState.nodes.find((n) => n.id === nodeId) ?? null;
  }, [annotating, pnidState, segmentReview?.nodeId]);

  const segmentReviewRaw = segmentReviewNode?.line_segments ?? [];

  const segScale = useMemo(() => {
    const sx = segmentCoordinateScale?.sx ?? 1;
    const sy = segmentCoordinateScale?.sy ?? 1;
    return { sx, sy };
  }, [segmentCoordinateScale?.sx, segmentCoordinateScale?.sy]);

  const annotationBounds = useMemo(() => {
    if (!annotating || !pnidState) return null;
    return getPnidAnnotationBounds(pnidState, segScale);
  }, [annotating, pnidState, segScale]);

  const selectedAnnotationBounds = useMemo(() => {
    if (!annotating || !pnidState || !selectedWizardItemKeys?.size) return null;
    let bounds: CanvasBounds | null = null;
    for (const key of selectedWizardItemKeys) {
      const target = parseWizardCanvasItemKey(key);
      if (!target) continue;
      bounds = mergeBounds(bounds, getPnidAnnotationTargetBounds(pnidState, target, segScale));
    }
    return bounds;
  }, [annotating, pnidState, selectedWizardItemKeys, segScale]);

  const shouldRenderSelectedShiftBounds = useMemo(() => {
    const count = selectedWizardItemKeys?.size ?? 0;
    if (count > 1) return true;
    if (count !== 1) return false;
    const first = selectedWizardItemKeys ? Array.from(selectedWizardItemKeys)[0] : null;
    return parseWizardCanvasItemKey(first ?? "")?.kind === "node";
  }, [selectedWizardItemKeys]);

  const segmentReviewStatusById = useMemo(() => {
    const m = new Map<string, LineSegmentStatus>();
    for (const s of segmentReviewRaw) m.set(s.id, s.status);
    return m;
  }, [segmentReviewRaw]);

  const segmentReviewRawScaled = useMemo(() => {
    if (segScale.sx === 1 && segScale.sy === 1) return segmentReviewRaw;
    return segmentReviewRaw.map((s) => ({
      ...s,
      x1: s.x1 * segScale.sx,
      y1: s.y1 * segScale.sy,
      x2: s.x2 * segScale.sx,
      y2: s.y2 * segScale.sy,
    }));
  }, [segmentReviewRaw, segScale.sx, segScale.sy]);

  const segmentReviewDistinct = useMemo<MergedLineSegment[]>(() => {
    if (!segmentReviewRawScaled.length) return [];
    return mergeOverlappingLineSegments(
      segmentReviewRawScaled.map((s) => ({
        id: s.id,
        x1: s.x1,
        y1: s.y1,
        x2: s.x2,
        y2: s.y2,
        kind: s.kind ?? undefined,
        confidence: s.confidence ?? undefined,
      }))
    );
  }, [segmentReviewRawScaled]);

  useEffect(() => {
    return () => {
      if (interactionImageIdleTimerRef.current !== null) {
        window.clearTimeout(interactionImageIdleTimerRef.current);
      }
      if (panInteractionIdleTimerRef.current !== null) {
        window.clearTimeout(panInteractionIdleTimerRef.current);
      }
    };
  }, []);

  const deriveDistinctStatus = useCallback(
    (distinct: MergedLineSegment): LineSegmentStatus => {
      const statuses = distinct.source_segment_ids
        .map((id) => segmentReviewStatusById.get(id))
        .filter(Boolean) as LineSegmentStatus[];
      if (!statuses.length) return 'pending';
      if (statuses.every((s) => s === 'deleted')) return 'deleted';
      if (statuses.some((s) => s === 'edited')) return 'edited';
      if (statuses.some((s) => s === 'pending')) return 'pending';
      if (statuses.every((s) => s === 'confirmed' || s === 'deleted')) return 'confirmed';
      return 'pending';
    },
    [segmentReviewStatusById]
  );

  const getSegmentStroke = useCallback(
    (status: LineSegmentStatus): { stroke: string; dash?: number[] } => {
      switch (status) {
        case 'confirmed':
          return { stroke: '#22c55e' };
        case 'edited':
          return { stroke: '#f59e0b' };
        case 'deleted':
          return { stroke: '#ef4444', dash: [8 / scale, 6 / scale] };
        case 'pending':
        default:
          // Lighter green for inactive stroke contrast
          return { stroke: '#4ade80', dash: [6 / scale, 6 / scale] };
      }
    },
    [scale]
  );

  // Zoom constraints

  const hasDraft = Boolean(
    isDrawing ||
    drawingBox ||
    isPolyDrawing ||
    polyPoints.length > 0 ||
    pathPoints.length > 0 ||
    isAnnotationShiftSaving ||
    annotationShiftPreview.dx !== 0 ||
    annotationShiftPreview.dy !== 0 ||
    isSelectedAnnotationShiftSaving ||
    selectedAnnotationShiftPreview.dx !== 0 ||
    selectedAnnotationShiftPreview.dy !== 0
  );

  useEffect(() => {
    onDraftStateChange?.(hasDraft);
  }, [hasDraft, onDraftStateChange]);

  useEffect(() => {
    if (clearDraftSignal === undefined) return;
    setIsDrawing(false);
    setDrawingBox(null);
    setIsPolyDrawing(false);
    setPolyPoints([]);
    // The redo history goes with the points: a redo after a save must not bring old points back.
    setLineDraft(EMPTY_LINE_DRAFT);
    setPathPreviewPoint(null);
    setAnnotationShiftPreview({ dx: 0, dy: 0 });
  }, [clearDraftSignal]);

  useEffect(() => {
    if (pendingMarkBbox) {
      setPathPreviewPoint(null);
    }
  }, [pendingMarkBbox]);

  const handleWheel = useCallback((e: any) => {
    e.evt.preventDefault();
    
    const stage = stageRef.current;
    if (!stage) return;

    // Use refs to get latest values (avoids stale closure on first zoom after fit)
    const currentScale = scaleRef.current;
    const currentPosition = positionRef.current;

    const pointer = stage.getPointerPosition();
    if (!pointer) return;
    // Velocity-based zoom: use deltaY magnitude
    const deltaY = Math.abs(e.evt.deltaY);
    const direction = e.evt.deltaY > 0 ? -1 : 1;
    
    // Map deltaY to zoom factor (small scroll = 1.02, fast scroll = 1.15)
    // Typical deltaY: trackpad ~1-10, mouse wheel ~50-150
    const minFactor = 1.02;
    const maxFactor = 1.15;
    const normalizedDelta = Math.min(deltaY / 100, 1); // clamp to 0-1
    const factor = minFactor + (maxFactor - minFactor) * normalizedDelta;
    
    const zoomed = zoomAtPoint(currentPosition, currentScale, pointer, Math.pow(factor, direction));
    const newScale = zoomed.scale;
    const newPosition = clampPanPosition(
      zoomed.position,
      newScale,
      panContextRef.current.viewport,
      panContextRef.current.imageSize
    );

    setIsZoomInteractionActive(true);
    if (interactionImageIdleTimerRef.current !== null) {
      window.clearTimeout(interactionImageIdleTimerRef.current);
    }
    interactionImageIdleTimerRef.current = window.setTimeout(() => {
      setIsZoomInteractionActive(false);
      interactionImageIdleTimerRef.current = null;
    }, INTERACTION_IMAGE_IDLE_MS);

    setScale(newScale);
    setPosition(newPosition);
    trackCanvasEvent('zoom');
  }, [trackCanvasEvent]);

  const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

  const clampBboxToImage = useCallback((bbox: BoundingBox) => {
    if (!image) return bbox;
    const maxX = imageCanvasWidth || image.width;
    const maxY = imageCanvasHeight || image.height;
    return {
      ...bbox,
      x1: clamp(bbox.x1, 0, maxX),
      y1: clamp(bbox.y1, 0, maxY),
      x2: clamp(bbox.x2, 0, maxX),
      y2: clamp(bbox.y2, 0, maxY),
    };
  }, [image, imageCanvasHeight, imageCanvasWidth]);

  const clampTranslatedBboxToImage = useCallback((bbox: BoundingBox) => {
    if (!image) return bbox;
    const maxX = imageCanvasWidth || image.width;
    const maxY = imageCanvasHeight || image.height;
    const width = Math.max(0, bbox.x2 - bbox.x1);
    const height = Math.max(0, bbox.y2 - bbox.y1);
    const x1 = clamp(bbox.x1, 0, Math.max(0, maxX - width));
    const y1 = clamp(bbox.y1, 0, Math.max(0, maxY - height));
    return {
      ...bbox,
      x1,
      y1,
      x2: x1 + width,
      y2: y1 + height,
    };
  }, [image, imageCanvasHeight, imageCanvasWidth]);

  // Simple polygon simplification (Douglas-Peucker-like)
  const simplifyPolygon = useCallback((points: number[], tolerance: number = 2): number[] => {
    if (points.length <= 6) return points; // keep if 3 or fewer points

    const simplified: number[] = [points[0], points[1]]; // always keep first point

    for (let i = 2; i < points.length - 1; i += 2) {
      const prevX = simplified[simplified.length - 2];
      const prevY = simplified[simplified.length - 1];
      const currX = points[i];
      const currY = points[i + 1];

      // Keep point if it's far enough from the previous kept point
      const dist = Math.sqrt((currX - prevX) ** 2 + (currY - prevY) ** 2);
      if (dist > tolerance) {
        simplified.push(currX, currY);
      }
    }

    // Always keep the last point
    simplified.push(points[points.length - 2], points[points.length - 1]);

    return simplified;
  }, []);


  // translate bbox top-right into viewport coords and offset 15px to right
  const computeAnchor = useCallback((box: BoundingBox): { x: number; y: number } => {
    if (!containerRef.current) return { x: 20, y: 20 };
    const rect = containerRef.current.getBoundingClientRect();
    const xCanvas = box.x2 * scaleRef.current + positionRef.current.x;
    const yCanvas = box.y1 * scaleRef.current + positionRef.current.y;
    return { x: rect.left + xCanvas + 15, y: rect.top + yCanvas };
  }, []);

  const updatePendingBbox = useCallback((bbox: BoundingBox) => {
    if (!onUpdatePendingMarkBbox) return;
    onUpdatePendingMarkBbox(clampBboxToImage(bbox));
  }, [onUpdatePendingMarkBbox, clampBboxToImage]);

  const updatePersistedBbox = useCallback((
    target: PersistedBboxCanvasTarget,
    bbox: BoundingBox,
    commit: boolean = false,
    regionIndex?: number,
  ) => {
    if (!onUpdateWizardItemBbox) return;
    onUpdateWizardItemBbox(target, clampBboxToImage(bbox), { commit, regionIndex });
  }, [onUpdateWizardItemBbox, clampBboxToImage]);

  /**
   * Move/resize affordance for one persisted rectangle. A multi-region marking renders
   * these once per region and names the region in `opts.regionIndex`, so the drag
   * commits against the rectangle the SME grabbed rather than the marking's first one.
   */
  const renderPersistedBboxHandles = useCallback((
    target: PersistedBboxCanvasTarget,
    bbox: BoundingBox,
    stroke: string,
    opts?: { onDblClick?: () => void; regionIndex?: number },
  ) => {
    if (!onUpdateWizardItemBbox || readOnly || !marksTakeClicks || drawingBox) return null;

    const width = bbox.x2 - bbox.x1;
    const height = bbox.y2 - bbox.y1;
    if (width <= 0 || height <= 0) return null;

    const handleSize = 10 / scale;
    const minSize = 6;
    const handles = [
      { key: 'nw', x: bbox.x1, y: bbox.y1 },
      { key: 'ne', x: bbox.x2, y: bbox.y1 },
      { key: 'se', x: bbox.x2, y: bbox.y2 },
      { key: 'sw', x: bbox.x1, y: bbox.y2 },
    ];
    const updateFromHandle = (key: string, x: number, y: number, commit = false) => {
      let { x1, y1, x2, y2 } = bbox;
      if (key === 'nw') {
        x1 = Math.min(x, x2 - minSize);
        y1 = Math.min(y, y2 - minSize);
      } else if (key === 'ne') {
        x2 = Math.max(x, x1 + minSize);
        y1 = Math.min(y, y2 - minSize);
      } else if (key === 'se') {
        x2 = Math.max(x, x1 + minSize);
        y2 = Math.max(y, y1 + minSize);
      } else if (key === 'sw') {
        x1 = Math.min(x, x2 - minSize);
        y2 = Math.max(y, y1 + minSize);
      }
      updatePersistedBbox(target, { x1, y1, x2, y2 }, commit, opts?.regionIndex);
    };

    return (
      <>
        <Rect
          x={bbox.x1}
          y={bbox.y1}
          width={width}
          height={height}
          fill="transparent"
          stroke={stroke}
          strokeWidth={1 / scale}
          dash={[3 / scale, 3 / scale]}
          // The outline is the move handle, and the paper inside takes a new box.
          hitStrokeWidth={8 / scale}
          fillEnabled={false}
          draggable={!isSpaceHeld}
          name="persisted-bbox-adjust"
          onMouseEnter={(e) => {
            cancelHoverClear();
            const stage = e.target.getStage();
            if (stage) stage.container().style.cursor = 'move';
          }}
          onMouseLeave={(e) => {
            scheduleHoverClear();
            const stage = e.target.getStage();
            if (stage) stage.container().style.cursor = '';
          }}
          onMouseDown={(e) => {
            if (isSpaceHeld) return;
            e.cancelBubble = true;
          }}
          onDragStart={(e) => {
            e.cancelBubble = true;
            onBeginWizardItemBboxUpdate?.(target);
          }}
          onDblClick={(e) => {
            if (!opts?.onDblClick || isSpaceHeld) return;
            e.cancelBubble = true;
            opts.onDblClick();
          }}
          onClick={(e) => {
            if (panMovedRef.current || isSpaceHeld) return;
            e.cancelBubble = true;
            onSelectWizardItem?.({
              kind: target.kind,
              id: target.id,
              screenPosition: getPointerScreenPosition(),
              isMultiSelect: e.evt.shiftKey || e.evt.metaKey || e.evt.ctrlKey,
            });
          }}
          onDragMove={(e) => {
            if (isSpaceHeld) return;
            e.cancelBubble = true;
            const node = e.target;
            updatePersistedBbox(target, clampTranslatedBboxToImage({
              x1: node.x(),
              y1: node.y(),
              x2: node.x() + width,
              y2: node.y() + height,
            }), false, opts?.regionIndex);
          }}
          onDragEnd={(e) => {
            if (isSpaceHeld) return;
            e.cancelBubble = true;
            const node = e.target;
            updatePersistedBbox(target, clampTranslatedBboxToImage({
              x1: node.x(),
              y1: node.y(),
              x2: node.x() + width,
              y2: node.y() + height,
            }), true, opts?.regionIndex);
          }}
        />
        {handles.map((handle) => (
          <Rect
            key={`${target.kind}-${target.id}-region-${opts?.regionIndex ?? 0}-handle-${handle.key}`}
            x={handle.x - handleSize / 2}
            y={handle.y - handleSize / 2}
            width={handleSize}
            height={handleSize}
            fill="#ffffff"
            stroke={stroke}
            strokeWidth={2 / scale}
            name="persisted-bbox-handle"
            draggable
            onMouseEnter={(e) => {
              cancelHoverClear();
              const stage = e.target.getStage();
              if (stage) {
                stage.container().style.cursor =
                  handle.key === 'nw' || handle.key === 'se' ? 'nwse-resize' : 'nesw-resize';
              }
            }}
            onMouseLeave={(e) => {
              scheduleHoverClear();
              const stage = e.target.getStage();
              if (stage) stage.container().style.cursor = '';
            }}
            onMouseDown={(e) => {
              e.cancelBubble = true;
            }}
            onDragStart={(e) => {
              e.cancelBubble = true;
              onBeginWizardItemBboxUpdate?.(target);
            }}
            onDragMove={(e) => {
              e.cancelBubble = true;
              const node = e.target;
              updateFromHandle(handle.key, node.x() + handleSize / 2, node.y() + handleSize / 2);
            }}
            onDragEnd={(e) => {
              e.cancelBubble = true;
              const node = e.target;
              updateFromHandle(handle.key, node.x() + handleSize / 2, node.y() + handleSize / 2, true);
            }}
          />
        ))}
      </>
    );
  }, [cancelHoverClear, clampTranslatedBboxToImage, drawingBox, getPointerScreenPosition, isSpaceHeld, marksTakeClicks, onBeginWizardItemBboxUpdate, onSelectWizardItem, onUpdateWizardItemBbox, readOnly, scale, scheduleHoverClear, updatePersistedBbox]);

  const renderQuickDeleteBadge = useCallback((
    target: { kind: 'equipmentInstrument' | 'nodeInstrument'; id: string },
    bbox: BoundingBox,
  ) => {
    if (!onQuickDeleteWizardItem || readOnly || !marksTakeClicks || drawingBox) return null;
    const radius = 9 / scale;
    const armLength = radius * 0.45;
    return (
      <Group
        x={bbox.x2 + 12 / scale}
        y={bbox.y1 - 12 / scale}
        name="quick-delete-badge"
        onMouseEnter={(e) => {
          cancelHoverClear();
          const stage = e.target.getStage();
          if (stage) stage.container().style.cursor = 'pointer';
        }}
        onMouseLeave={(e) => {
          scheduleHoverClear();
          const stage = e.target.getStage();
          if (stage) stage.container().style.cursor = '';
        }}
        onMouseDown={(e) => {
          e.cancelBubble = true;
        }}
        onClick={(e) => {
          e.cancelBubble = true;
          if (e.evt.button !== 0) return;
          const stage = e.target.getStage();
          if (stage) stage.container().style.cursor = '';
          onQuickDeleteWizardItem(target);
        }}
        onTap={(e) => {
          e.cancelBubble = true;
          onQuickDeleteWizardItem(target);
        }}
      >
        <Circle
          radius={radius}
          fill="#dc2626"
          stroke="#ffffff"
          strokeWidth={1.5 / scale}
          perfectDrawEnabled={false}
          shadowForStrokeEnabled={false}
        />
        <Line
          points={[-armLength, -armLength, armLength, armLength]}
          stroke="#ffffff"
          strokeWidth={2 / scale}
          lineCap="round"
          listening={false}
          perfectDrawEnabled={false}
        />
        <Line
          points={[-armLength, armLength, armLength, -armLength]}
          stroke="#ffffff"
          strokeWidth={2 / scale}
          lineCap="round"
          listening={false}
          perfectDrawEnabled={false}
        />
      </Group>
    );
  }, [cancelHoverClear, drawingBox, marksTakeClicks, onQuickDeleteWizardItem, readOnly, scale, scheduleHoverClear]);

  const handleMouseDown = useCallback((e: any) => {
    const stage = stageRef.current;
    if (!stage) return;

    const currentScale = scaleRef.current;
    const currentPosition = positionRef.current;
    const pointer = stage.getPointerPosition();
    if (!pointer) return;
    // The press is a copy, claimed by the native press listener: it neither draws nor pans.
    if (copyPressRef.current) return;

    const target = e.target;
    const isEditingPendingPolyline = Boolean(
      onUpdatePendingMarkBbox &&
      pendingMarkBbox?.polygon &&
      pendingMarkBbox.polygon.length >= 4
    );
    if (targetOrAncestorHasKonvaName(target, [
      'pending-handle',
      'pending-box',
      'pending-point',
      'annotation-shift-bounds',
      'selected-annotation-shift-bounds',
    ])) {
      return;
    }

    const isPassiveOverlayTarget = targetOrAncestorHasKonvaName(target, [
      'wizard-mark',
      'node-point-candidate',
    ]);
    if (drawMode === 'select' && !isSpaceHeld && isPassiveOverlayTarget) {
      return;
    }
    
    // Check if it's a right-click (button 2) or middle-click (button 1) for panning
    if (e.evt.button === 2 || e.evt.button === 1) {
      e.evt.preventDefault();
      e.cancelBubble = true;
      panMovedRef.current = false;
      panPointerStartRef.current = { x: pointer.x, y: pointer.y };
      activatePanInteractionRaster();
      setIsPanning(true);
      setHoveredItem(null);
      setHoveredClickTargetId(null);
      setPanStart({
        x: pointer.x - currentPosition.x,
        y: pointer.y - currentPosition.y,
      });

      return;
    }

    // Space held = pan mode (works in ALL modes, Figma-style)
    if (isSpaceHeld && e.evt.button === 0) {
      e.cancelBubble = true;
      panMovedRef.current = false;
      panPointerStartRef.current = { x: pointer.x, y: pointer.y };
      activatePanInteractionRaster();
      setIsPanning(true);
      setHoveredItem(null);
      setHoveredClickTargetId(null);
      setPanStart({
        x: pointer.x - currentPosition.x,
        y: pointer.y - currentPosition.y,
      });
      return;
    }

    // Select mode - left click to pan (no drawing)
    if (drawMode === 'select' && e.evt.button === 0 && !isSpaceHeld) {
      // While editing a line's points, disable select-mode panning so point drag
      // interactions are not hijacked by stage pan behavior.
      if (isEditingPendingPolyline) {
        return;
      }
      // In select mode, left-click drag pans the canvas
      e.cancelBubble = true;
      panMovedRef.current = false;
      panPointerStartRef.current = { x: pointer.x, y: pointer.y };
      activatePanInteractionRaster();
      setIsPanning(true);
      setHoveredItem(null);
      setHoveredClickTargetId(null);
      setPanStart({
        x: pointer.x - currentPosition.x,
        y: pointer.y - currentPosition.y,
      });
      return;
    }

    if (drawMode === 'shift-annotations' && e.evt.button === 0 && !isPanning) {
      return;
    }

    // Left-click (button 0) for rect drawing - only if not currently panning
    if (drawMode === 'rect' && e.evt.button === 0 && !isPanning) {
      const adjustedPointer = {
        x: (pointer.x - currentPosition.x) / currentScale,
        y: (pointer.y - currentPosition.y) / currentScale,
      };
      
      pressRef.current = { x: pointer.x, y: pointer.y, onEmptyPaper: !isPassiveOverlayTarget };
      setIsDrawing(true);
      trackCanvasEvent('draw_start');
      setDrawingBox({
        x: adjustedPointer.x,
        y: adjustedPointer.y,
        width: 0,
        height: 0,
      });
    }

    // Polygon drawing (drag to draw)
    if (drawMode === 'poly' && e.evt.button === 0 && !isPanning) {
      const adjusted = {
        x: (pointer.x - currentPosition.x) / currentScale,
        y: (pointer.y - currentPosition.y) / currentScale,
      };
      setPolyPoints([adjusted.x, adjusted.y]);
      setIsPolyDrawing(true);
      return;
    }

    // Path mode: the release adds the vertex, when the press did not drag.
    if (drawMode === 'path' && e.evt.button === 0 && !isPanning) {
      if (pendingMarkBbox) {
        return;
      }
      pressRef.current = { x: pointer.x, y: pointer.y, onEmptyPaper: !isPassiveOverlayTarget };
      return;
    }
  }, [
    activatePanInteractionRaster,
    isPanning,
    drawMode,
    isSpaceHeld,
    pendingMarkBbox,
    onUpdatePendingMarkBbox,
    imageCanvasHeight,
    imageCanvasWidth,
  ]);

  /**
   * Apply a pointer position to whichever gesture is in flight.
   *
   * `handleMouseMove` calls this when the cursor moves. The edge-pan loop calls
   * it when the drawing moves under a cursor that is holding still: the screen
   * coordinate is unchanged there, but the image coordinate beneath it is not,
   * so a rectangle or path preview must keep tracking it.
   */
  const applyPointerToGestures = useCallback((
    adjustedPointer: { x: number; y: number },
    currentScale: number
  ) => {
    cursorPositionRef.current = adjustedPointer;
    if (cursorRafRef.current === null) {
      cursorRafRef.current = window.requestAnimationFrame(() => {
        cursorRafRef.current = null;
        setCursorPosition(cursorPositionRef.current);
      });
    }

    // Handle drawing
    if (isDrawing && drawingBox) {
      setDrawingBox({
        ...drawingBox,
        width: adjustedPointer.x - drawingBox.x,
        height: adjustedPointer.y - drawingBox.y,
      });
    }

    if (drawMode === 'poly' && isPolyDrawing) {
      const adjusted = {
        x: adjustedPointer.x,
        y: adjustedPointer.y,
      };
      
      // Only add point if it's far enough from the last point (smoothing)
      const lastX = polyPoints[polyPoints.length - 2];
      const lastY = polyPoints[polyPoints.length - 1];
      const distance = Math.sqrt((adjusted.x - lastX) ** 2 + (adjusted.y - lastY) ** 2);
      
      if (distance > 5 / currentScale) { // minimum distance threshold
        setPolyPoints(prev => [...prev, adjusted.x, adjusted.y]);
      }
      return;
    }

    // Path drawing: the target under the pointer lights before the click lands.
    if (drawMode === 'path' && !isSpaceHeld && !pendingMarkBbox) {
      setBindHover(bindTargets ? entityAtPoint(adjustedPointer, currentScale, bindTargets) : null);
    }
    // Path drawing preview - show line to cursor (when not panning)
    if (drawMode === 'path' && !isSpaceHeld && pathPoints.length >= 2 && !pendingMarkBbox) {
      const adjusted = {
        x: adjustedPointer.x,
        y: adjustedPointer.y,
      };
      setPathPreviewPoint(adjusted);
    }
  }, [bindTargets, isDrawing, drawingBox, drawMode, isPolyDrawing, polyPoints, pathPoints.length, isSpaceHeld, pendingMarkBbox]);

  // --- Edge pan -------------------------------------------------------------
  // The drawing scrolls while the cursor rests in a band along the canvas
  // border, so an SME reaches an off-screen area without leaving the current
  // tool for pan mode. The loop is imperative for the same reason the manual
  // drag pan is: a setState per frame re-renders every overlay on the drawing.

  const applyPointerToGesturesRef = useRef(applyPointerToGestures);
  useEffect(() => {
    applyPointerToGesturesRef.current = applyPointerToGestures;
  }, [applyPointerToGestures]);

  const canvasPointerRef = useRef<PanPointer | null>(null);
  // A still pointer sits over another target once a zoom or a pan moves the view under it.
  useEffect(() => {
    const pointer = canvasPointerRef.current;
    if (drawMode !== 'path' || isSpaceHeld || pendingMarkBbox || !pointer || !bindTargets) return;
    const imagePoint = { x: (pointer.x - position.x) / scale, y: (pointer.y - position.y) / scale };
    setBindHover(entityAtPoint(imagePoint, scale, bindTargets));
  }, [scale, position, drawMode, isSpaceHeld, pendingMarkBbox, bindTargets]);
  const edgePanFrameRef = useRef<number | null>(null);
  const edgePanLastFrameMsRef = useRef(0);
  const edgePanLastCommitMsRef = useRef(0);
  const edgePanDwellStartMsRef = useRef(0);
  const runEdgePanFrameRef = useRef<(timestampMs: number) => void>(() => {});

  const stopEdgePan = useCallback(() => {
    if (edgePanFrameRef.current === null) return;
    window.cancelAnimationFrame(edgePanFrameRef.current);
    edgePanFrameRef.current = null;
    if (!edgePanMovingRef.current) return;
    edgePanMovingRef.current = false;
    releasePanInteractionRasterSoon();
    setPosition(positionRef.current);
  }, [releasePanInteractionRasterSoon]);

  const runEdgePanFrame = useCallback((timestampMs: number) => {
    const stage = stageRef.current;
    const pointer = canvasPointerRef.current;
    const { viewport, imageSize, suppressed, gestureInFlight } = panContextRef.current;
    if (!stage || !pointer || suppressed) {
      stopEdgePan();
      return;
    }

    const velocity = computeEdgePanVelocity(pointer, viewport);
    if (velocity.vx === 0 && velocity.vy === 0) {
      stopEdgePan();
      return;
    }

    // A cursor on its way to one of the canvas's own overlay controls crosses
    // the band. Only a cursor that stays there means to pan.
    if (edgePanDwellStartMsRef.current === 0) edgePanDwellStartMsRef.current = timestampMs;
    if (timestampMs - edgePanDwellStartMsRef.current < EDGE_PAN_DWELL_MS) {
      edgePanFrameRef.current = window.requestAnimationFrame(
        (nextTimestampMs) => runEdgePanFrameRef.current(nextTimestampMs)
      );
      return;
    }

    if (!edgePanMovingRef.current) {
      edgePanMovingRef.current = true;
      activatePanInteractionRaster();
      setHoveredClickTargetId(null);
      trackCanvasEvent('edge_pan');
    }

    const isFirstFrame = edgePanLastFrameMsRef.current === 0;
    const elapsedMs = isFirstFrame ? 0 : timestampMs - edgePanLastFrameMsRef.current;
    edgePanLastFrameMsRef.current = timestampMs;
    if (isFirstFrame) edgePanLastCommitMsRef.current = timestampMs;

    const currentScale = scaleRef.current;
    const nextPosition = clampPanPosition(
      stepPanPosition(positionRef.current, velocity, elapsedMs),
      currentScale,
      viewport,
      imageSize
    );
    // The clamp holds the drawing at its bound while the cursor stays in the
    // band, so a stalled position is the second way this loop ends. Velocity
    // alone never reaches zero there.
    if (
      !isFirstFrame
      && nextPosition.x === positionRef.current.x
      && nextPosition.y === positionRef.current.y
    ) {
      stopEdgePan();
      return;
    }

    positionRef.current = nextPosition;
    stage.position(nextPosition);
    stage.batchDraw();

    // The cursor holds still while the drawing moves beneath it, so the image
    // coordinate under it changed. A rectangle or path preview in flight
    // has to keep tracking that coordinate. Nothing else does: `applyPointerToGestures`
    // schedules a `setCursorPosition` on every call, and a re-render per frame
    // is the cost this loop exists to avoid.
    if (gestureInFlight) {
      applyPointerToGesturesRef.current(
        {
          x: (pointer.x - nextPosition.x) / currentScale,
          y: (pointer.y - nextPosition.y) / currentScale,
        },
        currentScale
      );
    }

    // Overlay culling reads `position` from React state. A long edge pan that
    // never commits would scroll into overlays that were culled away and never
    // re-rendered, so the SME would arrive at an empty region of the drawing.
    if (timestampMs - edgePanLastCommitMsRef.current >= EDGE_PAN_COMMIT_INTERVAL_MS) {
      edgePanLastCommitMsRef.current = timestampMs;
      setPosition(nextPosition);
    }

    edgePanFrameRef.current = window.requestAnimationFrame(
      (nextTimestampMs) => runEdgePanFrameRef.current(nextTimestampMs)
    );
  }, [activatePanInteractionRaster, stopEdgePan, trackCanvasEvent]);

  useEffect(() => {
    runEdgePanFrameRef.current = runEdgePanFrame;
  }, [runEdgePanFrame]);

  /** Start, continue, or stop the loop for the cursor's latest position. */
  const syncEdgePan = useCallback((pointer: PanPointer) => {
    canvasPointerRef.current = pointer;
    const { viewport, suppressed } = panContextRef.current;
    const velocity = computeEdgePanVelocity(pointer, viewport);
    if (suppressed || (velocity.vx === 0 && velocity.vy === 0)) {
      stopEdgePan();
      return;
    }
    if (edgePanFrameRef.current !== null) return;

    edgePanLastFrameMsRef.current = 0;
    edgePanLastCommitMsRef.current = 0;
    edgePanDwellStartMsRef.current = 0;
    edgePanFrameRef.current = window.requestAnimationFrame(
      (timestampMs) => runEdgePanFrameRef.current(timestampMs)
    );
  }, [stopEdgePan]);

  // A commit re-renders, and Konva then re-applies the `x`/`y` props from that
  // older position, which reads as a backwards jerk mid-pan. Re-assert the
  // loop's own position after the render instead.
  useLayoutEffect(() => {
    if (!isImperativePanActive()) return;
    const stage = stageRef.current;
    if (!stage) return;
    stage.position(positionRef.current);
    stage.batchDraw();
  });

  useEffect(() => () => {
    if (edgePanFrameRef.current !== null) {
      window.cancelAnimationFrame(edgePanFrameRef.current);
      edgePanFrameRef.current = null;
    }
  }, []);

  const handleMouseMove = useCallback((_e: any) => {
    const stage = stageRef.current;
    if (!stage) return;

    const pointer = stage.getPointerPosition();
    if (!pointer) return;
    const currentScale = scaleRef.current;
    const currentPosition = positionRef.current;
    const adjustedPointer = {
      x: (pointer.x - currentPosition.x) / currentScale,
      y: (pointer.y - currentPosition.y) / currentScale,
    };

    // Handle panning
    if (isPanning) {
      const newPosition = clampPanPosition(
        { x: pointer.x - panStart.x, y: pointer.y - panStart.y },
        scaleRef.current,
        panContextRef.current.viewport,
        panContextRef.current.imageSize
      );
      positionRef.current = newPosition;
      stage.position(newPosition);
      stage.batchDraw();
      if (panPointerStartRef.current) {
        const dx = pointer.x - panPointerStartRef.current.x;
        const dy = pointer.y - panPointerStartRef.current.y;
        if (Math.hypot(dx, dy) > 4) {
          panMovedRef.current = true;
        }
      }
      return;
    }

    syncEdgePan(pointer);
    applyPointerToGestures(adjustedPointer, currentScale);
    // An edge pan moves the sheet under a still pointer, so no target holds the
    // hover until the pan stops.
    setHoveredClickTargetId(
      edgePanMovingRef.current ? null : clickTargetAt(clickTargets, adjustedPointer)?.id ?? null
    );
  }, [applyPointerToGestures, clickTargets, isPanning, panStart, syncEdgePan]);

  /**
   * Adds the vertex a click placed, at `pointer` in container px. A click inside the
   * double-click window and within a few screen pixels of the last one is the second
   * click of a double-click: it finishes the line and adds no point. The clicks are read
   * here because Konva's dblclick fires for two clicks anywhere on the paper, and not for
   * a second click on the point the first one drew.
   */
  const addPathVertex = useCallback((pointer: { x: number; y: number }, timeStamp: number) => {
    const lastClick = lastPathClickRef.current;
    const repeated = pathPoints.length > 0 && lastClick !== null
      && timeStamp - lastClick.time <= konvaDoubleClickWindowMs()
      && Math.hypot(pointer.x - lastClick.x, pointer.y - lastClick.y) <= DOUBLE_CLICK_SLOP_PX;
    lastPathClickRef.current = repeated ? null : { x: pointer.x, y: pointer.y, time: timeStamp };
    if (repeated) {
      if (pathPoints.length >= 4) finishPathDrawingRef.current();
      return;
    }
    const currentScale = scaleRef.current;
    const currentPosition = positionRef.current;
    const adjusted = {
      x: (pointer.x - currentPosition.x) / currentScale,
      y: (pointer.y - currentPosition.y) / currentScale,
    };
    if (pathPoints.length === 0 && onPathStart) {
      const container = stageRef.current?.container();
      if (container) {
        const rect = container.getBoundingClientRect();
        onPathStart({ x: rect.left + pointer.x, y: rect.top + pointer.y }, { x: adjusted.x, y: adjusted.y });
      }
    }
    // A click on or near a marked geometry binds the vertex to it and lands it on the edge (D3).
    const hit = bindTargets ? entityAtPoint(adjusted, currentScale, bindTargets) : null;
    const vertex = hit
      ? { x: hit.snap.x, y: hit.snap.y, binding: { kind: hit.kind, id: hit.id } }
      : { x: adjusted.x, y: adjusted.y, binding: null };
    setLineDraft((prev) => addVertex(prev, vertex));
  }, [bindTargets, onPathStart, pathPoints.length]);

  const handleMouseUp = useCallback((e: any) => {
    const press = pressRef.current;
    pressRef.current = null;
    const pointer = stageRef.current?.getPointerPosition() ?? null;
    const clickPoint = press && pointer && Math.hypot(pointer.x - press.x, pointer.y - press.y) < CLICK_SLOP_PX
      ? pointer
      : null;
    pressDraggedRef.current = Boolean(press) && !clickPoint;

    // A copy press ends here, before the line and box branches below, which would read
    // a short press with nothing armed as a line's first vertex.
    const copyPress = copyPressRef.current;
    copyPressRef.current = null;
    pressCopiedRef.current = copyPress !== null;
    if (copyPress) {
      if (pointer && Math.hypot(pointer.x - copyPress.x, pointer.y - copyPress.y) < CLICK_SLOP_PX) {
        onCopyClickTarget?.(copyPress.clickTarget);
      }
      return;
    }

    // Handle panning end
    if (isPanning) {
      performance.mark('pan-end');
      const markPanEndIdle = () => {
        performance.mark('pan-end+idle');
        performance.measure('pan-end-to-idle', 'pan-end', 'pan-end+idle');
      };
      const idleCallback = window.requestIdleCallback;
      if (typeof idleCallback === 'function') {
        idleCallback(markPanEndIdle, { timeout: 1000 });
      } else {
        window.setTimeout(markPanEndIdle, 0);
      }
      setIsPanning(false);
      releasePanInteractionRasterSoon();
      setPosition(positionRef.current);
      if (panMovedRef.current) {
        const container = stageRef.current?.container();
        container?.addEventListener('click', (ev: MouseEvent) => {
          ev.stopPropagation();
          ev.preventDefault();
        }, { capture: true, once: true });
        trackCanvasEvent('pan');
        setTimeout(() => {
          panMovedRef.current = false;
          panPointerStartRef.current = null;
        }, 0);
      } else {
        panPointerStartRef.current = null;
      }
      return;
    }

    // Handle drawing end
    if (isDrawing && drawingBox) {
      setIsDrawing(false);

      // Convert to bounding box format
      const bbox: BoundingBox = {
        x1: Math.min(drawingBox.x, drawingBox.x + drawingBox.width),
        y1: Math.min(drawingBox.y, drawingBox.y + drawingBox.height),
        x2: Math.max(drawingBox.x, drawingBox.x + drawingBox.width),
        y2: Math.max(drawingBox.y, drawingBox.y + drawingBox.height),
      };

      if (collectingRegions && onClickTarget && clickPoint) {
        // The pointer barely moved on screen, so it is a click: on a target, the
        // target's own box becomes the region. A press with no target under it adds nothing.
        const clickTarget = clickTargetAt(clickTargets, { x: drawingBox.x, y: drawingBox.y });
        if (clickTarget) onClickTarget(clickTarget);
      } else if (!clickPoint && Math.abs(bbox.x2 - bbox.x1) > 5 && Math.abs(bbox.y2 - bbox.y1) > 5) {
        // Only call onBoxDrawn for a drag that gave the box some size. A box drawn to ask its
        // kind waits on the pill instead, and a second drag replaces it.
        if (askKindOnDraw) {
          setPendingBox(bbox);
        } else {
          const anchor = computeAnchor(bbox);
          onBoxDrawn(bbox, anchor);
        }
        trackCanvasEvent('draw_complete');
      }

      setDrawingBox(null);
    }

    // With nothing armed, a click on empty paper, or a shift-click on a mark, is a line's
    // first vertex, unless a box waits on the kind pill. The vertex binds to the mark under
    // it. In path mode a click adds a vertex and a drag adds none.
    const startsLineAtRest = drawMode === 'rect' && askKindOnDraw && !pendingBox && Boolean(press);
    const startsLineFromMark = startsLineAtRest && !press?.onEmptyPaper && Boolean(e.evt.shiftKey);
    const startsLine = startsLineAtRest && (Boolean(press?.onEmptyPaper) || startsLineFromMark);
    pressStartedLineFromMarkRef.current = Boolean(clickPoint) && !pendingMarkBbox && startsLineFromMark;
    if (clickPoint && !pendingMarkBbox && (startsLine || drawMode === 'path')) {
      addPathVertex(clickPoint, e.evt.timeStamp);
    }

    // Polygon drawing end
    if (drawMode === 'poly' && isPolyDrawing) {
      setIsPolyDrawing(false);
      
      if (polyPoints.length >= 6) { // at least 3 points
        // Simplify and close the polygon
        const simplified = simplifyPolygon(polyPoints);
        
        // Compute bounding box
        const xs: number[] = [];
        const ys: number[] = [];
        for (let i = 0; i < simplified.length; i += 2) {
          xs.push(simplified[i]);
          ys.push(simplified[i + 1]);
        }
        
        const bbox: BoundingBox = {
          x1: Math.min(...xs),
          y1: Math.min(...ys),
          x2: Math.max(...xs),
          y2: Math.max(...ys),
          polygon: simplified, // include actual polygon points
        };
        
        const anchor = computeAnchor(bbox);
        onBoxDrawn(bbox, anchor);
        trackCanvasEvent('draw_complete');
      }

      setPolyPoints([]);
      return;
    }
  }, [addPathVertex, askKindOnDraw, clickTargets, collectingRegions, computeAnchor, drawMode, drawingBox, isDrawing, isPanning, isPolyDrawing, onBoxDrawn, onClickTarget, onCopyClickTarget, pendingBox, pendingMarkBbox, polyPoints, releasePanInteractionRasterSoon, simplifyPolygon]);

  const choosePendingBoxKind = useCallback((kind: PendingBoxKind) => {
    if (!pendingBox) return;
    setPendingBox(null);
    onPendingBoxKind?.(kind, pendingBox, computeAnchor(pendingBox));
  }, [computeAnchor, onPendingBoxKind, pendingBox]);

  // Handle mouse leave to stop panning/drawing if mouse leaves the canvas
  const handleMouseLeave = useCallback(() => {
    canvasPointerRef.current = null;
    pressRef.current = null;
    copyPressRef.current = null;
    stopEdgePan();
    if (isPanning) {
      setIsPanning(false);
      releasePanInteractionRasterSoon();
      setPosition(positionRef.current);
    }
    if (isDrawing) {
      setIsDrawing(false);
      setDrawingBox(null);
    }
    // Clear path preview and the target under the pointer, but keep path points
    setPathPreviewPoint(null);
    setBindHover(null);
    // Clear hover tooltip
    setHoveredItem(null);
    setHoveredClickTargetId(null);
    setCursorPosition(null);
  }, [isPanning, isDrawing, releasePanInteractionRasterSoon, stopEdgePan]);

  // Helper to finish path drawing and create bbox
  const finishPathDrawing = useCallback(() => {
    const bbox = toDrawBox(lineDraft);
    if (!bbox) { // Need at least 2 points
      setLineDraft(EMPTY_LINE_DRAFT);
      setPathPreviewPoint(null);
      return;
    }

    const anchor = computeAnchor(bbox);
    onLineDrawn(bbox, anchor, toLineEnds(lineDraft));
    trackCanvasEvent('draw_complete');

    // The finished line renders once, as the pending mark the workspace hands back;
    // the draft it came from is done.
    setLineDraft(EMPTY_LINE_DRAFT);
    setBindHover(null);
    setPathPreviewPoint(null);
  }, [lineDraft, computeAnchor, onLineDrawn]);
  finishPathDrawingRef.current = finishPathDrawing;

  // Undo last path point
  const undoPathPoint = useCallback(() => {
    setLineDraft((prev) => removeLast(prev));
  }, []);

  // Redo last undone point
  const redoPathPoint = useCallback(() => {
    setLineDraft((prev) => redoLast(prev));
  }, []);

  const resetZoom = useCallback(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  const fitToScreen = useCallback((viewport: { width: number; height: number } = { width, height }) => {
    if (!image) return;
    
    const logicalWidth = imageCanvasWidth || image.width;
    const logicalHeight = imageCanvasHeight || image.height;
    const scaleX = viewport.width / logicalWidth;
    const scaleY = viewport.height / logicalHeight;
    const newScale = Math.min(scaleX, scaleY);
    
    const newPosition = {
      x: (viewport.width - logicalWidth * newScale) / 2,
      y: (viewport.height - logicalHeight * newScale) / 2,
    };
    scaleRef.current = newScale;
    positionRef.current = newPosition;
    setScale(newScale);
    setPosition(newPosition);
  }, [image, imageCanvasHeight, imageCanvasWidth, width, height]);

  const keyPanCommitMsRef = useRef(0);

  /**
   * Move the drawing to a position a key press computed.
   *
   * A single press commits to React state at once. An auto-repeat writes the
   * stage directly and commits on the same interval the edge-pan loop uses,
   * because a held arrow otherwise re-renders every overlay about thirty times
   * a second — the cost this file's imperative pan paths exist to avoid.
   */
  const applyKeyboardPan = useCallback((nextPosition: { x: number; y: number }, isRepeat: boolean) => {
    positionRef.current = nextPosition;
    const stage = stageRef.current;
    if (!isRepeat || !stage) {
      keyPanActiveRef.current = false;
      setPosition(nextPosition);
      return;
    }

    keyPanActiveRef.current = true;
    stage.position(nextPosition);
    stage.batchDraw();
    const nowMs = performance.now();
    if (nowMs - keyPanCommitMsRef.current >= EDGE_PAN_COMMIT_INTERVAL_MS) {
      keyPanCommitMsRef.current = nowMs;
      setPosition(nextPosition);
    }
  }, []);

  /** Commit the position a released arrow key left the drawing at. */
  const endKeyboardPan = useCallback(() => {
    if (!keyPanActiveRef.current) return;
    keyPanActiveRef.current = false;
    setPosition(positionRef.current);
  }, []);

  /**
   * Zoom about the cursor, or about the centre of the canvas when the cursor is
   * elsewhere. The clamp runs afterwards because a zoom out shrinks the drawing
   * and can leave it outside the bound the next pan would enforce.
   */
  const zoomByFactor = useCallback((factor: number) => {
    const { viewport, imageSize } = panContextRef.current;
    const anchor = canvasPointerRef.current
      ?? { x: viewport.width / 2, y: viewport.height / 2 };
    const zoomed = zoomAtPoint(positionRef.current, scaleRef.current, anchor, factor);
    const nextPosition = clampPanPosition(zoomed.position, zoomed.scale, viewport, imageSize);
    positionRef.current = nextPosition;
    setScale(zoomed.scale);
    setPosition(nextPosition);
    trackCanvasEvent('key_zoom');
  }, [trackCanvasEvent]);

  // A request already standing when the canvas mounts was meant for an earlier one.
  const handledZoomRequestIdRef = useRef(zoomRequest?.requestId ?? null);
  useEffect(() => {
    if (!zoomRequest || zoomRequest.requestId === handledZoomRequestIdRef.current) return;
    handledZoomRequestIdRef.current = zoomRequest.requestId;
    if (zoomRequest.kind === 'in') zoomByFactor(ZOOM_STEP_FACTOR);
    else if (zoomRequest.kind === 'out') zoomByFactor(1 / ZOOM_STEP_FACTOR);
    else if (zoomRequest.kind === 'reset') resetZoom();
    else fitToScreen();
  }, [zoomRequest, zoomByFactor, resetZoom, fitToScreen]);

  // A press moves focus to the page unless its native mousedown is prevented, and the
  // stage's onMouseDown cannot be relied on to do it: a mark under the pointer that stops
  // the bubble keeps the press from the stage. A capture listener on Konva's content div
  // runs before every shape handler, so it claims a press on a target to copy whatever
  // lies on top, and the dock field keeps its focus and caret. It claims nothing else,
  // so a press off every target still blurs the field as before.
  const stageMounted = width > 0 && height > 0;
  useEffect(() => {
    const stage = stageRef.current;
    if (!stageMounted || !stage || !onCopyClickTarget) return;
    const content: HTMLDivElement = stage.content;
    const claimCopyPress = (event: MouseEvent) => {
      copyPressRef.current = null;
      if (event.button !== 0 || isSpaceHeld) return;
      stage.setPointersPositions(event);
      const pointer = stage.getPointerPosition();
      if (!pointer) return;
      if (targetOrAncestorHasKonvaName(stage.getIntersection(pointer), CONTROLS_A_COPY_PRESS_LEAVES)) return;
      const clickTarget = clickTargetAt(clickTargets, {
        x: (pointer.x - positionRef.current.x) / scaleRef.current,
        y: (pointer.y - positionRef.current.y) / scaleRef.current,
      });
      if (!clickTarget) return;
      event.preventDefault();
      copyPressRef.current = { x: pointer.x, y: pointer.y, clickTarget };
    };
    content.addEventListener('mousedown', claimCopyPress, { capture: true });
    return () => {
      content.removeEventListener('mousedown', claimCopyPress, { capture: true });
      copyPressRef.current = null;
    };
  }, [stageMounted, onCopyClickTarget, clickTargets, isSpaceHeld]);

  // Keyboard handler: Space for pan, Enter to finish path, Escape to cancel
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isEventInsideEditorDock(e)) return;
      // Don't capture keys if user is typing in an input/textarea
      const activeEl = document.activeElement;
      const isTyping = activeEl && (
        activeEl.tagName === 'INPUT' || 
        activeEl.tagName === 'TEXTAREA' ||
        (activeEl as HTMLElement).isContentEditable
      );

      // The navigation keys belong to the drawing while no field has focus. A pending
      // mark blocks canvas clicks, not the keys: the SME pans to check the ends while
      // the form is open.
      // Cmd, Ctrl, and Alt carry shipped browser gestures on these keys, from
      // history navigation to page zoom, and a page cannot cancel all of them.
      const isBrowserGesture = e.metaKey || e.ctrlKey || e.altKey;
      const canNavigate = !isTyping && !isBrowserGesture;

      if (canNavigate) {
        const panDelta = arrowPanDelta(e.key, e.shiftKey);
        if (panDelta) {
          e.preventDefault();
          const { viewport, imageSize } = panContextRef.current;
          const nextPosition = clampPanPosition(
            {
              x: positionRef.current.x + panDelta.dx,
              y: positionRef.current.y + panDelta.dy,
            },
            scaleRef.current,
            viewport,
            imageSize
          );
          applyKeyboardPan(nextPosition, e.repeat);
          setHoveredClickTargetId(null);
          trackCanvasEvent('arrow_pan');
          return;
        }
      }

      if (canNavigate) {
        // Zoom keys anchor on the cursor, so an SME reading one corner of a
        // drawing zooms into that corner.
        const zoomFactor = zoomKeyFactor(e.key, isBrowserGesture);
        if (zoomFactor !== null) {
          e.preventDefault();
          zoomByFactor(zoomFactor);
          return;
        }

        if (e.key === '0') {
          e.preventDefault();
          resetZoom();
          trackCanvasEvent('key_zoom');
          return;
        }

        // Shift+1 produces '!' on a US layout, and both spellings mean fit.
        if (e.key === '1' || e.key === '!') {
          e.preventDefault();
          fitToScreen();
          trackCanvasEvent('key_zoom');
          return;
        }
      }

      if (e.key === 'Shift') setIsShiftHeld(true);

      // Space key for temporary pan mode (Figma-style) - works in all modes
      if (e.code === 'Space' && !e.repeat && !isTyping) {
        e.preventDefault();
        setIsSpaceHeld(true);
      }

      // X held isolates the active asset until it is released.
      if (canNavigate && e.key.toLowerCase() === 'x') {
        e.preventDefault();
        if (!e.repeat) setIsIsolateKeyHeld(true);
        return;
      }
      
      // The kind pill owns E, I and Escape while a drawn box waits on it. Enter names no
      // kind, so it does nothing here.
      if (pendingBox && canNavigate) {
        const key = e.key.toLowerCase();
        if (key === 'e' || key === 'i') {
          e.preventDefault();
          choosePendingBoxKind(key === 'e' ? 'equipment' : 'instrument');
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          setPendingBox(null);
          return;
        }
      }

      // At rest a draw tool's key presses its toolbar button, and the armed tool's own key
      // lets go of it, as a second click does. Any other key leaves an armed tool or a
      // draft alone, and a key typed in a dialog stays the dialog's.
      if (
        onPressDrawTool && canNavigate && !e.repeat && !pendingBox && pathPoints.length === 0 && !pendingMarkBbox
        && !(activeEl instanceof HTMLElement && activeEl.closest('[role="dialog"]'))
      ) {
        const pressedTool = offeredDrawTools?.find((tool) => DRAW_TOOL_KEY[tool].toLowerCase() === e.key.toLowerCase());
        if (pressedTool && (armedTool === pressedTool || (armedTool === null && !collectingRegions))) {
          e.preventDefault();
          onPressDrawTool(pressedTool);
          return;
        }
      }

      // The collect mode owns Enter and Escape while it is armed: Enter finishes the
      // draft, Escape peels regions off it one at a time and only leaves the mode once
      // nothing is left to peel. Both return, so Escape never also resets the draw mode
      // out from under a draft the SME is still building.
      if (collectingRegions && canNavigate) {
        if (e.key === 'Enter' && draftRegions.length > 0) {
          e.preventDefault();
          onFinishRegions?.();
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          if (draftRegions.length > 0) {
            onRemoveLastRegion?.();
          } else {
            onExitCollect?.();
          }
          return;
        }
      }

      // Path drawing shortcuts
      if (drawMode === 'path' && !isTyping) {
        if ((e.key === 'Enter' || e.key === 'Tab') && pathPoints.length >= 4) {
          e.preventDefault();
          finishPathDrawing();
        } else if (e.key === 'Escape' && !pendingMarkBbox) {
          // A finished line's form owns Escape while it is open: it closes, after asking when
          // it holds an edit, so a key pressed outside the form never drops what the SME typed.
          // DECISION(2026-10-22, abhishek): Escape removes the last vertex, and at zero
          // vertices it exits line drawing (D4).
          // One rule for Escape across line and instrument marking, and Cmd+Z already
          // removes one vertex. SMEs who ask for a one-key clear are the signal to add one.
          if (pathPoints.length === 0) {
            // Leaving line drawing takes the redo history with it. The key is left unclaimed,
            // so the workspace's Escape also closes an open editor dock, whichever window
            // listener runs first.
            setLineDraft(EMPTY_LINE_DRAFT);
            onExitLineDrawing?.();
          } else if (pathPoints.length <= 2 && !lineIntent) {
            // A line that a click on the paper started ends with its last vertex, unclaimed too.
            setLineDraft(EMPTY_LINE_DRAFT);
            setPathPreviewPoint(null);
            onExitLineDrawing?.();
          } else {
            e.preventDefault();
            setLineDraft((prev) => removeLast(prev));
            if (pathPoints.length <= 2) setPathPreviewPoint(null);
          }
        } else if (e.key === 'z' && (e.ctrlKey || e.metaKey) && !e.shiftKey) {
          // Ctrl+Z / Cmd+Z = Undo
          e.preventDefault();
          undoPathPoint();
        } else if (
          (e.key === 'z' && (e.ctrlKey || e.metaKey) && e.shiftKey) ||
          (e.key === 'y' && (e.ctrlKey || e.metaKey))
        ) {
          // Ctrl+Shift+Z / Cmd+Shift+Z / Ctrl+Y = Redo
          e.preventDefault();
          redoPathPoint();
        }
      }

      // At rest, Escape clears the saved-mark selection.
      if (e.key === 'Escape' && canNavigate && drawMode !== 'path' && selectedWizardItemKeys?.size) {
        onClearWizardItemSelection?.();
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        setIsSpaceHeld(false);
      }
      if (e.key === 'Shift') setIsShiftHeld(false);
      if (e.key.toLowerCase() === 'x') {
        setIsIsolateKeyHeld(false);
      }
      if (arrowPanDelta(e.key, false)) {
        endKeyboardPan();
      }
    };

    // A window that loses focus with X or Shift down never sees its release.
    const releaseIsolateKey = () => {
      setIsIsolateKeyHeld(false);
      setIsShiftHeld(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', releaseIsolateKey);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', releaseIsolateKey);
    };
  }, [armedTool, offeredDrawTools, onPressDrawTool, drawMode, lineIntent, pathPoints.length, pendingMarkBbox, finishPathDrawing, undoPathPoint, redoPathPoint, onExitLineDrawing, selectedWizardItemKeys?.size, onClearWizardItemSelection, trackCanvasEvent, resetZoom, fitToScreen, applyKeyboardPan, zoomByFactor, endKeyboardPan, collectingRegions, draftRegions.length, onFinishRegions, onRemoveLastRegion, onExitCollect, pendingBox, choosePendingBoxKind]);

  // Auto-fit only when image first loads (not on container resize)
  const [hasInitialFit, setHasInitialFit] = useState(false);
  useEffect(() => {
    if (!image || hasInitialFit) return;
    if (width <= 0 || height <= 0) return;
    fitToScreen();
    setHasInitialFit(true);
  }, [image, hasInitialFit, fitToScreen, width, height]);

  // Reset initial fit when image changes
  useEffect(() => {
    setHasInitialFit(false);
  }, [imageUrl]);

  useEffect(() => {
    if (!selectedCandidateId) return;
    const stillExists = nodePointCandidates.some((candidate) => candidate.id === selectedCandidateId);
    if (!stillExists) {
      setSelectedCandidateId(null);
    }
  }, [nodePointCandidates, selectedCandidateId]);

  // Each focus request moves the view once. Width and height are inputs, not
  // triggers: a canvas that resizes under a standing request keeps its view.
  const appliedFocusRequestIdRef = useRef<CanvasFocusRequest['requestId'] | null>(null);
  useLayoutEffect(() => {
    if (!focusRequest) {
      appliedFocusRequestIdRef.current = null;
      return;
    }
    // A deep link sets the focus before the sheet image has loaded, and the
    // initial fit-to-screen then runs and overwrites it. Wait for that fit, so
    // the focus is the last word on the first paint as well as on later ones.
    if (!hasInitialFit) return;
    if (appliedFocusRequestIdRef.current === focusRequest.requestId) return;
    appliedFocusRequestIdRef.current = focusRequest.requestId;

    // Measured for the reason a fit box request is: an editor that opens with the
    // focus narrows the canvas before the ResizeObserver reports it.
    const measured = containerRef.current?.getBoundingClientRect();
    const measuredIsUsable = Boolean(measured && measured.width > 0 && measured.height > 0);
    const nextPosition = navigateToTarget(
      scaleRef.current, focusRequest.bbox, measuredIsUsable ? measured! : { width, height }, panImageSize,
    );
    positionRef.current = nextPosition;
    setPosition(nextPosition);
    if (measuredIsUsable) observedContainerRectRef.current = measured!;
  }, [focusRequest, hasInitialFit, width, height, panImageSize]);

  const previousFocusRequestRef = useRef<CanvasFocusRequest | null | undefined>(focusRequest);
  useEffect(() => {
    if (fitOnFocusClear && previousFocusRequestRef.current && !focusRequest) {
      fitToScreen();
    }
    previousFocusRequestRef.current = focusRequest;
  }, [fitOnFocusClear, fitToScreen, focusRequest]);

  // A fit request usually rides on the layout change that resizes the canvas, such
  // as a side panel closing, and the ResizeObserver reports the new size only after
  // the browser lays it out. Measuring here fits to the size the canvas now has.
  const handledFitRequestIdRef = useRef(fitRequestId);
  useLayoutEffect(() => {
    if (fitRequestId === handledFitRequestIdRef.current) return;
    handledFitRequestIdRef.current = fitRequestId;
    const measured = containerRef.current?.getBoundingClientRect();
    if (!measured || !(measured.width > 0) || !(measured.height > 0)) {
      fitToScreen();
      return;
    }
    fitToScreen(measured);
    // The fit places the drawing for the layout it measured, so the resize callback
    // that follows has no edge move to undo and starts from the fitted view.
    observedContainerRectRef.current = measured;
  }, [fitRequestId, fitToScreen]);

  // A fit box request waits for the initial fit, as a focus request does. It
  // measures the container for the same reason a fit request does: a panel that
  // opens with it narrows the canvas before the ResizeObserver reports it.
  const appliedFitBoxRequestIdRef = useRef<CanvasFocusRequest['requestId'] | null>(null);
  useLayoutEffect(() => {
    if (!fitBoxRequest || !hasInitialFit) return;
    if (appliedFitBoxRequestIdRef.current === fitBoxRequest.requestId) return;
    appliedFitBoxRequestIdRef.current = fitBoxRequest.requestId;
    const measured = containerRef.current?.getBoundingClientRect();
    const measuredIsUsable = Boolean(measured && measured.width > 0 && measured.height > 0);
    const next = fitBoxInViewport(fitBoxRequest.bbox, measuredIsUsable ? measured! : { width, height });
    scaleRef.current = next.scale;
    positionRef.current = next.position;
    setScale(next.scale);
    setPosition(next.position);
    if (measuredIsUsable) observedContainerRectRef.current = measured!;
  }, [fitBoxRequest, hasInitialFit, width, height]);

  // Flash/highlight focused area briefly on navigation (search / asset rail selection).
  const FOCUS_FLASH_DURATION_MS = 4000;
  const [focusFlashElapsedMs, setFocusFlashElapsedMs] = useState<number>(FOCUS_FLASH_DURATION_MS);
  const focusFlashRafRef = useRef<number | null>(null);
  const focusFlashKey = focusRequest ? String(focusRequest.requestId) : '';
  useEffect(() => {
    if (!focusRequest) {
      setFocusFlashElapsedMs(FOCUS_FLASH_DURATION_MS);
      return;
    }

    if (focusFlashRafRef.current) cancelAnimationFrame(focusFlashRafRef.current);
    setFocusFlashElapsedMs(0);

    const start = performance.now();
    const tick = (now: number) => {
      const elapsed = now - start;
      if (elapsed >= FOCUS_FLASH_DURATION_MS) {
        setFocusFlashElapsedMs(FOCUS_FLASH_DURATION_MS);
        focusFlashRafRef.current = null;
        return;
      }
      setFocusFlashElapsedMs(elapsed);
      focusFlashRafRef.current = requestAnimationFrame(tick);
    };

    focusFlashRafRef.current = requestAnimationFrame(tick);
    return () => {
      if (focusFlashRafRef.current) cancelAnimationFrame(focusFlashRafRef.current);
      focusFlashRafRef.current = null;
    };
  }, [focusFlashKey]);

  // The workspace owns the arm, and the canvas follows both of its edges. Arming takes
  // the box mode and drops any half-drawn shape, so the next drag marks a region rather
  // than a line's vertex. Disarming returns to rest, except after Move All, which keeps
  // its own mode.
  const collectingRegionsSeenRef = useRef(false);
  useEffect(() => {
    if (collectingRegions === collectingRegionsSeenRef.current) return;
    collectingRegionsSeenRef.current = collectingRegions;
    if (!collectingRegions) {
      setDrawMode((mode) => (mode === 'select' || mode === 'shift-annotations' ? mode : defaultDrawMode));
      return;
    }
    setDrawMode('rect');
    setLineDraft(EMPTY_LINE_DRAFT);
    setPathPreviewPoint(null);
    setPolyPoints([]);
    setIsPolyDrawing(false);
  }, [collectingRegions]);

  // Get visual style (color + dash pattern) for marking status
  const getStatusStyle = (status: string, id?: string): MarkingStatusStyle => {
    // Selected item gets blue highlight
    if (id && selectedSectionId === id) {
      return { color: '#2563eb', dash: [4, 4], fill: 'rgba(37, 99, 235, 0.15)', label: 'Selected' };
    }

    // Handle new 3-state marking statuses
    if (status in MARKING_STATUS_STYLES) {
      return MARKING_STATUS_STYLES[status as MarkingStatus];
    }

    // Legacy/backend section statuses (for backward compatibility)
    switch (status) {
      case 'done':
        return { color: '#22c55e', dash: null, fill: 'rgba(34, 197, 94, 0.1)', label: 'Done' };
      case 'processing':
        return { color: '#f59e0b', dash: [8, 4], fill: 'rgba(245, 158, 11, 0.1)', label: 'Processing' };
      case 'error':
        return { color: '#ef4444', dash: [4, 4], fill: 'rgba(239, 68, 68, 0.1)', label: 'Error' };
      // Map old statuses to new ones for backward compatibility
      case 'unmarked':
        return MARKING_STATUS_STYLES.ai_identified;
      case 'marked':
        return MARKING_STATUS_STYLES.in_progress;
      default:
        // For pending pieces - if context empty use orange else grey
        if (!status.trim()) {
          return { color: '#ff6b35', dash: [4, 4], fill: 'rgba(255, 107, 53, 0.1)', label: 'Pending' };
        }
        return { color: 'var(--raven-text-secondary)', dash: [4, 4], fill: 'rgba(107, 114, 128, 0.1)', label: 'Unknown' };
    }
  };

  const getCursor = () => {
    if (isPanning) return 'grabbing';
    if (isDrawing) return 'crosshair';
    // Space held = grab cursor for panning (works in all modes)
    if (isSpaceHeld) return 'grab';
    if (isPanning) return 'grabbing';
    if (isPolyDrawing) return 'crosshair';
    if (drawMode === 'shift-annotations') return isAnnotationShiftSaving ? 'wait' : 'move';
    // Select mode - default pointer for clicking/selecting
    if (drawMode === 'select') return 'default';
    if (drawMode === 'path') return 'crosshair';
    if (drawMode === 'rect') return 'crosshair';
    if (drawMode === 'poly') return 'crosshair';
    return 'grab';
  };

  const canSelectMark = () => {
    if (!onSelectWizardItem) return false;
    if (panMovedRef.current || isSpaceHeld) return false;
    // A box drag that ends where it started on a mark is a drag, not a click.
    if (pressDraggedRef.current) return false;
    if (pressStartedLineFromMarkRef.current) return false;
    if (pressCopiedRef.current) return false;
    return marksTakeClicks;
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width: widthProp ? widthProp : '100%', height: heightProp ? heightProp : '100%' }}>
      {/* Draw mode toggle */}
      {annotating && (
        <div style={{
          position: 'absolute',
          top: '10px',
          left: '10px',
          zIndex: 11,
          padding: '6px',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 6,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}>
          <span style={{
            fontSize: 12,
            fontWeight: 700,
            color: 'var(--raven-text-secondary)',
            letterSpacing: '0.08em',
            marginRight: 4,
          }}>
            MODE
          </span>
          {!readOnly && onShiftWizardAnnotations && (
            <button
              onClick={() => {
                // A second press leaves Move All for rest.
                if (drawMode === 'shift-annotations') {
                  setDrawMode(defaultDrawMode);
                  return;
                }
                setDrawMode('shift-annotations');
                onExitCollect?.();
                setPolyPoints([]);
                setIsPolyDrawing(false);
                setLineDraft(EMPTY_LINE_DRAFT);
                setPathPreviewPoint(null);
                setAnnotationShiftPreview({ dx: 0, dy: 0 });
              }}
              disabled={!annotationBounds || isAnnotationShiftSaving}
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                backgroundColor: drawMode === 'shift-annotations' ? '#111827' : 'white',
                color: drawMode === 'shift-annotations' ? 'white' : '#111827',
                border: '1px solid #d1d5db',
                borderRadius: 4,
                cursor: annotationBounds && !isAnnotationShiftSaving ? 'pointer' : 'not-allowed',
                opacity: annotationBounds ? 1 : 0.55,
              }}
              title={
                annotationBounds
                  ? 'Move all saved annotations together'
                  : 'No saved annotations to move'
              }
            >
              Move All
            </button>
          )}
          {!readOnly && onSelectAllWizardItems && (
            <button
              onClick={onSelectAllWizardItems}
              disabled={!annotationBounds}
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                backgroundColor: selectedWizardItemKeys?.size ? '#eff6ff' : 'white',
                color: selectedWizardItemKeys?.size ? '#1d4ed8' : '#111827',
                border: selectedWizardItemKeys?.size ? '1px solid #93c5fd' : '1px solid #d1d5db',
                borderRadius: 4,
                cursor: annotationBounds ? 'pointer' : 'not-allowed',
                opacity: annotationBounds ? 1 : 0.55,
              }}
              title={annotationBounds ? 'Select all saved annotations' : 'No saved annotations to select'}
            >
              Select All
            </button>
          )}
          {!readOnly && selectedWizardItemKeys?.size ? (
            <>
              <span
                style={{
                  padding: '4px 6px',
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#1f2937',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #e5e7eb',
                  borderRadius: 4,
                }}
              >
                {selectedWizardItemKeys.size} selected
              </span>
              {onDeleteSelectedWizardItems && (
                <button
                  onClick={onDeleteSelectedWizardItems}
                  style={{
                    padding: '4px 8px',
                    fontSize: '12px',
                    backgroundColor: '#fef2f2',
                    color: '#991b1b',
                    border: '1px solid #fecaca',
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                  title="Delete selected saved annotations"
                >
                  Delete selected
                </button>
              )}
              {onMarkSelectedAsRedundancySet && (
                <button
                  onClick={onMarkSelectedAsRedundancySet}
                  style={{
                    padding: '4px 8px',
                    fontSize: '12px',
                    backgroundColor: 'white',
                    color: 'var(--raven-text-primary)',
                    border: '1px solid var(--raven-divider)',
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                  title="Put the selected tagged equipment in one redundancy set. Saves immediately."
                >
                  Mark as redundancy set
                </button>
              )}
              {onClearWizardItemSelection && (
                <button
                  onClick={onClearWizardItemSelection}
                  style={{
                    padding: '4px 8px',
                    fontSize: '12px',
                    backgroundColor: 'white',
                    color: '#4b5563',
                    border: '1px solid #d1d5db',
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                  title="Clear saved annotation selection"
                >
                  Clear
                </button>
              )}
            </>
          ) : null}
        </div>
      )}

      {!hidePerfHud && perfDebugEnabled && (
        <CanvasPerfHud
          scale={scale}
          perfStats={perfStats}
          nodePointMode={nodePointMode}
          setNodePointMode={setNodePointMode}
          cursorPosition={cursorPosition}
          onHide={() => setPerfDebugEnabled(false)}
        />
      )}

      <div style={{ 
        border: '1px solid #ddd', 
        borderRadius: '8px', 
        overflow: 'hidden',
        cursor: getCursor()
      }}>
        {/* Guard: Don't render Stage with 0 dimensions (causes Konva crash during layout reflow) */}
        {width > 0 && height > 0 && <Stage
          ref={stageRef}
          width={width}
          height={height}
          scaleX={scale}
          scaleY={scale}
          x={position.x}
          y={position.y}
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onContextMenu={(e) => e.evt.preventDefault()}
        >
          <Layer>
            {image && (
              <KonvaImage
                image={usingInteractionImage && interactionImage ? interactionImage : image}
                width={imageCanvasWidth || image.width}
                height={imageCanvasHeight || image.height}
              />
            )}

            {inspectionOverlay?.dimOutsideFootprint && inspectionOverlay.footprint && imageCanvasWidth && imageCanvasHeight && (
              <>
                <Rect
                  x={0}
                  y={0}
                  width={imageCanvasWidth}
                  height={Math.max(0, inspectionOverlay.footprint.y1)}
                  fill="rgba(14, 23, 27, 0.58)"
                  listening={false}
                />
                <Rect
                  x={0}
                  y={inspectionOverlay.footprint.y2}
                  width={imageCanvasWidth}
                  height={Math.max(0, imageCanvasHeight - inspectionOverlay.footprint.y2)}
                  fill="rgba(14, 23, 27, 0.58)"
                  listening={false}
                />
                <Rect
                  x={0}
                  y={inspectionOverlay.footprint.y1}
                  width={Math.max(0, inspectionOverlay.footprint.x1)}
                  height={Math.max(0, inspectionOverlay.footprint.y2 - inspectionOverlay.footprint.y1)}
                  fill="rgba(14, 23, 27, 0.58)"
                  listening={false}
                />
                <Rect
                  x={inspectionOverlay.footprint.x2}
                  y={inspectionOverlay.footprint.y1}
                  width={Math.max(0, imageCanvasWidth - inspectionOverlay.footprint.x2)}
                  height={Math.max(0, inspectionOverlay.footprint.y2 - inspectionOverlay.footprint.y1)}
                  fill="rgba(14, 23, 27, 0.58)"
                  listening={false}
                />
              </>
            )}

            {inspectionOverlay?.footprint && (
              <Rect
                x={inspectionOverlay.footprint.x1}
                y={inspectionOverlay.footprint.y1}
                width={inspectionOverlay.footprint.x2 - inspectionOverlay.footprint.x1}
                height={inspectionOverlay.footprint.y2 - inspectionOverlay.footprint.y1}
                stroke="#FF9800"
                strokeWidth={4 / scale}
                listening={false}
              />
            )}

            {(inspectionOverlay?.tiles ?? []).map((item, index) => (
              <Rect
                key={`inspection-tile-${index}`}
                x={item.bbox.x1}
                y={item.bbox.y1}
                width={item.bbox.x2 - item.bbox.x1}
                height={item.bbox.y2 - item.bbox.y1}
                stroke="#6A1B9A"
                strokeWidth={2 / scale}
                dash={[10 / scale, 6 / scale]}
                listening={false}
              />
            ))}

            {(inspectionOverlay?.textBoxes ?? []).map((item, index) => (
              <Rect
                key={`inspection-text-${index}`}
                x={item.bbox.x1}
                y={item.bbox.y1}
                width={item.bbox.x2 - item.bbox.x1}
                height={item.bbox.y2 - item.bbox.y1}
                stroke="#0288D1"
                fill="rgba(2, 136, 209, 0.10)"
                strokeWidth={1.5 / scale}
                listening={false}
              />
            ))}

            {(inspectionOverlay?.equipmentBoxes ?? []).map((item, index) => (
              <Rect
                key={`inspection-equipment-${index}`}
                x={item.bbox.x1}
                y={item.bbox.y1}
                width={item.bbox.x2 - item.bbox.x1}
                height={item.bbox.y2 - item.bbox.y1}
                stroke="#1976D2"
                fill="rgba(25, 118, 210, 0.12)"
                strokeWidth={3 / scale}
                listening={false}
              />
            ))}

            {(inspectionOverlay?.lineBoxes ?? []).map((item, index) => (
              <Rect
                key={`inspection-line-${index}`}
                x={item.bbox.x1}
                y={item.bbox.y1}
                width={item.bbox.x2 - item.bbox.x1}
                height={item.bbox.y2 - item.bbox.y1}
                stroke="#388E3C"
                fill="rgba(56, 142, 60, 0.12)"
                strokeWidth={3 / scale}
                listening={false}
              />
            ))}
            
            {/* Existing sections */}
            {sections
              .filter((section) => {
                const force = section.id === selectedSectionId;
                return isInViewport(section.bbox, force);
              })
              .map((section) => {
              // Check if section has polygon data representing component boxes
              const hasComponentBoxes = section.bbox.polygon && section.bbox.polygon.length >= 8 && section.bbox.polygon.length % 8 === 0;
              
              if (hasComponentBoxes && section.bbox.polygon) {
                // Render multiple component rectangles
                const numBoxes = section.bbox.polygon.length / 8;
                const components = [];
                
                for (let i = 0; i < numBoxes; i++) {
                  const startIdx = i * 8;
                  const x1 = section.bbox.polygon[startIdx];
                  const y1 = section.bbox.polygon[startIdx + 1];
                  const x2 = section.bbox.polygon[startIdx + 2];
                  const y2 = section.bbox.polygon[startIdx + 5];
                  const style = getStatusStyle(section.status, section.id);
                  
                  components.push(
                    <Rect
                      key={`${section.id}-component-${i}`}
                      x={x1}
                      y={y1}
                      width={x2 - x1}
                      height={y2 - y1}
                      stroke={style.color}
                      strokeWidth={2 / scale}
                      dash={style.dash ? style.dash.map(d => d / scale) : undefined}
                      fill={style.fill}
                      listening={drawMode === 'select' && !isPanning}
                      onClick={() => onSelectSection(section.id)}
                    />
                  );
                }
                
                return components;
              } else {
                // Render as single rectangle (existing behavior)
                const style = getStatusStyle(section.status, section.id);
                return (
                  <Rect
                    key={section.id}
                    x={section.bbox.x1}
                    y={section.bbox.y1}
                    width={section.bbox.x2 - section.bbox.x1}
                    height={section.bbox.y2 - section.bbox.y1}
                    stroke={style.color}
                    strokeWidth={2 / scale}
                    dash={style.dash ? style.dash.map(d => d / scale) : undefined}
                    fill={style.fill}
                    listening={drawMode === 'select' && !isPanning}
                    onClick={() => onSelectSection(section.id)}
                  />
                );
              }
            }).flat()}

            {/* Current drawing box */}
            {drawingBox && (
              <Rect
                x={drawingBox.x}
                y={drawingBox.y}
                width={drawingBox.width}
                height={drawingBox.height}
                stroke="#3b82f6"
                strokeWidth={4 / scale}
                fill="rgba(59, 130, 246, 0.15)"
                listening={false}
              />
            )}

            {/* The box waiting on the kind pill. Dashed: nothing is marked yet. */}
            {pendingBox && !drawingBox && (
              <Rect
                name="kind-pending-box"
                x={pendingBox.x1}
                y={pendingBox.y1}
                width={pendingBox.x2 - pendingBox.x1}
                height={pendingBox.y2 - pendingBox.y1}
                stroke={pendingBoxStroke}
                strokeWidth={3 / scale}
                dash={[8 / scale, 4 / scale]}
                listening={false}
              />
            )}

            {/* Pending mark box/line (shown while mark form is open) */}
            {pendingMarkBbox && !drawingBox && (
              pendingMarkBbox.polygon && pendingMarkBbox.polygon.length >= 4 ? (
                // Render as a line for node paths
                <>
                  <Line
                    points={pendingMarkBbox.polygon}
                    stroke="#2563eb"
                    strokeWidth={3 / scale}
                    lineCap="round"
                    lineJoin="round"
                    dash={[6 / scale, 3 / scale]}
                    listening={false}
                  />
                  {Array.from({ length: pendingMarkBbox.polygon.length / 2 }).map((_, i) => (
                    <Circle
                      key={`pending-pt-${i}`}
                      x={pendingMarkBbox.polygon![i * 2]}
                      y={pendingMarkBbox.polygon![i * 2 + 1]}
                      radius={8 / scale}
                      fill="#2563eb"
                      stroke="white"
                      strokeWidth={2 / scale}
                      name="pending-point"
                      draggable={Boolean(onUpdatePendingMarkBbox)}
                      onMouseDown={(e) => {
                        e.cancelBubble = true;
                      }}
                      onDragMove={(e) => {
                        if (!onUpdatePendingMarkBbox || !pendingMarkBbox?.polygon) return;
                        e.cancelBubble = true;
                        const next = [...pendingMarkBbox.polygon];
                        next[i * 2] = e.target.x();
                        next[i * 2 + 1] = e.target.y();
                        const xs = next.filter((_, idx) => idx % 2 === 0);
                        const ys = next.filter((_, idx) => idx % 2 === 1);
                        onUpdatePendingMarkBbox({
                          x1: Math.min(...xs),
                          y1: Math.min(...ys),
                          x2: Math.max(...xs),
                          y2: Math.max(...ys),
                          polygon: next,
                        });
                      }}
                      onDragStart={(e) => {
                        e.cancelBubble = true;
                      }}
                      listening={Boolean(onUpdatePendingMarkBbox)}
                    />
                  ))}
                </>
              ) : (
                // Render as a rectangle for equipment/instruments
                <>
                  <Rect
                    x={pendingMarkBbox.x1}
                    y={pendingMarkBbox.y1}
                    width={pendingMarkBbox.x2 - pendingMarkBbox.x1}
                    height={pendingMarkBbox.y2 - pendingMarkBbox.y1}
                    stroke="#2563eb"
                    strokeWidth={3 / scale}
                    fill="rgba(37, 99, 235, 0.12)"
                    name="pending-box"
                    draggable={Boolean(onUpdatePendingMarkBbox)}
                    onDragMove={(e) => {
                      if (!onUpdatePendingMarkBbox) return;
                      e.cancelBubble = true;
                      const node = e.target;
                      const width = pendingMarkBbox.x2 - pendingMarkBbox.x1;
                      const height = pendingMarkBbox.y2 - pendingMarkBbox.y1;
                      updatePendingBbox({
                        x1: node.x(),
                        y1: node.y(),
                        x2: node.x() + width,
                        y2: node.y() + height,
                      });
                    }}
                    onDragStart={(e) => {
                      e.cancelBubble = true;
                    }}
                    listening={Boolean(onUpdatePendingMarkBbox)}
                  />
                  {onUpdatePendingMarkBbox && (() => {
                    const handleSize = 10 / scale;
                    const minSize = 6;
                    const bounds = [
                      { key: 'nw', x: pendingMarkBbox.x1, y: pendingMarkBbox.y1 },
                      { key: 'ne', x: pendingMarkBbox.x2, y: pendingMarkBbox.y1 },
                      { key: 'se', x: pendingMarkBbox.x2, y: pendingMarkBbox.y2 },
                      { key: 'sw', x: pendingMarkBbox.x1, y: pendingMarkBbox.y2 },
                    ];
                    const updateFromHandle = (key: string, x: number, y: number) => {
                      let { x1, y1, x2, y2 } = pendingMarkBbox;
                      if (key === 'nw') {
                        x1 = Math.min(x, x2 - minSize);
                        y1 = Math.min(y, y2 - minSize);
                      } else if (key === 'ne') {
                        x2 = Math.max(x, x1 + minSize);
                        y1 = Math.min(y, y2 - minSize);
                      } else if (key === 'se') {
                        x2 = Math.max(x, x1 + minSize);
                        y2 = Math.max(y, y1 + minSize);
                      } else if (key === 'sw') {
                        x1 = Math.min(x, x2 - minSize);
                        y2 = Math.max(y, y1 + minSize);
                      }
                      updatePendingBbox({ x1, y1, x2, y2 });
                    };
                    return bounds.map((handle) => (
                      <Rect
                        key={`pending-handle-${handle.key}`}
                        x={handle.x - handleSize / 2}
                        y={handle.y - handleSize / 2}
                        width={handleSize}
                        height={handleSize}
                        fill="#ffffff"
                        stroke="#2563eb"
                        strokeWidth={2 / scale}
                        name="pending-handle"
                        draggable
                        onDragMove={(e) => {
                          e.cancelBubble = true;
                          const node = e.target;
                          updateFromHandle(handle.key, node.x() + handleSize / 2, node.y() + handleSize / 2);
                        }}
                        onDragStart={(e) => {
                          e.cancelBubble = true;
                        }}
                        listening
                      />
                    ));
                  })()}
                </>
              )
            )}

            {/* Pending pieces */}
            {pendingPieces
              .filter((piece) => {
                const force = piece.id === selectedPendingId;
                return isInViewport(piece.bbox, force);
              })
              .map((piece) => (
                <Rect
                  key={piece.id}
                  x={piece.bbox.x1}
                  y={piece.bbox.y1}
                  width={piece.bbox.x2 - piece.bbox.x1}
                  height={piece.bbox.y2 - piece.bbox.y1}
                  stroke={piece.id===selectedPendingId? '#2563eb': (piece.context ? 'var(--raven-text-secondary)':'#ff6b35')}
                  strokeWidth={2 / scale}
                  dash={[5 / scale, 5 / scale]}
                  fill="rgba(128,128,128,0.15)"
                  listening={drawMode === 'select' && !isPanning && Boolean(onSelectPending)}
                  onClick={() => onSelectPending?.(piece.id)}
                />
              ))}

            {/* Polygon live preview */}
            {drawMode === 'poly' && polyPoints.length > 3 && (
              <Line
                points={polyPoints}
                closed={isPolyDrawing ? false : true}
                stroke="#3b82f6"
                strokeWidth={2 / scale}
                dash={[3 / scale, 3 / scale]}
                fill={isPolyDrawing ? "transparent" : "rgba(59, 130, 246, 0.1)"}
              />
            )}

            {/* Path drawing preview (for connection lines) */}
            {drawMode === 'path' && pathPoints.length >= 2 && (
              <>
                {/* The main path line */}
                <Line
                  points={pathPreviewPoint 
                    ? [...pathPoints, pathPreviewPoint.x, pathPreviewPoint.y] 
                    : pathPoints
                  }
                  stroke="#059669"
                  strokeWidth={3 / scale}
                  lineCap="round"
                  lineJoin="round"
                />
                {/* The vertices: a dot on the paper, a filled ring on the asset it binds to */}
                {lineDraft.vertices.map((vertex, i) => (
                  <Circle
                    key={`path-point-${i}`}
                    name={vertex.binding ? 'bound-vertex' : 'free-vertex'}
                    x={vertex.x}
                    y={vertex.y}
                    radius={(vertex.binding ? 6 : 5) / scale}
                    fill={vertex.binding ? '#2563eb' : '#059669'}
                    stroke="white"
                    strokeWidth={(vertex.binding ? 2 : 1) / scale}
                  />
                ))}
              </>
            )}
            {/* The target under the pointer, and where the next vertex will land on it */}
            {drawMode === 'path' && bindHover && (
              <>
                {bindHover.outline.kind === 'box' ? (
                  <Rect
                    name="bind-target"
                    x={bindHover.outline.bbox.x1}
                    y={bindHover.outline.bbox.y1}
                    width={bindHover.outline.bbox.x2 - bindHover.outline.bbox.x1}
                    height={bindHover.outline.bbox.y2 - bindHover.outline.bbox.y1}
                    stroke="#2563eb"
                    strokeWidth={2 / scale}
                    listening={false}
                  />
                ) : (
                  <Line
                    name="bind-target"
                    points={bindHover.outline.points}
                    stroke="#2563eb"
                    strokeWidth={4 / scale}
                    lineCap="round"
                    lineJoin="round"
                    listening={false}
                  />
                )}
                <Circle
                  name="bind-snap-ring"
                  x={bindHover.snap.x}
                  y={bindHover.snap.y}
                  radius={7 / scale}
                  stroke="#2563eb"
                  strokeWidth={2 / scale}
                  fill="rgba(37, 99, 235, 0.15)"
                  listening={false}
                />
              </>
            )}

            {/* Annotating surface: render the PNID data */}
            {annotating && pnidState && (
              <Group
                x={annotationShiftPreview.dx}
                y={annotationShiftPreview.dy}
                name="wizard-annotation-layer"
              >
                {/* Equipments - only render marked ones with bbox */}
                {pnidState.equipments
                  .filter(eq => eq.bbox)
                  .filter(eq => equipmentOverlayVisible(activeAssetKind, isolating, eq.id === activeEquipmentId))
                  .filter(eq => {
                    const force = eq.id === activeEquipmentId || eq.id === highlightedEntityId;
                    return isInViewport(eq.bbox, force);
                  })
                  .map((eq) => {
                  // Equipment is only "active" when equipment is the active kind AND it is the selected equipment
                  const isActive = activeAssetKind === 'equipment' && eq.id === activeEquipmentId;
                  const isBulkSelected = selectedWizardItemKeys?.has(wizardCanvasItemKey({ kind: 'equipment', id: eq.id })) ?? false;
                  const isHighlighted = eq.id === highlightedEntityId;
                  const bbox = eq.bbox!;
                  const isHovered = hoveredItem?.id === eq.id && hoveredItem?.type === 'equipment';
                  const inactive = wizardInactivePalette('equipment', eq.status === 'final_and_reviewed');
                  // Active/highlighted: blue; inactive: type color until reviewed, then light green
                  const strokeColor = isHovered
                    ? (isActive || isBulkSelected || isHighlighted ? '#1d4ed8' : inactive.hoverStroke)
                    : (isActive || isBulkSelected || isHighlighted ? '#2563eb' : inactive.stroke);
                  const fillColor = isHovered
                    ? (isActive || isBulkSelected || isHighlighted ? 'rgba(37, 99, 235, 0.25)' : inactive.hoverFill)
                    : (isActive || isBulkSelected || isHighlighted ? 'rgba(37, 99, 235, 0.15)' : inactive.fill);
                  return (
                    <React.Fragment key={`eq-${eq.id}`}>
                      <Rect
                        x={bbox.x1}
                        y={bbox.y1}
                        width={bbox.x2 - bbox.x1}
                        height={bbox.y2 - bbox.y1}
                        stroke={strokeColor}
                        strokeWidth={(isHovered || isActive || isBulkSelected || isHighlighted ? 3 : 2) / scale}
                        fill={fillColor}
                        opacity={isActive || isBulkSelected || isHighlighted ? 1 : 0.85}
                        dash={isActive || isBulkSelected || isHighlighted ? undefined : [6 / scale, 3 / scale]}
                        listening={Boolean(onSelectWizardItem)}
                        name="wizard-mark equipment"
                        onClick={(e) => {
                          if (!canSelectMark()) return;
                          e.cancelBubble = true;
                          onSelectWizardItem?.({
                            kind: 'equipment',
                            id: eq.id,
                            screenPosition: getPointerScreenPosition(),
                            isMultiSelect: e.evt.shiftKey || e.evt.metaKey || e.evt.ctrlKey,
                          });
                        }}
                        onMouseEnter={() => {
                          cancelHoverClear();
                          setHoveredItem({
                            type: 'equipment',
                            id: eq.id,
                            tag: eq.equipment_no,
                            itemType: eq.equipment_type,
                            bbox,
                          });
                        }}
                        onMouseLeave={scheduleHoverClear}
                      />
                      {(isActive || isBulkSelected) && renderPersistedBboxHandles(
                        { kind: 'equipment', id: eq.id },
                        bbox,
                        '#2563eb',
                      )}
                    </React.Fragment>
                  );
                })}

                {/* Equipment Instruments - only render marked ones with bbox */}
                {equipmentInstruments
                  .filter(inst => instrumentRegions(inst).length > 0)
                  .filter(inst => attachedInstrumentOverlayVisible(
                    activeAssetKind,
                    isolating,
                    'equipment',
                    getInstrumentEffectiveContext(inst, pnidState).context_id === activeEquipmentId,
                  ))
                  .filter(inst => {
                    const effectiveContext = getInstrumentEffectiveContext(inst, pnidState);
                    const force =
                      inst.id === selectedInstrumentId ||
                      inst.id === highlightedEntityId ||
                      effectiveContext.context_id === activeEquipmentId;
                    // Per region: a balloon drawn across the sheet from its symbol still
                    // draws when only the balloon is on screen.
                    return instrumentRegions(inst).some((region) => isInViewport(region, force));
                  })
                  .map((inst) => {
                  const effectiveContext = getInstrumentEffectiveContext(inst, pnidState);
                  // An equipment's instruments are active with their parent.
                  const isActive = activeAssetKind === 'equipment' && effectiveContext.context_id === activeEquipmentId;
                  const isSelected = inst.id === selectedInstrumentId;
                  const isBulkSelected = selectedWizardItemKeys?.has(wizardCanvasItemKey({ kind: 'equipmentInstrument', id: inst.id })) ?? false;
                  const isHighlighted = inst.id === highlightedEntityId;
                  const regions = instrumentRegions(inst);
                  const isHovered = hoveredItem?.id === inst.id && hoveredItem?.type === 'equipment_instrument';
                  const reviewed = inst.status === 'final_and_reviewed';
                  const inactive = wizardInactivePalette('instrument', reviewed);
                  const baseStroke = isSelected || isBulkSelected
                    ? (reviewed ? '#15803d' : '#7c3aed')
                    : (isActive || isHighlighted ? '#2563eb' : inactive.stroke);
                  const hoverStroke = isSelected || isBulkSelected
                    ? (reviewed ? '#166534' : '#6d28d9')
                    : (isActive || isHighlighted ? '#1d4ed8' : inactive.hoverStroke);
                  const baseFill = isSelected || isBulkSelected
                    ? (reviewed ? 'rgba(21, 128, 61, 0.2)' : 'rgba(124, 58, 237, 0.18)')
                    : (isActive || isHighlighted ? 'rgba(37, 99, 235, 0.15)' : inactive.fill);
                  const hoverFill = isSelected || isBulkSelected
                    ? (reviewed ? 'rgba(21, 128, 61, 0.3)' : 'rgba(124, 58, 237, 0.28)')
                    : (isActive || isHighlighted ? 'rgba(37, 99, 235, 0.25)' : inactive.hoverFill);
                  const strokeColor = isHovered ? hoverStroke : baseStroke;
                  const fillColor = isHovered ? hoverFill : baseFill;
                  const hitPadding = 4 / scale;
                  const shouldTrackHover = !suppressHoverInspection;
                  return (
                    <React.Fragment key={`eq-inst-${inst.id}`}>
                      {/* One rectangle per region, all sharing the marking's hover and
                          selection state, so pointing at the balloon lights the symbol too. */}
                      {regions.map((bbox, regionIndex) => (
                        <React.Fragment key={`eq-inst-${inst.id}-region-${regionIndex}`}>
                          {regions.length > 1 && (
                            <RegionIndexChip
                              region={bbox}
                              index={regionIndex}
                              color={strokeColor}
                              scale={scale}
                            />
                          )}
                          <Rect
                            x={bbox.x1}
                            y={bbox.y1}
                            width={bbox.x2 - bbox.x1}
                            height={bbox.y2 - bbox.y1}
                            stroke={strokeColor}
                            strokeWidth={(isHovered || isActive || isSelected || isBulkSelected || isHighlighted ? 3 : 2) / scale}
                            fill={fillColor}
                            opacity={isActive || isSelected || isBulkSelected || isHighlighted ? 1 : 0.85}
                            dash={isActive || isSelected || isBulkSelected || isHighlighted ? undefined : [4 / scale, 2 / scale]}
                            listening={false}
                            name="wizard-mark equipment-instrument"
                            perfectDrawEnabled={false}
                            shadowForStrokeEnabled={false}
                          />
                          <Rect
                            x={bbox.x1 - hitPadding}
                            y={bbox.y1 - hitPadding}
                            width={bbox.x2 - bbox.x1 + hitPadding * 2}
                            height={bbox.y2 - bbox.y1 + hitPadding * 2}
                            fill="transparent"
                            listening={Boolean(onSelectWizardItem || onRightClickInstrument)}
                            name="wizard-mark equipment-instrument"
                            perfectDrawEnabled={false}
                            shadowForStrokeEnabled={false}
                            onMouseDown={(e) => {
                              if (drawMode === 'select' || e.evt.button !== 0) {
                                e.cancelBubble = true;
                              }
                            }}
                            onClick={(e) => {
                              if (!canSelectMark()) return;
                              e.cancelBubble = true;
                              onSelectWizardItem?.({
                                kind: 'equipmentInstrument',
                                id: inst.id,
                                screenPosition: getPointerScreenPosition(),
                                isMultiSelect: e.evt.shiftKey || e.evt.metaKey || e.evt.ctrlKey,
                              });
                            }}
                            onContextMenu={(e) => {
                              e.evt.preventDefault();
                              e.cancelBubble = true;
                              if (e.evt.metaKey || e.evt.ctrlKey || e.evt.shiftKey || e.evt.altKey) return;
                              onRightClickInstrument?.(inst.id, { x: e.evt.clientX, y: e.evt.clientY });
                            }}
                            onMouseEnter={() => {
                              if (!shouldTrackHover) return;
                              cancelHoverClear();
                              setHoveredItem({
                                type: 'equipment_instrument',
                                id: inst.id,
                                tag: getInstrumentPrimaryLabel(inst),
                                itemType: getEquipmentInstrumentName(inst.instrument_type),
                                // The region under the cursor, so the tooltip lands beside
                                // what the SME is actually pointing at.
                                bbox,
                              });
                            }}
                            onMouseLeave={() => {
                              if (!shouldTrackHover) return;
                              scheduleHoverClear();
                            }}
                          />
                          {(isSelected || isBulkSelected) && renderPersistedBboxHandles(
                            { kind: 'equipmentInstrument', id: inst.id },
                            bbox,
                            isSelected || isBulkSelected ? '#7c3aed' : '#2563eb',
                            { regionIndex },
                          )}
                        </React.Fragment>
                      ))}
                      {/* The badge deletes the marking, not a region, so it sits on the symbol alone. */}
                      {(isSelected || isBulkSelected || isHovered) && renderQuickDeleteBadge(
                        { kind: 'equipmentInstrument', id: inst.id },
                        regions[0],
                      )}
                    </React.Fragment>
                  );
                })}

                {/* Unattached instruments — no equipment and no line owns them, so no
                    parent's layer draws them. A drawing with neither can hold nothing
                    else, and an SME who marked one has to be able to reach it. They wear
                    the violet dashed outline the unattached template/AI annotations wear
                    below, so "dashed violet" reads as "attached to nothing" everywhere. */}
                {unattachedInstruments
                  .filter(inst => instrumentRegions(inst).length > 0)
                  .filter(() => unattachedInstrumentOverlayVisible(isolating))
                  .filter(inst => {
                    const force = inst.id === selectedInstrumentId || inst.id === highlightedEntityId;
                    // Per region: a balloon drawn across the sheet from its symbol still
                    // draws when only the balloon is on screen.
                    return instrumentRegions(inst).some((region) => isInViewport(region, force));
                  })
                  .map((inst) => {
                  const isSelected = inst.id === selectedInstrumentId;
                  // An unattached instrument keys as 'equipmentInstrument', the kind the
                  // workspace already gives every instrument that is not a line's.
                  const isBulkSelected = selectedWizardItemKeys?.has(wizardCanvasItemKey({ kind: 'equipmentInstrument', id: inst.id })) ?? false;
                  const isHighlighted = inst.id === highlightedEntityId;
                  const regions = instrumentRegions(inst);
                  const isHovered = hoveredItem?.id === inst.id && hoveredItem?.type === 'unattached_instrument';
                  const reviewed = inst.status === 'final_and_reviewed';
                  const unselected = wizardInactivePalette('instrument', reviewed);
                  const baseStroke = isSelected || isBulkSelected
                    ? (reviewed ? '#15803d' : '#7c3aed')
                    : (isHighlighted ? '#2563eb' : unselected.stroke);
                  const hoverStroke = isSelected || isBulkSelected
                    ? (reviewed ? '#166534' : '#6d28d9')
                    : (isHighlighted ? '#1d4ed8' : unselected.hoverStroke);
                  const baseFill = isSelected || isBulkSelected
                    ? (reviewed ? 'rgba(21, 128, 61, 0.2)' : 'rgba(124, 58, 237, 0.18)')
                    : (isHighlighted ? 'rgba(37, 99, 235, 0.15)' : unselected.fill);
                  const hoverFill = isSelected || isBulkSelected
                    ? (reviewed ? 'rgba(21, 128, 61, 0.3)' : 'rgba(124, 58, 237, 0.28)')
                    : (isHighlighted ? 'rgba(37, 99, 235, 0.25)' : unselected.hoverFill);
                  const strokeColor = isHovered ? hoverStroke : baseStroke;
                  const fillColor = isHovered ? hoverFill : baseFill;
                  const hitPadding = 4 / scale;
                  const shouldTrackHover = !suppressHoverInspection;
                  return (
                    <React.Fragment key={`unattached-inst-${inst.id}`}>
                      {/* One rectangle per region, all sharing the marking's hover and
                          selection state, so pointing at the balloon lights the symbol too. */}
                      {regions.map((bbox, regionIndex) => (
                        <React.Fragment key={`unattached-inst-${inst.id}-region-${regionIndex}`}>
                          {regions.length > 1 && (
                            <RegionIndexChip
                              region={bbox}
                              index={regionIndex}
                              color={strokeColor}
                              scale={scale}
                            />
                          )}
                          <Rect
                            x={bbox.x1}
                            y={bbox.y1}
                            width={bbox.x2 - bbox.x1}
                            height={bbox.y2 - bbox.y1}
                            stroke={strokeColor}
                            strokeWidth={(isHovered || isSelected || isBulkSelected || isHighlighted ? 3 : 2) / scale}
                            fill={fillColor}
                            opacity={isSelected || isBulkSelected || isHighlighted ? 1 : 0.85}
                            // Dashed at every state: the dash is what says "attached to nothing".
                            dash={[6 / scale, 3 / scale]}
                            listening={false}
                            name="wizard-mark unattached-instrument"
                            perfectDrawEnabled={false}
                            shadowForStrokeEnabled={false}
                          />
                          <Rect
                            x={bbox.x1 - hitPadding}
                            y={bbox.y1 - hitPadding}
                            width={bbox.x2 - bbox.x1 + hitPadding * 2}
                            height={bbox.y2 - bbox.y1 + hitPadding * 2}
                            fill="transparent"
                            listening={Boolean(onSelectWizardItem || onRightClickInstrument)}
                            name="wizard-mark unattached-instrument"
                            perfectDrawEnabled={false}
                            shadowForStrokeEnabled={false}
                            onMouseDown={(e) => {
                              if (drawMode === 'select' || e.evt.button !== 0) {
                                e.cancelBubble = true;
                              }
                            }}
                            onClick={(e) => {
                              // DECISION(2026-10-23, Abhishek): a plain click adopts a template match
                              // that nothing owns onto the active equipment or line, and persists it at once; a
                              // plain unattached instrument still opens its editor. M1c I1 lifts the template
                              // gate once every unattached instrument attaches this way.
                              const attachTargetId = activeAssetKind === 'equipment' ? activeEquipmentId : activeNodeId;
                              if (
                                isTemplateMatchInstrument(inst)
                                && attachTargetId
                                && onDirectAttachAnnotation
                                && !readOnly
                                && marksTakeClicks
                                && !pressDraggedRef.current
                                && !pressStartedLineFromMarkRef.current
                                && !pressCopiedRef.current
                                && !(e.evt.metaKey || e.evt.ctrlKey)
                              ) {
                                e.cancelBubble = true;
                                onDirectAttachAnnotation(inst.id);
                                return;
                              }
                              if (!canSelectMark()) return;
                              e.cancelBubble = true;
                              onSelectWizardItem?.({
                                kind: 'equipmentInstrument',
                                id: inst.id,
                                screenPosition: getPointerScreenPosition(),
                                isMultiSelect: e.evt.shiftKey || e.evt.metaKey || e.evt.ctrlKey,
                              });
                            }}
                            onContextMenu={(e) => {
                              e.evt.preventDefault();
                              e.cancelBubble = true;
                              if (e.evt.metaKey || e.evt.ctrlKey || e.evt.shiftKey || e.evt.altKey) return;
                              onRightClickInstrument?.(inst.id, { x: e.evt.clientX, y: e.evt.clientY });
                            }}
                            onMouseEnter={() => {
                              if (!shouldTrackHover) return;
                              cancelHoverClear();
                              setHoveredItem({
                                type: 'unattached_instrument',
                                id: inst.id,
                                tag: getInstrumentPrimaryLabel(inst),
                                itemType: inst.instrument_type
                                  ? getInstrumentCategoryName(inst.instrument_type)
                                  : 'Unattached',
                                // The region under the cursor, so the tooltip lands beside
                                // what the SME is actually pointing at.
                                bbox,
                              });
                            }}
                            onMouseLeave={() => {
                              if (!shouldTrackHover) return;
                              scheduleHoverClear();
                            }}
                          />
                          {(isSelected || isBulkSelected) && renderPersistedBboxHandles(
                            { kind: 'equipmentInstrument', id: inst.id },
                            bbox,
                            isSelected || isBulkSelected ? '#7c3aed' : '#a78bfa',
                            { regionIndex },
                          )}
                        </React.Fragment>
                      ))}
                      {/* The badge deletes the marking, not a region, so it sits on the symbol alone. */}
                      {(isSelected || isBulkSelected || isHovered) && renderQuickDeleteBadge(
                        { kind: 'equipmentInstrument', id: inst.id },
                        regions[0],
                      )}
                    </React.Fragment>
                  );
                })}

                {/* Nodes - render as visible corridors (like pipes) */}
                {pnidState.nodes
                  .filter((node) => {
                    // During redraw for an existing line, hide the active line so users can draw cleanly.
                    if (isNodeMarkingMode && activeNodeId && node.id === activeNodeId) {
                      return false;
                    }
                    if (!nodeOverlayVisible(activeAssetKind, isolating, node.id === activeNodeId)) return false;
                    if (isolating) return true;
                    // A marked pipeline needs a polyline, a rectangular duct its box.
                    return node.is_duct ? Boolean(node.duct_bbox) : Boolean(node.polyline && node.polyline.length >= 4);
                  })
                  .filter((node) => {
                    const nodeBounds =
                      (node.is_duct ? boundsFromBbox(node.duct_bbox) : boundsFromPolyline(node.polyline)) ??
                      boundsFromLineSegments(node.line_segments, segScale.sx, segScale.sy);
                    const force =
                      node.id === activeNodeId ||
                      node.id === highlightedEntityId ||
                      segmentReview?.nodeId === node.id;
                    return isInViewport(
                      nodeBounds,
                      force
                    );
                  })
                  .map((node) => {
                  // Node is only "active" when line is the active kind AND it is the selected node
                  const isLineActiveKind = activeAssetKind === 'line';
                  const isActive = isLineActiveKind && node.id === activeNodeId;
                  const isBulkSelected = selectedWizardItemKeys?.has(wizardCanvasItemKey({ kind: 'node', id: node.id })) ?? false;
                  const isHighlighted = node.id === highlightedEntityId;
                  const hasLineSegments = Boolean(node.line_segments && node.line_segments.length > 0);
                  const shouldShowSegmentMarker =
                    isLineActiveKind && hasLineSegments && !(node.polyline && node.polyline.length >= 4);
                  const shouldShowSegmentReviewOverlay =
                    segmentReview?.nodeId === node.id && segmentReviewDistinct.length > 0;

                  if (node.is_duct && node.duct_bbox) {
                    const duct = node.duct_bbox;
                    const isNodeHovered = hoveredItem?.id === node.id && hoveredItem?.type === 'node';
                    const isEmphasized = isActive || isBulkSelected || isHighlighted;
                    const palette = wizardInactivePalette('line', node.status === 'final_and_reviewed');
                    const stroke = isEmphasized || isNodeHovered ? '#2563eb' : palette.stroke;
                    const fill = isEmphasized || isNodeHovered ? 'rgba(37, 99, 235, 0.18)' : palette.fill;
                    const canSelect = marksTakeClicks && !isNodeMarkingMode && !isPanning;
                    return (
                      <React.Fragment key={`node-duct-${node.id}`}>
                        <Rect
                          name="wizard-mark node-duct"
                          x={duct.x1}
                          y={duct.y1}
                          width={duct.x2 - duct.x1}
                          height={duct.y2 - duct.y1}
                          fill={fill}
                          stroke={stroke}
                          strokeWidth={(isEmphasized || isNodeHovered ? 3 : 2) / scale}
                          dash={isActive ? undefined : [8 / scale, 4 / scale]}
                          listening={canSelect}
                          onMouseEnter={() => {
                            cancelHoverClear();
                            setHoveredItem({
                              type: 'node',
                              id: node.id,
                              tag: `Duct #${node.node_no}`,
                              itemType: `${node.line_no}\n${node.source_ref} → ${node.dest_ref}`,
                              lineNo: node.line_no,
                              bbox: duct,
                            });
                          }}
                          onMouseLeave={scheduleHoverClear}
                          onClick={(e) => {
                            if (!canSelect || !canSelectMark()) return;
                            e.cancelBubble = true;
                            onSelectWizardItem?.({
                              kind: 'node',
                              id: node.id,
                              screenPosition: getPointerScreenPosition(),
                              isMultiSelect: e.evt.shiftKey || e.evt.metaKey || e.evt.ctrlKey,
                            });
                          }}
                          onDblClick={(e) => {
                            if (isSpaceHeld || !onDoubleClickNode) return;
                            e.cancelBubble = true;
                            onDoubleClickNode(node.id, getPointerScreenPosition());
                          }}
                        />
                        {(isActive || isNodeHovered) && (
                          <Text
                            x={duct.x1 + 4 / scale}
                            y={duct.y1 + 4 / scale}
                            text={node.line_no?.trim() ? `Duct #${node.node_no} · ${node.line_no.trim()}` : `Duct #${node.node_no}`}
                            fontSize={13 / scale}
                            fontStyle="bold"
                            fill={stroke}
                            listening={false}
                          />
                        )}
                        {canSelect && (isActive || isBulkSelected) && renderPersistedBboxHandles(
                          { kind: 'node', id: node.id },
                          duct,
                          '#2563eb',
                          onDoubleClickNode
                            ? { onDblClick: () => onDoubleClickNode(node.id, getPointerScreenPosition()) }
                            : undefined,
                        )}
                      </React.Fragment>
                    );
                  }
                  
                  // Nodes use polyline directly (not bbox)
                  if (node.polyline && node.polyline.length >= 4) {
                    // Calculate midpoint for label placement
                    const midIdx = Math.floor(node.polyline.length / 4) * 2;
                    const labelX = node.polyline[midIdx] ?? node.polyline[0];
                    const labelY = node.polyline[midIdx + 1] ?? node.polyline[1];
                    
                    // Inactive lines: orange until reviewed, then light green
                    const linePalette = wizardInactivePalette('line', node.status === 'final_and_reviewed');
                    const inactiveColor = linePalette.stroke;
                    const inactiveFill = linePalette.fill;
                    const isEmphasized = isActive || isBulkSelected || isHighlighted;
                    const baseColor = isEmphasized ? '#2563eb' : inactiveColor;
                    const fillColor = isEmphasized ? 'rgba(37, 99, 235, 0.4)' : inactiveFill;
                    const pointColor = isEmphasized ? '#2563eb' : baseColor;
                    const labelColor = isEmphasized ? '#2563eb' : baseColor;

                    // Corridor width - thicker for visibility
                    const corridorWidth = (isEmphasized ? 24 : 12) / scale;
                    const strokeWidth = (isEmphasized ? 3 : 1.5) / scale;
                    
                    // Compute bounding box from polyline for hover tooltip positioning
                    const nodeBbox = boundsFromPolyline(node.polyline) ?? {
                      x1: node.polyline[0],
                      y1: node.polyline[1],
                      x2: node.polyline[0],
                      y2: node.polyline[1],
                    };
                    const isNodeHovered = hoveredItem?.id === node.id && hoveredItem?.type === 'node';
                    const showLabel = (isActive || isNodeHovered) && (!suppressHoverInspection || isActive);
                    const focusT = Math.max(0, Math.min(1, 1 - focusFlashElapsedMs / FOCUS_FLASH_DURATION_MS)); // 1 -> 0
                    const isFocusFlash = Boolean(isActive && focusRequest && focusT > 0);

                    const shouldRenderNodePoints =
                      !reducePassiveOverlays &&
                      (nodePointMode === 'all' ||
                        (nodePointMode === 'active' && (isActive || isNodeHovered || scale >= 1.5)));
                    const pointCount = node.polyline.length / 2;
                    const maxPointDots = scale >= 1.5 ? 800 : 200;
                    const pointStride = pointCount > maxPointDots ? Math.ceil(pointCount / maxPointDots) : 1;
                    
                    return (
                      <React.Fragment key={`node-${node.id}`}>
                        {/* Background corridor - thick semi-transparent line for visibility */}
                        <Line
                          points={node.polyline}
                          stroke={isNodeHovered ? 'rgba(59, 130, 246, 0.5)' : fillColor}
                          strokeWidth={isNodeHovered ? corridorWidth * 1.3 : corridorWidth}
                          lineCap="round"
                          lineJoin="round"
                          opacity={isActive ? 1 : (isNodeHovered ? 0.92 : 0.82)}
                          listening={true}
                          name="wizard-mark node-line"
                          onMouseEnter={() => setHoveredItem({
                            type: 'node',
                            id: node.id,
                            tag: `Node #${node.node_no}`,
                            itemType: `${node.line_no}\n${node.source_ref} → ${node.dest_ref}`,
                            lineNo: node.line_no,
                            bbox: nodeBbox,
                          })}
                          onMouseLeave={() => setHoveredItem(null)}
                          onClick={(e) => {
                            // Don't open dialog if in marking mode or actively drawing
                            if (isNodeMarkingMode || !canSelectMark()) return;
                            e.cancelBubble = true;
                            onSelectWizardItem?.({
                              kind: 'node',
                              id: node.id,
                              screenPosition: getPointerScreenPosition(),
                              isMultiSelect: e.evt.shiftKey || e.evt.metaKey || e.evt.ctrlKey,
                            });
                          }}
                          onDblClick={(e) => {
                            // Double-click to enter mark mode for this node
                            if (isSpaceHeld) return;
                            e.cancelBubble = true;
                            if (onDoubleClickNode) {
                              const stage = stageRef.current;
                              const pointer = stage?.getPointerPosition();
                              const container = stage?.container();
                              if (pointer && container) {
                                const rect = container.getBoundingClientRect();
                                const screenX = rect.left + pointer.x;
                                const screenY = rect.top + pointer.y;
                                onDoubleClickNode(node.id, { x: screenX, y: screenY });
                              } else {
                                onDoubleClickNode(node.id);
                              }
                            }
                          }}
                        />
                        {/* Main line stroke on top */}
                        <Line
                          points={node.polyline}
                          stroke={isNodeHovered ? '#3b82f6' : baseColor}
                          strokeWidth={isNodeHovered ? strokeWidth * 1.5 : strokeWidth}
                          lineCap="round"
                          lineJoin="round"
                          dash={isActive ? undefined : [8 / scale, 4 / scale]}
                          opacity={isActive ? 1 : (isNodeHovered ? 1 : 0.85)}
                          listening={false}
                        />
                        {/* Line text highlight (AI label bbox) after zoom/focus */}
                        {isFocusFlash && node.label_bbox && (
                          <>
                            <Rect
                              x={node.label_bbox.x1 - 6 / scale}
                              y={node.label_bbox.y1 - 6 / scale}
                              width={node.label_bbox.x2 - node.label_bbox.x1 + 12 / scale}
                              height={node.label_bbox.y2 - node.label_bbox.y1 + 12 / scale}
                              stroke="#fde047"
                              strokeWidth={4 / scale}
                              shadowColor="#fde047"
                              shadowBlur={(16 * focusT) / scale}
                              shadowOpacity={0.55 * focusT}
                              listening={false}
                            />
                            <Rect
                              x={node.label_bbox.x1 - 10 / scale}
                              y={node.label_bbox.y1 - 10 / scale}
                              width={node.label_bbox.x2 - node.label_bbox.x1 + 20 / scale}
                              height={node.label_bbox.y2 - node.label_bbox.y1 + 20 / scale}
                              stroke="#7c3aed"
                              strokeWidth={2 / scale}
                              dash={[10 / scale, 6 / scale]}
                              dashOffset={-(focusFlashElapsedMs * 0.05) / Math.max(1e-6, scale)}
                              opacity={0.9}
                              listening={false}
                            />
                          </>
                        )}
	                        {/* Line segment review overlay (distinct merged segments) */}
	                        {shouldShowSegmentReviewOverlay && (
	                          <>
	                            {segmentReviewDistinct.map((seg) => {
	                              const status = deriveDistinctStatus(seg);
	                              const base = getSegmentStroke(status);
	                              const isSegHovered = segmentReview?.hoveredDistinctId === seg.id;
	                              const isSegSelected = segmentReview?.selectedDistinctId === seg.id;
	                              const stroke = isSegHovered ? '#2563eb' : base.stroke;
	                              const segStrokeWidth = (isSegHovered || isSegSelected ? 5 : 4) / scale;
	                              return (
	                                <React.Fragment key={`node-${node.id}-seg-${seg.id}`}>
	                                  <Line
	                                    points={[seg.x1, seg.y1, seg.x2, seg.y2]}
	                                    stroke={stroke}
	                                    strokeWidth={segStrokeWidth}
	                                    lineCap="round"
	                                    lineJoin="round"
	                                    dash={base.dash}
	                                    opacity={0.95}
	                                    listening={false}
	                                  />
	                                  {(isSegHovered || isSegSelected) && (
	                                    <>
	                                      <Circle
	                                        x={seg.x1}
	                                        y={seg.y1}
	                                        radius={6 / scale}
	                                        fill={stroke}
	                                        stroke="white"
	                                        strokeWidth={2 / scale}
	                                        listening={false}
	                                      />
	                                      <Circle
	                                        x={seg.x2}
	                                        y={seg.y2}
	                                        radius={6 / scale}
	                                        fill={stroke}
	                                        stroke="white"
	                                        strokeWidth={2 / scale}
	                                        listening={false}
	                                      />
	                                    </>
	                                  )}
	                                </React.Fragment>
	                              );
	                            })}
	                          </>
	                        )}
	                        {/* Render points along the path - smaller, cleaner */}
	                        {shouldRenderNodePoints && (() => {
	                          const dots: React.ReactNode[] = [];
	                          for (let i = 0; i < pointCount; i += pointStride) {
	                            const x = node.polyline![i * 2];
	                            const y = node.polyline![i * 2 + 1];
	                            if (typeof x !== 'number' || typeof y !== 'number') continue;
	                            dots.push(
	                              <Circle
                                key={`node-${node.id}-pt-${i}`}
                                x={x}
                                y={y}
                                radius={(isActive || isNodeHovered ? 6 : 4) / scale}
                                fill={isNodeHovered ? '#3b82f6' : pointColor}
                                stroke="white"
                                strokeWidth={1.5 / scale}
                                opacity={isActive ? 1 : (isNodeHovered ? 1 : 0.6)}
                                listening={false}
                              />
                            );
                          }
                          const lastIdx = pointCount - 1;
                          if (lastIdx >= 0 && lastIdx % pointStride !== 0) {
                            const x = node.polyline![lastIdx * 2];
                            const y = node.polyline![lastIdx * 2 + 1];
                            if (typeof x === 'number' && typeof y === 'number') {
                              dots.push(
                                <Circle
                                  key={`node-${node.id}-pt-last`}
                                  x={x}
                                  y={y}
                                  radius={(isActive || isNodeHovered ? 6 : 4) / scale}
                                  fill={isNodeHovered ? '#3b82f6' : pointColor}
                                  stroke="white"
                                  strokeWidth={1.5 / scale}
                                  opacity={isActive ? 1 : (isNodeHovered ? 1 : 0.6)}
                                  listening={false}
                                />
                              );
                            }
                          }
                          return dots;
                        })()}
                        {/* Node label (includes line text); highlighted after zoom/focus */}
                        {showLabel && (
                          <Label
                            x={labelX}
                            y={labelY}
                            offsetX={12 / scale}
                            offsetY={12 / scale}
                            opacity={isActive || isNodeHovered ? 1 : 0.7}
                          >
                            <Tag
                              fill={isFocusFlash ? '#fde047' : (isNodeHovered ? '#3b82f6' : labelColor)}
                              stroke={isFocusFlash ? '#7c3aed' : undefined}
                              strokeWidth={isFocusFlash ? 2 / scale : 0}
                              cornerRadius={4 / scale}
                              pointerDirection="down"
                              pointerWidth={6 / scale}
                              pointerHeight={4 / scale}
                            />
                            <Text
                              text={node.line_no?.trim() ? `#${node.node_no} · ${node.line_no.trim()}` : `#${node.node_no}`}
                              fontSize={14 / scale}
                              fontStyle="bold"
                              fill={isFocusFlash ? '#111827' : 'white'}
                              padding={4 / scale}
                            />
                          </Label>
                        )}
                      </React.Fragment>
                    );
                  }
                  
                  // Nodes with AI segments but no polyline yet: show a small marker so they’re visible/clickable,
                  // and (when selected) draw the distinct merged segment overlay.
                  if (shouldShowSegmentMarker) {
                    const segs = node.line_segments ?? [];
                    const midpoints = segs.map((s) => ({
                      x: ((s.x1 + s.x2) / 2) * segScale.sx,
                      y: ((s.y1 + s.y2) / 2) * segScale.sy,
                    }));
                    const marker = midpoints.length
                      ? {
                          x: midpoints.reduce((sum, p) => sum + p.x, 0) / midpoints.length,
                          y: midpoints.reduce((sum, p) => sum + p.y, 0) / midpoints.length,
                        }
                      : { x: 0, y: 0 };

                    const reviewStatus = node.segments_review_status ?? 'pending';
                    const markerStyle =
                      reviewStatus === 'confirmed'
                        ? { fill: 'rgba(34, 197, 94, 0.9)', stroke: '#166534' }
                        : reviewStatus === 'in_review'
                          ? { fill: 'rgba(245, 158, 11, 0.9)', stroke: '#92400e' }
                          : { fill: 'rgba(74, 222, 128, 0.92)', stroke: '#166534' };

                    const bbox: BoundingBox = {
                      x1: marker.x - 20,
                      y1: marker.y - 20,
                      x2: marker.x + 20,
                      y2: marker.y + 20,
                    };

                    return (
                      <React.Fragment key={`node-marker-${node.id}`}>
                        {shouldShowSegmentReviewOverlay && (
                          <>
                            {segmentReviewDistinct.map((seg) => {
                              const status = deriveDistinctStatus(seg);
                              const base = getSegmentStroke(status);
                              const isSegHovered = segmentReview?.hoveredDistinctId === seg.id;
                              const isSegSelected = segmentReview?.selectedDistinctId === seg.id;
                              const stroke = isSegHovered ? '#2563eb' : base.stroke;
                              const segStrokeWidth = (isSegHovered || isSegSelected ? 5 : 4) / scale;
                              return (
                                <React.Fragment key={`node-${node.id}-seg-${seg.id}`}>
                                  <Line
                                    points={[seg.x1, seg.y1, seg.x2, seg.y2]}
                                    stroke={stroke}
                                    strokeWidth={segStrokeWidth}
                                    lineCap="round"
                                    lineJoin="round"
                                    dash={base.dash}
                                    opacity={0.95}
                                    listening={false}
                                  />
                                  {(isSegHovered || isSegSelected) && (
                                    <>
                                      <Circle
                                        x={seg.x1}
                                        y={seg.y1}
                                        radius={6 / scale}
                                        fill={stroke}
                                        stroke="white"
                                        strokeWidth={2 / scale}
                                        listening={false}
                                      />
                                      <Circle
                                        x={seg.x2}
                                        y={seg.y2}
                                        radius={6 / scale}
                                        fill={stroke}
                                        stroke="white"
                                        strokeWidth={2 / scale}
                                        listening={false}
                                      />
                                    </>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </>
                        )}

                        <Group>
                          <Circle
                            name="wizard-mark node-line"
                            x={marker.x}
                            y={marker.y}
                            radius={10 / scale}
                            fill={markerStyle.fill}
                            stroke={markerStyle.stroke}
                            strokeWidth={2 / scale}
                            opacity={0.95}
                            listening={marksTakeClicks && !isNodeMarkingMode && !isPanning}
                            onMouseEnter={() =>
                              setHoveredItem({
                                type: 'node',
                                id: node.id,
                                tag: `Node #${node.node_no}`,
                                itemType: `${node.line_no}\n${node.source_ref} → ${node.dest_ref}`,
                                lineNo: node.line_no,
                                bbox,
                              })
                            }
                            onMouseLeave={() => setHoveredItem(null)}
                            onClick={(e) => {
                              if (isNodeMarkingMode || !canSelectMark()) return;
                              e.cancelBubble = true;
                              onSelectWizardItem?.({
                                kind: 'node',
                                id: node.id,
                                screenPosition: getPointerScreenPosition(),
                                isMultiSelect: e.evt.shiftKey || e.evt.metaKey || e.evt.ctrlKey,
                              });
                            }}
                          />
                          <Text
                            x={marker.x}
                            y={marker.y}
                            text={`#${node.node_no}`}
                            offsetX={(16 / 2) / scale}
                            offsetY={(10 / 2) / scale}
                            fontSize={10 / scale}
                            fontStyle="bold"
                            fill="white"
                            listening={false}
                          />
                        </Group>
                      </React.Fragment>
                    );
                  }

                  // Fallback: render nothing for nodes without valid polyline/segments.
                  return null;
                })}

                {/* Node Instruments - only render marked ones with bbox */}
                {nodeInstruments
                  .filter(inst => instrumentRegions(inst).length > 0)
                  .filter(inst => attachedInstrumentOverlayVisible(
                    activeAssetKind,
                    isolating,
                    'line',
                    getInstrumentEffectiveContext(inst, pnidState).context_id === activeNodeId,
                  ))
                  .filter(inst => {
                    const effectiveContext = getInstrumentEffectiveContext(inst, pnidState);
                    const force =
                      inst.id === selectedInstrumentId ||
                      inst.id === highlightedEntityId ||
                      effectiveContext.context_id === activeNodeId;
                    // Per region: a balloon drawn across the sheet from its symbol still
                    // draws when only the balloon is on screen.
                    return instrumentRegions(inst).some((region) => isInViewport(region, force));
                  })
                  .map((inst) => {
                  const effectiveContext = getInstrumentEffectiveContext(inst, pnidState);
                  const node = effectiveContext.context_id ? nodesById.get(effectiveContext.context_id) : undefined;
                  // A line's instruments are active with their parent.
                  const isActive = activeAssetKind === 'line' && node?.id === activeNodeId;
                  const isSelected = inst.id === selectedInstrumentId;
                  const isBulkSelected = selectedWizardItemKeys?.has(wizardCanvasItemKey({ kind: 'nodeInstrument', id: inst.id })) ?? false;
                  const isHighlighted = inst.id === highlightedEntityId;
                  const regions = instrumentRegions(inst);
                  // Region 0 is the symbol: the branch marker and the delete badge anchor on it.
                  const symbolRegion = regions[0];
                  const isHovered = hoveredItem?.id === inst.id && hoveredItem?.type === 'node_instrument';
                  const isBranch = Boolean(inst.branch_ref || inst.branch_direction);
                  const centerX = (symbolRegion.x1 + symbolRegion.x2) / 2;
                  const centerY = (symbolRegion.y1 + symbolRegion.y2) / 2;
                  const reviewed = inst.status === 'final_and_reviewed';
                  const inactive = wizardInactivePalette('instrument', reviewed);
                  const baseStroke = isSelected || isBulkSelected
                    ? (reviewed ? '#15803d' : '#7c3aed')
                    : (isActive || isHighlighted ? '#2563eb' : inactive.stroke);
                  const hoverStroke = isSelected || isBulkSelected
                    ? (reviewed ? '#166534' : '#6d28d9')
                    : (isActive || isHighlighted ? '#1d4ed8' : inactive.hoverStroke);
                  const baseFill = isSelected || isBulkSelected
                    ? (reviewed ? 'rgba(21, 128, 61, 0.2)' : 'rgba(124, 58, 237, 0.18)')
                    : (isActive || isHighlighted ? 'rgba(37, 99, 235, 0.15)' : inactive.fill);
                  const hoverFill = isSelected || isBulkSelected
                    ? (reviewed ? 'rgba(21, 128, 61, 0.3)' : 'rgba(124, 58, 237, 0.28)')
                    : (isActive || isHighlighted ? 'rgba(37, 99, 235, 0.25)' : inactive.hoverFill);
                  const hitPadding = 4 / scale;
                  const shouldTrackHover = !suppressHoverInspection;

                  if (isBranch) {
                    return (
                      <React.Fragment key={`node-inst-${inst.id}`}>
                        <Circle
                          x={centerX}
                          y={centerY}
                          radius={(isHovered || isActive || isSelected || isBulkSelected || isHighlighted ? 10 : 7) / scale}
                          fill={isHovered ? hoverFill : baseFill}
                          stroke={isHovered ? hoverStroke : baseStroke}
                          strokeWidth={(isHovered || isActive || isSelected || isBulkSelected || isHighlighted ? 3 : 2) / scale}
                          opacity={isActive || isSelected || isBulkSelected || isHighlighted ? 1 : 0.85}
                          listening={false}
                          name="wizard-mark node-instrument"
                          perfectDrawEnabled={false}
                          shadowForStrokeEnabled={false}
                        />
                        <Circle
                          x={centerX}
                          y={centerY}
                          radius={10 / scale + hitPadding}
                          fill="transparent"
                          listening={Boolean(onSelectWizardItem || onRightClickInstrument)}
                          name="wizard-mark node-instrument"
                          perfectDrawEnabled={false}
                          shadowForStrokeEnabled={false}
                          onMouseDown={(e) => {
                            if (drawMode === 'select' || e.evt.button !== 0) {
                              e.cancelBubble = true;
                            }
                          }}
                          onClick={(e) => {
                            if (!canSelectMark()) return;
                            e.cancelBubble = true;
                            onSelectWizardItem?.({
                              kind: 'nodeInstrument',
                              id: inst.id,
                              screenPosition: getPointerScreenPosition(),
                              isMultiSelect: e.evt.shiftKey || e.evt.metaKey || e.evt.ctrlKey,
                            });
                          }}
                          onContextMenu={(e) => {
                            e.evt.preventDefault();
                            e.cancelBubble = true;
                            if (e.evt.metaKey || e.evt.ctrlKey || e.evt.shiftKey || e.evt.altKey) return;
                            onRightClickInstrument?.(inst.id, { x: e.evt.clientX, y: e.evt.clientY });
                          }}
                          onMouseEnter={() => {
                            if (!shouldTrackHover) return;
                            cancelHoverClear();
                            setHoveredItem({
                              type: 'node_instrument',
                              id: inst.id,
                              tag: getInstrumentPrimaryLabel(inst),
                              itemType: inst.instrument_type ? getInstrumentCategoryName(inst.instrument_type) : 'Branch',
                              bbox: symbolRegion,
                            });
                          }}
                          onMouseLeave={() => {
                            if (!shouldTrackHover) return;
                            scheduleHoverClear();
                          }}
                        />
                        {/* Balloons and captions drawn alongside the branch marker. */}
                        {regions.slice(1).map((region, offset) => (
                          <Rect
                            key={`node-inst-${inst.id}-region-${offset + 1}`}
                            x={region.x1}
                            y={region.y1}
                            width={region.x2 - region.x1}
                            height={region.y2 - region.y1}
                            stroke={isHovered ? hoverStroke : baseStroke}
                            strokeWidth={(isHovered || isActive || isSelected || isBulkSelected || isHighlighted ? 3 : 2) / scale}
                            fill={isHovered ? hoverFill : baseFill}
                            opacity={isActive || isSelected || isBulkSelected || isHighlighted ? 1 : 0.85}
                            dash={isActive || isHovered || isSelected || isBulkSelected || isHighlighted ? undefined : [4 / scale, 2 / scale]}
                            listening={false}
                            name="wizard-mark node-instrument"
                            perfectDrawEnabled={false}
                            shadowForStrokeEnabled={false}
                          />
                        ))}
                        {(isSelected || isBulkSelected) && regions.map((region, regionIndex) => (
                          <React.Fragment key={`node-inst-${inst.id}-handles-${regionIndex}`}>
                            {renderPersistedBboxHandles(
                              { kind: 'nodeInstrument', id: inst.id },
                              region,
                              isSelected || isBulkSelected ? '#7c3aed' : '#2563eb',
                              { regionIndex },
                            )}
                          </React.Fragment>
                        ))}
                        {(isSelected || isBulkSelected || isHovered) && renderQuickDeleteBadge(
                          { kind: 'nodeInstrument', id: inst.id },
                          symbolRegion,
                        )}
                      </React.Fragment>
                    );
                  }
                  return (
                    <React.Fragment key={`node-inst-${inst.id}`}>
                      {/* One rectangle per region, all sharing the marking's hover and
                          selection state, so pointing at the balloon lights the symbol too. */}
                      {regions.map((region, regionIndex) => (
                        <React.Fragment key={`node-inst-${inst.id}-region-${regionIndex}`}>
                          {regions.length > 1 && (
                            <RegionIndexChip
                              region={region}
                              index={regionIndex}
                              color={isHovered ? hoverStroke : baseStroke}
                              scale={scale}
                            />
                          )}
                          <Rect
                            x={region.x1}
                            y={region.y1}
                            width={region.x2 - region.x1}
                            height={region.y2 - region.y1}
                            stroke={isHovered ? hoverStroke : baseStroke}
                            strokeWidth={(isHovered || isActive || isSelected || isBulkSelected || isHighlighted ? 3 : 2) / scale}
                            fill={isHovered ? hoverFill : baseFill}
                            opacity={isActive || isSelected || isBulkSelected || isHighlighted ? 1 : 0.85}
                            dash={isActive || isHovered || isSelected || isBulkSelected || isHighlighted ? undefined : [4 / scale, 2 / scale]}
                            listening={false}
                            name="wizard-mark node-instrument"
                            perfectDrawEnabled={false}
                            shadowForStrokeEnabled={false}
                          />
                          <Rect
                            x={region.x1 - hitPadding}
                            y={region.y1 - hitPadding}
                            width={region.x2 - region.x1 + hitPadding * 2}
                            height={region.y2 - region.y1 + hitPadding * 2}
                            fill="transparent"
                            listening={Boolean(onSelectWizardItem || onRightClickInstrument)}
                            name="wizard-mark node-instrument"
                            perfectDrawEnabled={false}
                            shadowForStrokeEnabled={false}
                            onMouseDown={(e) => {
                              if (drawMode === 'select' || e.evt.button !== 0) {
                                e.cancelBubble = true;
                              }
                            }}
                            onClick={(e) => {
                              if (!canSelectMark()) return;
                              e.cancelBubble = true;
                              onSelectWizardItem?.({
                                kind: 'nodeInstrument',
                                id: inst.id,
                                screenPosition: getPointerScreenPosition(),
                                isMultiSelect: e.evt.shiftKey || e.evt.metaKey || e.evt.ctrlKey,
                              });
                            }}
                            onContextMenu={(e) => {
                              e.evt.preventDefault();
                              e.cancelBubble = true;
                              if (e.evt.metaKey || e.evt.ctrlKey || e.evt.shiftKey || e.evt.altKey) return;
                              onRightClickInstrument?.(inst.id, { x: e.evt.clientX, y: e.evt.clientY });
                            }}
                            onMouseEnter={() => {
                              if (!shouldTrackHover) return;
                              cancelHoverClear();
                              setHoveredItem({
                                type: 'node_instrument',
                                id: inst.id,
                                tag: getInstrumentPrimaryLabel(inst),
                                itemType: inst.instrument_type ? getInstrumentCategoryName(inst.instrument_type) : 'Instrument',
                                // The region under the cursor, so the tooltip lands beside
                                // what the SME is actually pointing at.
                                bbox: region,
                              });
                            }}
                            onMouseLeave={() => {
                              if (!shouldTrackHover) return;
                              scheduleHoverClear();
                            }}
                          />
                          {(isSelected || isBulkSelected) && renderPersistedBboxHandles(
                            { kind: 'nodeInstrument', id: inst.id },
                            region,
                            isSelected || isBulkSelected ? '#7c3aed' : '#2563eb',
                            { regionIndex },
                          )}
                        </React.Fragment>
                      ))}
                      {/* The badge deletes the marking, not a region, so it sits on the symbol alone. */}
                      {(isSelected || isBulkSelected || isHovered) && renderQuickDeleteBadge(
                        { kind: 'nodeInstrument', id: inst.id },
                        symbolRegion,
                      )}
                    </React.Fragment>
                  );
                })}

                {/* Control loops - render imported/marked loop bounding boxes */}
                {pnidState.control_loops
                  .filter((loop) => loop.bbox)
                  .filter((loop) => {
                    const force = loop.id === highlightedEntityId;
                    return isInViewport(loop.bbox, force);
                  })
                  .map((loop) => {
                    const bbox = loop.bbox!;
                    const isBulkSelected = selectedWizardItemKeys?.has(wizardCanvasItemKey({ kind: 'controlLoop', id: loop.id })) ?? false;
                    const isHovered = hoveredItem?.id === loop.id && hoveredItem?.type === 'control_loop';
                    const strokeColor = isBulkSelected || isHovered ? '#7c3aed' : '#9333ea';
                    const fillColor = isBulkSelected || isHovered ? 'rgba(124, 58, 237, 0.16)' : 'rgba(147, 51, 234, 0.08)';
                    return (
                      <React.Fragment key={`control-loop-${loop.id}`}>
                        <Rect
                          x={bbox.x1}
                          y={bbox.y1}
                          width={bbox.x2 - bbox.x1}
                          height={bbox.y2 - bbox.y1}
                          stroke={strokeColor}
                          strokeWidth={(isBulkSelected || isHovered ? 3 : 2) / scale}
                          fill={fillColor}
                          dash={isBulkSelected ? undefined : [5 / scale, 3 / scale]}
                          opacity={isBulkSelected || isHovered ? 1 : 0.85}
                          listening={Boolean(onSelectWizardItem)}
                          name="wizard-mark control-loop"
                          onClick={(e) => {
                            if (!canSelectMark()) return;
                            e.cancelBubble = true;
                            onSelectWizardItem?.({
                              kind: 'controlLoop',
                              id: loop.id,
                              screenPosition: getPointerScreenPosition(),
                              isMultiSelect: e.evt.shiftKey || e.evt.metaKey || e.evt.ctrlKey,
                            });
                          }}
                          onMouseEnter={() => {
                            cancelHoverClear();
                            setHoveredItem({
                              type: 'control_loop',
                              id: loop.id,
                              tag: loop.loop_tag,
                              itemType: 'Control loop',
                              bbox,
                            });
                          }}
                          onMouseLeave={scheduleHoverClear}
                        />
                        {isBulkSelected && renderPersistedBboxHandles(
                          { kind: 'controlLoop', id: loop.id },
                          bbox,
                          '#7c3aed',
                        )}
                      </React.Fragment>
                    );
                  })}

                {/* Node point candidates (inline selection) */}
                {nodePointCandidates.map((candidate) => {
                  const isSelected = candidate.id === selectedCandidateId;
                  const statusColor = candidate.status === 'error'
                    ? '#dc2626'
                    : candidate.status === 'running'
                      ? '#2563eb'
                      : '#f59e0b';
                  const fillColor = candidate.status === 'ready'
                    ? 'rgba(245, 158, 11, 0.25)'
                    : candidate.status === 'running'
                      ? 'rgba(37, 99, 235, 0.2)'
                      : 'rgba(220, 38, 38, 0.2)';
                  const radius = (isSelected ? 7 : 5) / scale;
                  const menuWidth = 190 / scale;
                  const rowHeight = 22 / scale;
                  const menuPadding = 6 / scale;
                  const options = [...(candidate.options || [])].sort((a, b) => b.score - a.score).slice(0, 3);
                  const rows = [
                    { key: 'ignore', label: 'Ignore (default)', score: null, value: 'ignore' as const },
                    ...options.map((option, idx) => ({
                      key: `opt-${idx}`,
                      label:
                        option.label === 'Branch'
                          ? (candidate.branchLabel ? `Branch to ${candidate.branchLabel}` : 'Branch')
                          : getInstrumentCategoryName(option.label as InstrumentCategory),
                      score: option.score,
                      value: option.label,
                    })),
                  ];
                  const menuHeight = rows.length * rowHeight + menuPadding * 2;
                  const menuX = candidate.point.x + 12 / scale;
                  const menuY = candidate.point.y - menuHeight / 2;

                  return (
                    <React.Fragment key={candidate.id}>
                      {isSelected && candidate.bbox && (
                        <Rect
                          x={candidate.bbox.x1}
                          y={candidate.bbox.y1}
                          width={candidate.bbox.x2 - candidate.bbox.x1}
                          height={candidate.bbox.y2 - candidate.bbox.y1}
                          stroke={statusColor}
                          strokeWidth={2 / scale}
                          dash={[6 / scale, 3 / scale]}
                          fill="rgba(255,255,255,0.0)"
                          listening={false}
                        />
                      )}
                      <Circle
                        name="node-point-candidate"
                        x={candidate.point.x}
                        y={candidate.point.y}
                        radius={radius}
                        fill={fillColor}
                        stroke={statusColor}
                        strokeWidth={2 / scale}
                        listening={Boolean(onResolveNodePointCandidate)}
                        onMouseDown={(e) => {
                          if (!onResolveNodePointCandidate) return;
                          e.cancelBubble = true;
                        }}
                        onClick={(e) => {
                          if (!onResolveNodePointCandidate) return;
                          e.cancelBubble = true;
                          setSelectedCandidateId((prev) => (prev === candidate.id ? null : candidate.id));
                        }}
                      />
                      {candidate.status === 'running' && (
                        <Text
                          x={candidate.point.x + 10 / scale}
                          y={candidate.point.y - 10 / scale}
                          text="Identifying..."
                          fontSize={10 / scale}
                          fill="#2563eb"
                          listening={false}
                        />
                      )}
                      {isSelected && onResolveNodePointCandidate && candidate.status !== 'running' && (
                        <Group x={menuX} y={menuY}>
                          <Rect
                            width={menuWidth}
                            height={menuHeight}
                            fill="white"
                            stroke="#e5e7eb"
                            strokeWidth={1 / scale}
                            cornerRadius={6 / scale}
                            shadowColor="black"
                            shadowBlur={6 / scale}
                            shadowOffsetX={1 / scale}
                            shadowOffsetY={1 / scale}
                            shadowOpacity={0.12}
                            onMouseDown={(e) => {
                              e.cancelBubble = true;
                            }}
                          />
                          {rows.map((row, idx) => {
                            const rowY = menuPadding + idx * rowHeight;
                            const rowWidth = menuWidth - menuPadding * 2;
                            const scoreText = row.score == null ? '' : `${Math.round(row.score * 100)}%`;
                            const isIgnore = row.value === 'ignore';
                            const rowColor = isIgnore ? 'var(--raven-text-secondary)' : '#111827';
                            return (
                              <Group key={row.key} x={menuPadding} y={rowY}>
                                <Rect
                                  width={rowWidth}
                                  height={rowHeight - 2 / scale}
                                  fill={isIgnore ? '#f3f4f6' : '#f9fafb'}
                                  cornerRadius={4 / scale}
                                  listening
                                  onMouseDown={(e) => {
                                    e.cancelBubble = true;
                                  }}
                                  onClick={(e) => {
                                    e.cancelBubble = true;
                                    onResolveNodePointCandidate(
                                      candidate.id,
                                      row.value === 'ignore' ? 'ignore' : (row.value as NodePointCandidateLabel)
                                    );
                                    setSelectedCandidateId(null);
                                  }}
                                />
                                <Text
                                  text={row.label}
                                  x={6 / scale}
                                  y={4 / scale}
                                  fontSize={11 / scale}
                                  fill={rowColor}
                                  width={rowWidth - 12 / scale}
                                  align="left"
                                  listening={false}
                                />
                                {scoreText && (
                                  <Text
                                    text={scoreText}
                                    x={6 / scale}
                                    y={4 / scale}
                                    fontSize={10 / scale}
                                    fill="var(--raven-text-secondary)"
                                    width={rowWidth - 12 / scale}
                                    align="right"
                                    listening={false}
                                  />
                                )}
                              </Group>
                            );
                          })}
                        </Group>
                      )}
                    </React.Fragment>
                  );
                })}

                {/* Focus highlight: marks where a navigation landed while its flash runs. */}
                {focusRequest && focusFlashElapsedMs < FOCUS_FLASH_DURATION_MS && (() => {
                  const target = focusRequest.bbox;
                  const t = Math.max(0, Math.min(1, 1 - focusFlashElapsedMs / FOCUS_FLASH_DURATION_MS)); // 1 -> 0
                  const stroke = '#7c3aed'; // vlink-ish violet
                  const dashOffset = -(focusFlashElapsedMs * 0.06) / Math.max(1e-6, scale);

                  // Keep highlight OUTSIDE the target so it doesn't cover text.
                  // `pad` is in image coords so it stays constant in screen pixels.
                  const pad = 10 / Math.max(1e-6, scale);
                  const wImg = Math.max(1, target.x2 - target.x1);
                  const hImg = Math.max(1, target.y2 - target.y1);
                  const x = target.x1 - pad;
                  const y = target.y1 - pad;
                  const w = wImg + pad * 2;
                  const h = hImg + pad * 2;

                  const cx = target.x1 + wImg / 2;
                  const cy = target.y1 + hImg / 2;
                  const pingR = (14 + 26 * (1 - t)) / scale;
                  const bracket = 16 / Math.max(1e-6, scale);

                  return (
                    <>
                      <Rect
                        x={x}
                        y={y}
                        width={w}
                        height={h}
                        stroke={stroke}
                        strokeWidth={(t > 0 ? 3.25 : 2.25) / scale}
                        dash={[10 / scale, 6 / scale]}
                        dashOffset={dashOffset}
                        shadowColor={stroke}
                        shadowBlur={(t > 0 ? 18 * t : 0) / scale}
                        shadowOpacity={t > 0 ? 0.55 * t : 0}
                        listening={false}
                        name="focus-highlight"
                      />
                      {/* Corner brackets (stroke-only, does not obscure text) */}
                      <Line
                        points={[x, y + bracket, x, y, x + bracket, y]}
                        stroke={stroke}
                        strokeWidth={2.4 / scale}
                        lineCap="round"
                        lineJoin="round"
                        listening={false}
                        opacity={0.95}
                      />
                      <Line
                        points={[x + w - bracket, y, x + w, y, x + w, y + bracket]}
                        stroke={stroke}
                        strokeWidth={2.4 / scale}
                        lineCap="round"
                        lineJoin="round"
                        listening={false}
                        opacity={0.95}
                      />
                      <Line
                        points={[x, y + h - bracket, x, y + h, x + bracket, y + h]}
                        stroke={stroke}
                        strokeWidth={2.4 / scale}
                        lineCap="round"
                        lineJoin="round"
                        listening={false}
                        opacity={0.95}
                      />
                      <Line
                        points={[x + w - bracket, y + h, x + w, y + h, x + w, y + h - bracket]}
                        stroke={stroke}
                        strokeWidth={2.4 / scale}
                        lineCap="round"
                        lineJoin="round"
                        listening={false}
                        opacity={0.95}
                      />
                      {t > 0 && (
                        <Circle
                          x={cx}
                          y={cy}
                          radius={pingR}
                          stroke={stroke}
                          strokeWidth={2.2 / scale}
                          opacity={0.55 * t}
                          listening={false}
                        />
                      )}
                    </>
                  );
                })()}

                {/* Hover tooltip for marked items */}
                {hoveredItem && !suppressHoverInspection && (
                  <Group
                    x={hoveredItem.bbox.x2 + 8 / scale}
                    y={hoveredItem.bbox.y1}
                  >
                    <Label>
                      <Tag
                        fill="#1f2937"
                        cornerRadius={4 / scale}
                        shadowColor="black"
                        shadowBlur={4}
                        shadowOffsetX={1}
                        shadowOffsetY={1}
                        shadowOpacity={0.3}
                      />
                      <Text
                        text={`${hoveredItem.tag}\n${hoveredItem.itemType}`}
                        fontSize={12 / scale}
                        fontFamily="system-ui, -apple-system, sans-serif"
                        fill="white"
                        padding={6 / scale}
                        lineHeight={1.4}
                      />
                    </Label>
                  </Group>
                )}

                {/* Hover line details near cursor (node + line no + refs) */}
                {hoveredItem?.type === 'node' && cursorPosition && !suppressHoverInspection && (
                  <Group
                    x={cursorPosition.x + 14 / Math.max(1e-6, scale)}
                    y={cursorPosition.y + 14 / Math.max(1e-6, scale)}
                    listening={false}
                  >
                    <Label opacity={0.98}>
                      <Tag
                        fill="rgba(17, 24, 39, 0.92)"
                        cornerRadius={6 / scale}
                        shadowColor="black"
                        shadowBlur={6}
                        shadowOffsetX={1}
                        shadowOffsetY={1}
                        shadowOpacity={0.25}
                      />
                      <Text
                        text={(() => {
                          const lineNo = (hoveredItem.lineNo || '').trim();
                          const header = lineNo ? `${hoveredItem.tag} · ${lineNo}` : hoveredItem.tag;
                          const parts = (hoveredItem.itemType || '').split('\n').map((s) => s.trim()).filter(Boolean);
                          // itemType for nodes is "line_no\nsource → dest" — show only the ref line below header.
                          const refLine =
                            parts.length >= 2 ? parts.slice(1).join(' · ') :
                            (parts.length === 1 && parts[0] !== lineNo ? parts[0] : '');
                          return refLine ? `${header}\n${refLine}` : header;
                        })()}
                        fontSize={12 / scale}
                        fontStyle="bold"
                        fontFamily="system-ui, -apple-system, sans-serif"
                        fill="#fde047"
                        padding={6 / scale}
                        lineHeight={1.25}
                      />
                    </Label>
                  </Group>
                )}
              </Group>
            )}

            {/* Every region of the marking being identified, waiting on one answer */}
            {identifyingBboxes.map((identifying, index) => (
              <Group key={`identifying-${index}`}>
                <Rect
                  x={identifying.x1}
                  y={identifying.y1}
                  width={identifying.x2 - identifying.x1}
                  height={identifying.y2 - identifying.y1}
                  stroke="#2563eb"
                  strokeWidth={3 / scale}
                  dash={[8 / scale, 4 / scale]}
                  fill="rgba(37, 99, 235, 0.15)"
                  listening={false}
                />
                {/* Loading label */}
                <Label
                  x={(identifying.x1 + identifying.x2) / 2}
                  y={(identifying.y1 + identifying.y2) / 2}
                  offsetX={50}
                  offsetY={12}
                >
                  <Tag
                    fill="#2563eb"
                    cornerRadius={4}
                    pointerDirection="none"
                  />
                  <Text
                    text="🔍 Identifying..."
                    fontSize={14 / scale}
                    fontStyle="bold"
                    fill="white"
                    padding={6 / scale}
                  />
                </Label>
              </Group>
            ))}

            {/* Comment bubbles */}
            {comments
              .filter(c => showResolvedComments || !c.resolved)
              .filter(c => {
                const force = c.id === selectedCommentId;
                return isInViewport(rectBounds(c.x - 48, c.y - 48, 96, 96), force);
              })
              .map(comment => (
                <CommentBubble
                  key={comment.id}
                  comment={comment}
                  scale={scale}
                  isSelected={selectedCommentId === comment.id}
                  onClick={() => {
                    if (onSelectComment) {
                      // Get screen coordinates from the click position
                      const stage = stageRef.current;
                      const container = stage?.container();
                      if (container) {
                        const rect = container.getBoundingClientRect();
                        // Use the comment's canvas position and current stage transform to screen coords
                        const currentScale = scaleRef.current;
                        const currentPosition = positionRef.current;
                        const screenX = rect.left + (comment.x * currentScale + currentPosition.x);
                        const screenY = rect.top + (comment.y * currentScale + currentPosition.y);
                        onSelectComment(comment.id, screenX, screenY);
                      }
                    }
                  }}
                />
              ))}

            {annotating &&
              selectedAnnotationBounds &&
              selectedWizardItemKeys?.size &&
              shouldRenderSelectedShiftBounds &&
              marksTakeClicks &&
              !drawingBox &&
              onShiftSelectedWizardAnnotations && (
                <Rect
                  x={selectedAnnotationBounds.x1 + selectedAnnotationShiftPreview.dx}
                  y={selectedAnnotationBounds.y1 + selectedAnnotationShiftPreview.dy}
                  width={Math.max(1, selectedAnnotationBounds.x2 - selectedAnnotationBounds.x1)}
                  height={Math.max(1, selectedAnnotationBounds.y2 - selectedAnnotationBounds.y1)}
                  stroke={isSelectedAnnotationShiftSaving ? '#f59e0b' : '#1d4ed8'}
                  strokeWidth={2 / scale}
                  dash={[10 / scale, 6 / scale]}
                  // The outline is the move handle, and the paper inside takes a new box.
                  hitStrokeWidth={8 / scale}
                  fillEnabled={false}
                  draggable={!isSpaceHeld && !isSelectedAnnotationShiftSaving}
                  name="selected-annotation-shift-bounds"
                  onMouseDown={(e) => {
                    if (isSpaceHeld || isSelectedAnnotationShiftSaving) return;
                    e.cancelBubble = true;
                  }}
                  onDragStart={(e) => {
                    e.cancelBubble = true;
                    setHoveredItem(null);
                  }}
                  onDragMove={(e) => {
                    e.cancelBubble = true;
                    const node = e.target;
                    setSelectedAnnotationShiftPreview({
                      dx: node.x() - selectedAnnotationBounds.x1,
                      dy: node.y() - selectedAnnotationBounds.y1,
                    });
                  }}
                  onDragEnd={async (e) => {
                    e.cancelBubble = true;
                    const node = e.target;
                    const dx = node.x() - selectedAnnotationBounds.x1;
                    const dy = node.y() - selectedAnnotationBounds.y1;
                    if (Math.abs(dx) < 0.1 && Math.abs(dy) < 0.1) {
                      setSelectedAnnotationShiftPreview({ dx: 0, dy: 0 });
                      return;
                    }
                    setIsSelectedAnnotationShiftSaving(true);
                    try {
                      await onShiftSelectedWizardAnnotations(
                        { dx, dy },
                        { lineSegmentScale: segScale }
                      );
                      setSelectedAnnotationShiftPreview({ dx: 0, dy: 0 });
                    } catch (error) {
                      console.error('Failed to shift selected annotations:', error);
                      setSelectedAnnotationShiftPreview({ dx: 0, dy: 0 });
                    } finally {
                      setIsSelectedAnnotationShiftSaving(false);
                    }
                  }}
                />
              )}

            {annotating && annotationBounds && drawMode === 'shift-annotations' && onShiftWizardAnnotations && (
              <Rect
                x={annotationBounds.x1 + annotationShiftPreview.dx}
                y={annotationBounds.y1 + annotationShiftPreview.dy}
                width={Math.max(1, annotationBounds.x2 - annotationBounds.x1)}
                height={Math.max(1, annotationBounds.y2 - annotationBounds.y1)}
                stroke={isAnnotationShiftSaving ? '#f59e0b' : '#111827'}
                strokeWidth={2 / scale}
                dash={[12 / scale, 8 / scale]}
                fill="rgba(17, 24, 39, 0.04)"
                draggable={!isSpaceHeld && !isAnnotationShiftSaving}
                name="annotation-shift-bounds"
                onMouseDown={(e) => {
                  if (isSpaceHeld || isAnnotationShiftSaving) return;
                  e.cancelBubble = true;
                }}
                onDragStart={(e) => {
                  e.cancelBubble = true;
                  setHoveredItem(null);
                }}
                onDragMove={(e) => {
                  e.cancelBubble = true;
                  const node = e.target;
                  setAnnotationShiftPreview({
                    dx: node.x() - annotationBounds.x1,
                    dy: node.y() - annotationBounds.y1,
                  });
                }}
                onDragEnd={async (e) => {
                  e.cancelBubble = true;
                  const node = e.target;
                  const dx = node.x() - annotationBounds.x1;
                  const dy = node.y() - annotationBounds.y1;
                  if (Math.abs(dx) < 0.1 && Math.abs(dy) < 0.1) {
                    setAnnotationShiftPreview({ dx: 0, dy: 0 });
                    return;
                  }
                  setIsAnnotationShiftSaving(true);
                  try {
                    await onShiftWizardAnnotations(
                      { dx, dy },
                      { lineSegmentScale: segScale }
                    );
                    setAnnotationShiftPreview({ dx: 0, dy: 0 });
                  } catch (error) {
                    console.error('Failed to shift annotations:', error);
                    setAnnotationShiftPreview({ dx: 0, dy: 0 });
                  } finally {
                    setIsAnnotationShiftSaving(false);
                  }
                }}
              />
            )}

            {/*
              The click targets while an instrument marking is armed or a dock field
              takes a copy, drawn at every scale and culled to the viewport: a thin dashed outline each, quiet enough
              to leave the sheet readable under a thousand of them. They go before the
              draft so its numbered regions sit on top. The one under the pointer draws
              solid and heavier, with its label. Nothing here listens: hover is a hit
              test on the pointer in handleMouseMove, not on the scene graph.
            */}
            {clickTargetsInViewport.map(({ id, bbox }) => (
              <Rect
                key={`click-target-${id}`}
                name="click-target"
                x={bbox.x1}
                y={bbox.y1}
                width={bbox.x2 - bbox.x1}
                height={bbox.y2 - bbox.y1}
                stroke={clickTargetColors.resting}
                strokeWidth={1 / scale}
                dash={[4 / scale, 3 / scale]}
                listening={false}
                perfectDrawEnabled={false}
                shadowForStrokeEnabled={false}
              />
            ))}
            {hoveredClickTarget && (
              <Group
                listening={false}
                x={hoveredClickTarget.bbox.x1}
                y={hoveredClickTarget.bbox.y1}
              >
                {!reducePassiveOverlays && (
                  <Rect
                    width={hoveredClickTarget.bbox.x2 - hoveredClickTarget.bbox.x1}
                    height={hoveredClickTarget.bbox.y2 - hoveredClickTarget.bbox.y1}
                    fill={clickTargetColors.hovered}
                    opacity={0.08}
                    perfectDrawEnabled={false}
                  />
                )}
                <Rect
                  name="hovered-click-target"
                  width={hoveredClickTarget.bbox.x2 - hoveredClickTarget.bbox.x1}
                  height={hoveredClickTarget.bbox.y2 - hoveredClickTarget.bbox.y1}
                  stroke={clickTargetColors.hovered}
                  strokeWidth={2 / scale}
                  perfectDrawEnabled={false}
                  shadowForStrokeEnabled={false}
                />
              </Group>
            )}
            {hoveredClickTargetLabel && !reducePassiveOverlays && (
              <Label
                name="click-target-label"
                listening={false}
                x={hoveredClickTargetLabel.x}
                y={hoveredClickTargetLabel.y}
              >
                <Tag fill={clickTargetColors.hovered} cornerRadius={4 / scale} />
                <Text
                  text={hoveredClickTargetLabel.text}
                  {...clickTargetLabelFont(scale)}
                  width={hoveredClickTargetLabel.truncatedWidth}
                  fill={clickTargetColors.labelText}
                />
              </Label>
            )}

            {/*
              The collected draft. Region 1 is the anchor — the symbol every downstream
              reader sees and the box the distance rules measure from — so it carries the
              primary blue and a heavier stroke while the rest sit back in the secondary
              tone. Culling is per region: a balloon drawn across the sheet from its
              symbol still draws when only it is on screen.
            */}
            {draftRegions
              .map((region, index) => ({ region, index }))
              .filter(({ region }) => isInViewport(region))
              .map(({ region, index }) => {
                const isAnchor = index === 0;
                const stroke = isAnchor ? '#2563eb' : '#60a5fa';
                const fill = isAnchor ? 'rgba(37, 99, 235, 0.15)' : 'rgba(59, 130, 246, 0.08)';
                return (
                  <React.Fragment key={`draft-region-${index}`}>
                    <Rect
                      x={region.x1}
                      y={region.y1}
                      width={region.x2 - region.x1}
                      height={region.y2 - region.y1}
                      stroke={stroke}
                      strokeWidth={(isAnchor ? 4 : 2) / scale}
                      dash={[8 / scale, 4 / scale]}
                      fill={fill}
                      listening={false}
                      perfectDrawEnabled={false}
                      shadowForStrokeEnabled={false}
                    />
                    <RegionIndexChip
                      region={region}
                      index={index}
                      color={stroke}
                      scale={scale}
                    />
                  </React.Fragment>
                );
              })}

          </Layer>
          {/* Its own layer, so the cut-outs erase the dim and not the sheet image under it. */}
          {focusOverlay && imageCanvasWidth && imageCanvasHeight && (
            <Layer listening={false}>
              <Rect x={0} y={0} width={imageCanvasWidth} height={imageCanvasHeight} fill={FOCUS_OVERLAY_DIM_FILL} />
              {focusOverlay.boxes.map((box, index) => (
                <Rect
                  key={`focus-clear-box-${index}`}
                  x={box.x1}
                  y={box.y1}
                  width={box.x2 - box.x1}
                  height={box.y2 - box.y1}
                  fill="#000"
                  globalCompositeOperation="destination-out"
                />
              ))}
              {focusOverlay.polylines.map((points, index) => (
                <Line
                  key={`focus-clear-path-${index}`}
                  points={points}
                  stroke="#000"
                  strokeWidth={FOCUS_OVERLAY_PATH_CLEARANCE}
                  lineCap="round"
                  lineJoin="round"
                  globalCompositeOperation="destination-out"
                />
              ))}
              {focusOverlay.boxes.map((box, index) => (
                <Rect
                  key={`focus-box-${index}`}
                  x={box.x1}
                  y={box.y1}
                  width={box.x2 - box.x1}
                  height={box.y2 - box.y1}
                  stroke={focusOverlayStroke}
                  strokeWidth={3 / scale}
                />
              ))}
              {focusOverlay.polylines.map((points, index) => (
                <Line
                  key={`focus-path-${index}`}
                  points={points}
                  stroke={focusOverlayStroke}
                  strokeWidth={4 / scale}
                  opacity={0.7}
                  lineCap="round"
                  lineJoin="round"
                />
              ))}
            </Layer>
          )}
        </Stage>}

        {/*
          The collecting pill is HTML rather than a Konva shape so the keys can be
          real <kbd> elements: they are the content, and a canvas-drawn imitation
          reads as decoration. It follows the region just drawn, so the SME's next
          click is where their eye already is, and it is clamped into the canvas so
          it stays reachable when that region sits against an edge. The SME can drag
          it off the text they want to box next, and it then stays where they put it
          until the draft ends. Shift held hides it.
        */}
        {collectingRegions && draftRegions.length > 0 && (() => {
          const lastRegion = draftRegions[draftRegions.length - 1];
          const clamp = (value: number, low: number, high: number) =>
            Math.min(Math.max(value, low), high);
          const maxTop = Math.max(DONE_PILL_VIEWPORT_MARGIN_PX, height - DONE_PILL_VIEWPORT_MARGIN_PX - DONE_PILL_HEIGHT_PX);
          const left = donePillDraggedPosition
            ? clamp(donePillDraggedPosition.left, 0, Math.max(0, width - DONE_PILL_VIEWPORT_MARGIN_PX))
            : clamp(
              lastRegion.x1 * scale + position.x,
              DONE_PILL_VIEWPORT_MARGIN_PX,
              Math.max(DONE_PILL_VIEWPORT_MARGIN_PX, width - DONE_PILL_VIEWPORT_MARGIN_PX - 320),
            );
          const top = donePillDraggedPosition
            ? clamp(donePillDraggedPosition.top, 0, maxTop)
            : clamp(lastRegion.y2 * scale + position.y + DONE_PILL_GAP_PX, DONE_PILL_VIEWPORT_MARGIN_PX, maxTop);
          return (
            <button
              type="button"
              aria-label="Finish this marking"
              title="Drag to move · hold Shift to hide"
              onClick={(e) => {
                e.stopPropagation();
                if (suppressDonePillClickRef.current) {
                  suppressDonePillClickRef.current = false;
                  return;
                }
                onFinishRegions?.();
              }}
              // The canvas starts a new rectangle on mouse-down, and the press that
              // lands on the pill must not also begin a drag underneath it.
              onMouseDown={(e) => e.stopPropagation()}
              onPointerDown={(e) => {
                if (e.button !== 0) return;
                e.stopPropagation();
                suppressDonePillClickRef.current = false;
                donePillDragRef.current = {
                  pointerId: e.pointerId,
                  startClientX: e.clientX,
                  startClientY: e.clientY,
                  startLeft: left,
                  startTop: top,
                  pillWidth: e.currentTarget.offsetWidth,
                  moved: false,
                };
                e.currentTarget.setPointerCapture(e.pointerId);
              }}
              onPointerMove={(e) => {
                const drag = donePillDragRef.current;
                if (!drag || drag.pointerId !== e.pointerId) return;
                e.stopPropagation();
                const dx = e.clientX - drag.startClientX;
                const dy = e.clientY - drag.startClientY;
                if (!drag.moved) {
                  if (Math.hypot(dx, dy) < DONE_PILL_DRAG_THRESHOLD_PX) return;
                  drag.moved = true;
                  setIsDonePillDragging(true);
                }
                setDonePillDraggedPosition({
                  left: clamp(drag.startLeft + dx, 0, Math.max(0, width - drag.pillWidth)),
                  top: clamp(drag.startTop + dy, 0, Math.max(0, height - DONE_PILL_HEIGHT_PX)),
                });
              }}
              onPointerUp={(e) => {
                const drag = donePillDragRef.current;
                if (!drag || drag.pointerId !== e.pointerId) return;
                e.stopPropagation();
                if (e.currentTarget.hasPointerCapture(e.pointerId)) {
                  e.currentTarget.releasePointerCapture(e.pointerId);
                }
                suppressDonePillClickRef.current = drag.moved;
                donePillDragRef.current = null;
                setIsDonePillDragging(false);
              }}
              onPointerCancel={() => {
                donePillDragRef.current = null;
                setIsDonePillDragging(false);
              }}
              style={{
                position: 'absolute',
                left,
                top,
                visibility: isShiftHeld ? 'hidden' : 'visible',
                touchAction: 'none',
                userSelect: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                height: DONE_PILL_HEIGHT_PX,
                padding: '0 12px',
                borderRadius: DONE_PILL_HEIGHT_PX / 2,
                border: '2px solid #ffffff',
                background: '#2563eb',
                color: '#ffffff',
                font: '600 12px system-ui, -apple-system, sans-serif',
                whiteSpace: 'nowrap',
                cursor: isDonePillDragging ? 'grabbing' : 'grab',
                boxShadow: '0 2px 8px rgba(15, 23, 42, 0.35)',
                zIndex: 5,
              }}
            >
              <span><kbd style={COLLECT_KEY_STYLE}>Enter</kbd> to finish</span>
              <span style={{ opacity: 0.55 }}>·</span>
              <span><kbd style={COLLECT_KEY_STYLE}>Esc</kbd> removes the last region</span>
            </button>
          );
        })()}

        {/*
          The kind pill asks what the pending box is. It is HTML for the reason the Done
          pill is, and it sits where the Done pill would: under the box's bottom-left
          corner, clamped into the canvas. A box low on the canvas leaves the pill no room
          under it, so the pill goes above the box instead.
        */}
        {pendingBox && (() => {
          const pillTopUnderBox = pendingBox.y2 * scale + position.y + DONE_PILL_GAP_PX;
          const roomBelowEndsAt = height - DONE_PILL_VIEWPORT_MARGIN_PX;
          const pillTop = pillTopUnderBox + KIND_PILL_HEIGHT_PX > roomBelowEndsAt
            ? pendingBox.y1 * scale + position.y - KIND_PILL_HEIGHT_PX - DONE_PILL_GAP_PX
            : pillTopUnderBox;
          return (
            <div
              role="group"
              aria-label="Mark this box as"
              className="pnid-kind-pill"
              // The press that lands on the pill must not also begin a drag underneath it.
              onMouseDown={(e) => e.stopPropagation()}
              style={{
                left: clamp(
                  pendingBox.x1 * scale + position.x,
                  DONE_PILL_VIEWPORT_MARGIN_PX,
                  Math.max(DONE_PILL_VIEWPORT_MARGIN_PX, width - DONE_PILL_VIEWPORT_MARGIN_PX - KIND_PILL_WIDTH_PX),
                ),
                top: clamp(
                  pillTop,
                  DONE_PILL_VIEWPORT_MARGIN_PX,
                  Math.max(DONE_PILL_VIEWPORT_MARGIN_PX, height - DONE_PILL_VIEWPORT_MARGIN_PX - KIND_PILL_HEIGHT_PX),
                ),
              }}
            >
              {(['equipment', 'instrument'] as const).map((kind) => (
                <button
                  key={kind}
                  type="button"
                  className="pnid-btn pnid-btn--contained pnid-btn--sm"
                  aria-label={DRAW_TOOL_LABEL[kind]}
                  aria-keyshortcuts={DRAW_TOOL_KEY[kind]}
                  onClick={() => choosePendingBoxKind(kind)}
                >
                  <DrawToolLabel tool={kind} />
                </button>
              ))}
              <span className="pnid-kind-pill__cancel" title="Esc discards the box">
                <kbd className="pnid-kind-pill__key">Esc</kbd> to cancel
              </span>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default PidCanvas; 
